import _ from "lodash";
import React from "react";
import classNames from "classnames";
import moment from "moment";
import ACLStore from "lib/aclStore";

import { Tooltip, Button, Avatar } from "@toddle-edu/ds-web";
import {
  SubjectGenericDuotone,
  UpDownArrowOutlined,
  MultipleUsersOutlined,
  ChecklistOutlined,
  InformationOutlined,
} from "@toddle-edu/ds-icons";
import {
  NoChecklistIllustration,
  NoAssignmentIllustration,
} from "@toddle-edu/ds-theme";
import {
  ASSESSMENT_TOOLS,
  ASSESSMENT_TOOLS_OBJ,
} from "@toddle-edu/common-utils";
import ConditionalWrapper from "Utils/ConditionalWrapper";

import { colors } from "Constants";
import {
  MYP_OBJECTIVE_RUBRIC,
  FIRST_NAME,
  MIDDLE_NAME,
  LAST_NAME,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_UBD,
  CURRICULUM_TYPE_DP,
  ASSESSMENT_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
  SCORE,
  STUDENT_USER,
  MANUAL,
  MYP_CRITERIA,
  PERCENTAGE_UPPERCASE,
  FINAL_SCORE,
  FINAL_GRADE,
  LOCAL_GRADE,
  GRADE_SCALE,
  WEIGHT,
  CATEGORY_TOTAL,
  DP_MAX_GRADE,
  MYP_CRITERIA_TOTAL_TYPE_ALL_CRITERIA,
  CATEGORY_SCORE,
  CATEGORY_GRADE,
  ELEMENT_TYPE_UBD_LEARNING_STANDARD,
  ELEMENT_TYPE_UBD_SUBJECT,
  ELEMENT_TYPE_MYP_LEARNING_STANDARD,
  ELEMENT_TYPE_MYP_SUBJECT,
  ELEMENT_TYPE_PYP_SUBJECT,
  ELEMENT_TYPE_DP_SUBJECT_STANDARD,
  SUBJECT,
  ELEMENT_TYPE_DP_SUBJECT,
  FULL_NAME,
  ASC,
  DESC,
  ELEMENT_TYPE_MYP_OBJECTIVES,
  COMMENT,
  SCORE_BASED_RUBRIC,
  STANDARD_BASED_RUBRIC,
  GRADE_SCALE_TOOL,
  MYP_FINAL_GRADE,
  NUMERIC_VALUE,
  STUDENT,
  STAFF,
  DEPENDENT,
  STANDARD_BASED,
  MYP_CRITERIA_BASED,
  MYP_IB_DEFINED,
  MYP_CRITERIA_TOTAL,
  REGULAR,
} from "Constants/stringConstants";

import {
  getCriteriaSetBasedOnPlannerElementType,
  getAcademicYearById,
} from "modules/Services";
import {
  checkAssessmentHasPypEvaluation,
  checkAchievementCriteriaExists,
  getStaffElementRemarkMessage,
  getIsScoreBasedRubricEnabled,
  getIsNewGradingSetupEnabled,
  getIsAssessmentGradeScaleToolEnabled,
} from "ClassRoom/modules/utils";
import { getIconComponent } from "Administrator/modules/Utils";
import { getIsAdminByRole } from "lib/staffPermissionUtils";
import {
  getFinalGradeCalculationSettings,
  getSelectedStandardSetsForGradeScale,
} from "GradingConfigurations/modules/Utils";
import {
  FINAL_GRADE_CALCULATION_METHODOLOGY_UID,
  FINAL_GRADE_CALCULATION_GRADE_SCALE_UID,
} from "GradingConfigurations/modules/Constants";

import NoGradeCalculationMethodSelectedWarning from "AssessmentsGradebookV2/components/NoGradeCalculationMethodSelectedWarning";

import {
  getEvaluatedButNotSharedAssignmentsFilter,
  getStudentWiseRatingsFromCache,
  readCategoryItemFragment,
  readEvaluatedButNotSharedAssignmentsCountByTermFromCache,
  writeCategoryItemFragment,
  writeEvaluatedButNotSharedAssignmentsCountByTermInCache,
  writeStudentWiseRatingsToCache,
} from "./GradebookGraphqlHelpers";

import gradebookClasses from "AssessmentsGradebook/components/GradebookTable/GradebookTable.scss";
import subjectSettingsClasses from "AssessmentsGradebook/components/SubjectSettings/SubjectSettings.scss";
import {
  getYearDurationFormat,
  getMemoizedLangFunction,
  generateRandomId,
} from "Utils";
import { calculateGradeScaleRatingFromOtherTool } from "Utils/AssessmentToolHelpers";
import { getCriteriaSetLabels } from "Utils/GetCriteriaSetLabels";
import { getStudentSortByList } from "Utils/GetStudentSortByList";
import {
  getIsConfigurationEnabledForCPAY,
  getSettingValue,
} from "modules/PermissionModule";
import {
  getIsNewAssessmentSettingsEnabled,
  OVERALL_SCORE_FOR_GRADING_SETUP_SETTING,
  USE_WEIGHTAGE_FOR_CATEGORY_SETTING,
  FINAL_GRADE_CALCULATION_SETTING,
  getIsGradingSetupDecimalConfigEnabled,
  getIsGradingPeriodUnlocked,
} from "GradingSetup/modules/Utils";
import { getAssociatedEntitiesVariables } from "UnitPlans/components/IBcomponents/MaxScoreTool/Utils";
import { getUserArchivalStatus } from "modules/CommonSelectors";
import {
  CRITERION_A,
  CRITERION_B,
  CRITERION_C,
  CRITERION_D,
  BULK_EVALUATION,
  BULK_GRADE,
  TERM_LEVEL_CRITERION_COLUMNS,
  ASSIGNMENT_CRITERION_A,
  ASSIGNMENT_CRITERION_B,
  ASSIGNMENT_CRITERION_C,
  ASSIGNMENT_CRITERION_D,
  MYP_CRITERIA_FINAL_SCORE,
  CATEGORY_COLUMNS,
  GRADEBOOK_ROUTES,
  SUBJECT_LEVEL_SORT_ORDER,
  ASSESSMENTS_GRADEBOOK,
  LEARNING_GOALS_GRADEBOOK,
  STANDARDS_GRADEBOOK,
  FINAL_GRADES_GRADEBOOK,
  CRITERIA_TOTAL,
  CRITERION_A_COMMENT,
  CRITERION_B_COMMENT,
  CRITERION_C_COMMENT,
  CRITERION_D_COMMENT,
  SUBJECT_DESCRIPTION,
  FINAL_COMMENT,
  TERM_TOTAL_COLUMNS,
  TERM_LEVEL_CRITERION_COMMENTS_COLUMNS,
  GRADE_SCALE_VALUES_COLORS_MAP,
  CATEGORY_GRADE_SCALE,
  TERM_LEVEL_CRITERION_GRADE_SCALE_COLUMNS,
  CRITERION_A_GRADE_SCALE,
  CRITERION_B_GRADE_SCALE,
  CRITERION_C_GRADE_SCALE,
  CRITERION_D_GRADE_SCALE,
} from "Gradebook/modules/Constants/stringConstants";
import { getIsMypPhaseSelectionVisible } from "Course/routes/Settings/modules/SettingsUtils";
import {
  ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
  MANAGE_GRADEBOOK_PERMISSION,
  USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
  LIST_AND_ACCESS_ALL_CLASSES_PERMISSION,
} from "Constants/permissionConstants";
import ACLStorage from "lib/aclService";
import { getDateMomentObject, isDateInBetween } from "Utils/DateTimeHelpers";
import { getHexCodeFromColorToken } from "Utils/ColorHelper";
import {
  shouldShowLevelsInGradingSetupV2,
  getIsRatingVisible,
} from "GradingSetupV2/modules/Utils";
import { getFormattedUserName } from "Utils/GetFormattedUserName";

const IB_GRADE_TOOLS = [
  ASSESSMENT_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
];

const iconStyle = {
  width: "20px",
  height: "20px",
};

const styles = {
  overrideRatingsModalTableCols: {
    minWidth: "200px",
  },
  iconContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
  },
  flexColumn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  studentDropdown: {
    maxHeight: "calc(100vh - 336px)",
    overflowY: "auto",
  },
  lockedTermContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
  },
  infoTooltipStyle: {
    width: "280px",
  },
  infoIconStyle: { color: colors.red52 },
  lockingGradeTooltipOverlayInner: {
    maxWidth: "350px",
  },
  courseContainer: { borderRadius: "4px", padding: "8px 8px" },
};

const textSubtleBody3 = classNames(
  "text-body-3",
  gradebookClasses.assignmentTitleSubtext
);

const assignmentTitleClass = classNames(
  gradebookClasses.assignmentTitle,
  "heading-6"
);
const assignmentTitleSubtextClass = classNames(
  gradebookClasses.assignmentTitleSubtext,
  "text-body-3"
);

const tokFinalScoreSubtextClass = classNames(
  "text-body-3",
  subjectSettingsClasses.tokFinalScoreSubtext
);

export const getHeaderLabel = ({
  label,
  customClass,
  isGradebookCompactUIEnabled,
}) => {
  return (
    <Tooltip
      visible={_.size(label) > (isGradebookCompactUIEnabled ? 6 : 25)}
      tooltip={label}
    >
      <div
        className={classNames(gradebookClasses.gradebookHeaderLabel, {
          [gradebookClasses.textEllipsis]: isGradebookCompactUIEnabled,
          [customClass]: !_.isEmpty(customClass),
        })}
      >
        {label}
      </div>
    </Tooltip>
  );
};

export const getElementTypesForDownload = ({ elementType }) => {
  switch (elementType) {
    case ELEMENT_TYPE_UBD_LEARNING_STANDARD:
      return [ELEMENT_TYPE_UBD_LEARNING_STANDARD, ELEMENT_TYPE_UBD_SUBJECT];
    case ELEMENT_TYPE_MYP_LEARNING_STANDARD:
      return [ELEMENT_TYPE_MYP_LEARNING_STANDARD, ELEMENT_TYPE_MYP_SUBJECT];
    case ELEMENT_TYPE_DP_SUBJECT_STANDARD:
      return [ELEMENT_TYPE_DP_SUBJECT_STANDARD, ELEMENT_TYPE_DP_SUBJECT];
    default:
      return elementType;
  }
};

export const getGradingPeriodDropdownOptions = _.memoize(
  ({ gradingPeriods, showTermTotalColumns = true, groupByYear = false }) => {
    const filteredGradingPeriods = _.filter(gradingPeriods, ({ type }) => {
      if (!showTermTotalColumns) {
        return type !== "CUMULATIVE";
      }

      return true;
    });

    if (!groupByYear) {
      return _.map(filteredGradingPeriods, period => {
        const { label, id, year, type, disabled, disabledTooltip } = period;
        return {
          key: id,
          label: `${label} (${year})`,
          type,
          value: id,
          disabled,
          disabledTooltip,
          isGradingPeriodUnlocked: getIsGradingPeriodUnlocked(period),
        };
      });
    }

    const periodsGroupedByYear = _.groupBy(
      filteredGradingPeriods,
      "academicYearId"
    );

    const years = _.uniqBy(
      _.map(filteredGradingPeriods, ({ academicYearId, year }) => {
        return {
          academicYearId,
          yearLabel: year,
        };
      }),
      "academicYearId"
    );

    return _.map(years, year => {
      const { academicYearId, yearLabel } = year;
      const gradingPeriods = _.get(periodsGroupedByYear, academicYearId, []);

      return {
        key: academicYearId,
        value: academicYearId,
        isItemGroup: true,
        label: yearLabel,
        options: _.map(gradingPeriods, period => {
          const { label, id, type, disabled, disabledTooltip } = period;
          return {
            key: id,
            label,
            type,
            value: id,
            disabled,
            disabledTooltip,
            isGradingPeriodUnlocked: getIsGradingPeriodUnlocked(period),
          };
        }),
      };
    });
  }
);

const getAllSubjectsOption = ({ t, subjects }) => {
  return {
    id: _.join(_.map(subjects, "id"), ","),
    key: "ALL_SUBJECTS",
    name: t("academicSetup:all_subjects"),
    icon: (
      <SubjectGenericDuotone
        size={"xx-small"}
        color="yellow"
        containerStyle={iconStyle}
      />
    ),
  };
};

export const getSortedSubjects = params => {
  const { subjectsArr, sortKey = "name" } = params;
  const sortedSubjects = _.orderBy(
    subjectsArr,
    ["isArchived", subject => subject?.[sortKey]?.toLowerCase()],
    ["asc", "asc"]
  );
  return sortedSubjects;
};

export const getAssessmentsGradebookSubjectDropdownOptions = _.memoize(
  ({
    t,
    subjects,
    curriculumType,
    isCumulativePeriod,
    isCourseBasedGrading,
  }) => {
    if (isCourseBasedGrading) {
      return [];
    }
    let updatedSubjects = subjects;
    if (curriculumType === CURRICULUM_TYPE_PYP && !isCumulativePeriod) {
      updatedSubjects = [getAllSubjectsOption({ t, subjects }), ...subjects];
    }
    return _.map(updatedSubjects, subject => {
      const { icon, id, name, isArchived } = subject;
      const iconComponent = getIconComponent({
        icon: icon,
        size: "xx-small",
        containerStyle: iconStyle,
      });

      return {
        key: id,
        label: name,
        icon: iconComponent,
        isArchived,
      };
    });
  },
  ({ subjects, curriculumType, isCumulativePeriod, isCourseBasedGrading }) =>
    JSON.stringify({
      subjects: _.map(subjects, ({ id, name, isArchived }) => ({
        id,
        name,
        isArchived,
      })),
      curriculumType,
      isCumulativePeriod,
      isCourseBasedGrading,
    })
);

export const getAssessmentGradebookViewByButtonLabel = ({
  selectedViewBy,
  t,
}) => {
  return selectedViewBy === "CATEGORIES"
    ? t("common:category_plural")
    : t("common:creation_date");
};

export const getAssessmentGradebookViewByDropdownOptions = ({ t }) => {
  return [
    {
      key: "CREATION_DATE",
      label: t("common:creation_date"),
      subText: t("progressSummary:creation_date_view_subtext"),
    },
    {
      key: "CATEGORIES",
      label: t("common:category_plural"),
      subText: t("progressSummary:categories_view_subtext"),
    },
  ];
};

export const getPlannerElementSetFilter = _.memoize(
  ({ course }) => {
    const filter = {
      upToDepth: 1,
      startDepth: 1,
    };
    const subject = _.first(_.get(course, "subjects"));
    if (!_.isEmpty(subject)) {
      filter.associatedParents = {
        ids: [_.get(subject, "id", "")],
        type: "SUBJECT",
      };
    }
    return filter;
  },
  params => JSON.stringify(params)
);

export const getAssessmentsGradebookTermsDropdownButtonLabel = _.memoize(
  ({ gradingPeriods, selectedGradingPeriod }) => {
    const term = _.find(
      gradingPeriods,
      period => period?.id === selectedGradingPeriod
    );
    const termLabel = _.get(term, "label");
    const termYear = _.get(term, "year");
    return `${termLabel} (${termYear})`;
  },
  params => JSON.stringify(params)
);

export const getSubjectSettingsTabOptions = _.memoize(
  ({
    t,
    isCumulativePeriod,
    termLabel,
    isMypPhaseSelectionVisible,
    canDefineGradeBoundary,
  }) => {
    const options = [];

    if (isMypPhaseSelectionVisible) {
      options.push({
        value: MYP_CRITERIA,
        label: t("progressSummary:myp_criteria"),
      });
    }

    if (isCumulativePeriod) {
      options.push({
        value: "CUMULATIVE_PERIOD_WEIGHTS",
        label: t("progressSummary:grading_period_weights"),
      });
    } else {
      options.push({
        value: "ASSESSMENT_CATEGORY",
        label: t("progressSummary:assessment_category"),
      });
    }

    if (canDefineGradeBoundary) {
      const gradeBoundaryOptionLabel = isCumulativePeriod
        ? t("progressSummary:grade_boundary_with_term_label", { termLabel })
        : t("academicSetup:grade_boundary");

      options.push({
        value: "GRADE_BOUNDARY",
        label: gradeBoundaryOptionLabel,
      });
    }

    return options;
  },
  params => JSON.stringify(params)
);

export const getStudentsColumnDropdownOptions = getMemoizedLangFunction(
  ({ t, isCurriculumMYP }) => {
    const nameSortByOptions = [
      {
        key: "FIRSTNAME_AZ",
        label: t("common:first_name_az"),
        sortEntity: "FIRST_NAME",
        direction: "asc",
      },
      {
        key: "FIRSTNAME_ZA",
        label: t("common:first_name_za"),
        sortEntity: "FIRST_NAME",
        direction: "desc",
      },

      {
        key: "MIDDLENAME_AZ",
        label: t("common:middle_name_az"),
        sortEntity: "MIDDLE_NAME",
        direction: "asc",
      },
      {
        key: "MIDDLENAME_ZA",
        label: t("common:middle_name_za"),
        sortEntity: "MIDDLE_NAME",
        direction: "desc",
      },
      {
        key: "LASTNAME_AZ",
        label: t("common:last_name_az"),
        sortEntity: "LAST_NAME",
        direction: "asc",
      },
      {
        key: "LASTNAME_ZA",
        label: t("common:last_name_za"),
        sortEntity: "LAST_NAME",
        direction: "desc",
      },
      {
        key: "FULLNAME_AZ",
        label: t("common:full_name_az"),
        sortEntity: "FULL_NAME",
        direction: "asc",
      },
      {
        key: "FULLNAME_ZA",
        label: t("common:full_name_za"),
        sortEntity: "FULL_NAME",
        direction: "desc",
      },
    ];

    return [
      {
        key: "SORT_BY",
        label: t("common:sort_by"),
        isItemGroup: true,
        options: getStudentSortByList({
          sortList: nameSortByOptions,
          keyToMap: "sortEntity",
        }),
      },
      {
        key: "Display",
        label: t("progressSummary:display"),
        isItemGroup: true,
        options: [
          {
            key: "STUDENT_UID",
            label: t("common:student_uid"),
            entity: "DISPLAY",
          },
          ...(isCurriculumMYP
            ? [
                {
                  key: "MYP_CRITERIA",
                  label: t("progressSummary:myp_assessment_criteria"),
                  entity: "DISPLAY",
                },
              ]
            : []),
          {
            key: "EMAIL_ID",
            label: t("common:email_id"),
            entity: "DISPLAY",
          },
        ],
      },
    ];
  }
);

export const getIsMYPBasedGradeScale = ({
  gradeScale,
  gradingMethodologyTypes,
}) => {
  const isMYPBasedGradingMethodology = _.includes(
    gradingMethodologyTypes,
    MYP_CRITERIA_BASED
  );

  if (!isMYPBasedGradingMethodology) {
    return false;
  }

  const { type } = gradeScale || {};
  const mypBasedScales = [MYP_FINAL_GRADE, MYP_IB_DEFINED, MYP_CRITERIA_TOTAL];

  if (type === DEPENDENT) {
    const { dependentGradeScales } = gradeScale || {};

    return _.some(dependentGradeScales, dependentScale =>
      _.includes(mypBasedScales, dependentScale?.type)
    );
  }

  return _.includes(mypBasedScales, type);
};

const getTermColumns = ({
  t,
  term,
  userType,
  gradeScales,
  isCurriculumDP,
  isCurriculumMYP,
  isCurriculumUBD,
  criteriaLabels,
  hiddenGradebookEntities,
  gradingMethodologyTypes,
  finalGradesVisibilitySettings,
  isVisibilitySettingsV2Enabled,
  isUsingNewGradingSetupInCourses,
}) => {
  const types = [];

  const isOverallScoreEnabled = isUsingNewGradingSetupInCourses
    ? getIsRatingVisible({
        term,
        userType,
        ratingType: SCORE,
        isVisibilitySettingsV2Enabled,
        visibilitySettings: finalGradesVisibilitySettings,
      })
    : true;

  if (isOverallScoreEnabled) {
    types.unshift(FINAL_SCORE);
  }

  if (!isUsingNewGradingSetupInCourses) {
    if (isCurriculumDP || isCurriculumMYP) {
      types.push(FINAL_GRADE);
    }
    types.push(LOCAL_GRADE);
  }

  const columns = _.map(types, type => {
    return {
      id: type,
      type,
      label: getTermTotalLabelByType({
        t,
        type,
        isCurriculumUBD,
        criteriaLabels,
      }),
    };
  });

  if (isUsingNewGradingSetupInCourses) {
    const gradeScaleColumns = [];

    _.forEach(gradeScales, gradeScale => {
      const { id, displayName, templateName } = gradeScale;

      const isMYPBasedGradeScale = getIsMYPBasedGradeScale({
        gradeScale,
        gradingMethodologyTypes,
      });

      const isVisible = getIsRatingVisible({
        term,
        userType,
        gradeScaleId: id,
        ratingType: GRADE_SCALE,
        isVisibilitySettingsV2Enabled,
        visibilitySettings: finalGradesVisibilitySettings,
      });

      if (
        !isVisible ||
        isMYPBasedGradeScale ||
        _.includes(_.map(gradeScaleColumns, "id"), id) // skip if already added
      ) {
        return;
      }

      const gradeScaleCol = {
        id,
        type: GRADE_SCALE,
        label: displayName || templateName,
        academicCriteriaSet: gradeScale,
      };

      gradeScaleColumns.push(gradeScaleCol);
    });

    columns.push(...gradeScaleColumns);
  }

  return _.filter(columns, ({ id }) => !_.get(hiddenGradebookEntities, id));
};
export const getTermColumnsSortEntity = ({
  termId,
  isCurriculumDP,
  isCurriculumMYP,
  isCurriculumUBD,
  isOverallScoreEnabled,
  isOverallGradeEnabled,
  isLocalGradeCalculationEnabled,
  columnConfiguration,
}) => {
  const termColumns = [];

  if (isOverallScoreEnabled) {
    const scoreColumnKey = termId ? `${termId}-${FINAL_SCORE}` : FINAL_SCORE;
    termColumns.push(scoreColumnKey);
  }

  if (isCurriculumDP || isCurriculumMYP) {
    const finalGradeColumnKey = termId
      ? `${termId}-${FINAL_GRADE}`
      : FINAL_GRADE;
    termColumns.push(finalGradeColumnKey);
  }

  const showLocalGradeCondition = getShowLocalGradeCondition({
    isCurriculumUBD,
    isLocalGradeCalculationEnabled,
    isOverallGradeEnabled,
  });

  if (showLocalGradeCondition) {
    const localGradeColumnKey = termId
      ? `${termId}-${LOCAL_GRADE}`
      : LOCAL_GRADE;
    termColumns.push(localGradeColumnKey);
  }

  return _.find(termColumns, item => _.get(columnConfiguration, [item], true));
};

const getMypCriteriaHeaderKey = ({ termId = "", label }) => {
  return termId ? `${termId}-${label}` : label;
};

export const getTermLevelCriteriaColumns = ({
  t,
  termId = "",
  columnConfiguration,
  mypIbDefinedGradeScale,
  gradingMethodologyTypes,
  mypCriteriaTotalGradeScale,
  isUsingNewGradingSetupInCourses,
  mypIbDefinedAchievementCriteriaSet,
  showCriteriaCommentsColumns,
  isGradebookCompactUIEnabled,
  mypCriteriaRegularGradeScales,
  hasMypCriteriaMappedGradeScales,
}) => {
  const customWidth = 76;

  const mypCriteriaGradeScales = [
    mypIbDefinedGradeScale,
    ...(mypCriteriaRegularGradeScales || []),
  ];

  const getCriterionLabel = key =>
    isGradebookCompactUIEnabled && !hasMypCriteriaMappedGradeScales
      ? key
      : `${t("common:criterion")} ${key}`;

  const criterionColumns = [
    {
      entity: CRITERION_A,
      label: getCriterionLabel("A"),
      customWidth,
      commentEntity: CRITERION_A_COMMENT,
      gradeScaleEntity: CRITERION_A_GRADE_SCALE,
    },
    {
      entity: CRITERION_B,
      label: getCriterionLabel("B"),
      customWidth,
      commentEntity: CRITERION_B_COMMENT,
      gradeScaleEntity: CRITERION_B_GRADE_SCALE,
    },
    {
      entity: CRITERION_C,
      label: getCriterionLabel("C"),
      customWidth,
      commentEntity: CRITERION_C_COMMENT,
      gradeScaleEntity: CRITERION_C_GRADE_SCALE,
    },
    {
      entity: CRITERION_D,
      label: getCriterionLabel("D"),
      customWidth,
      commentEntity: CRITERION_D_COMMENT,
      gradeScaleEntity: CRITERION_D_GRADE_SCALE,
    },
  ];

  const totalColumn = {
    entity: CRITERIA_TOTAL,
    label: t("common:total"),
    customWidth,
  };

  const mypCriteria = [];

  if (
    !isUsingNewGradingSetupInCourses ||
    _.includes(gradingMethodologyTypes, MYP_CRITERIA_BASED)
  ) {
    mypCriteria.push(...criterionColumns, totalColumn);
  }

  const criteriaColumns = [];
  const criteriaGradeScaleColumns = [];

  _.forEach(mypCriteria, option => {
    const { entity, label, commentEntity } = option;
    const key = getMypCriteriaHeaderKey({ termId, label: entity });
    const commentKey = getMypCriteriaHeaderKey({
      termId,
      label: commentEntity,
    });
    const isColumnVisible = isUsingNewGradingSetupInCourses
      ? true
      : _.get(columnConfiguration, key, true);
    const isCommentColumnVisible = isUsingNewGradingSetupInCourses
      ? true
      : _.get(columnConfiguration, commentKey, true);

    const ratingOptions = getOrderedRatingOptionsByEntityType({
      entityType: entity,
      isUsingNewGradingSetupInCourses,
      mypIbDefinedGradeScale,
      mypIbDefinedAchievementCriteriaSet,
      hasDashOption: !isUsingNewGradingSetupInCourses,
    });

    let academicCriteriaSet;
    if (isUsingNewGradingSetupInCourses) {
      academicCriteriaSet =
        entity === CRITERIA_TOTAL
          ? mypCriteriaTotalGradeScale
          : mypIbDefinedGradeScale;
    } else {
      academicCriteriaSet =
        entity === CRITERIA_TOTAL
          ? undefined
          : mypIbDefinedAchievementCriteriaSet;
    }

    if (isColumnVisible) {
      criteriaColumns.push({
        id: key,
        key,
        label: isGradebookCompactUIEnabled
          ? getHeaderLabel({
              label,
              isGradebookCompactUIEnabled,
              customClass: hasMypCriteriaMappedGradeScales
                ? textSubtleBody3
                : undefined,
            })
          : label,
        labelText: label,
        entity,
        validInputs: { options: ratingOptions },
        academicCriteriaSet,
      });
    }

    if (
      showCriteriaCommentsColumns &&
      !_.isEmpty(commentEntity) &&
      isCommentColumnVisible
    ) {
      criteriaColumns.push({
        key: commentKey,
        label: isGradebookCompactUIEnabled
          ? getHeaderLabel({
              label: `${label}: ${t("common:comment")}`,
              isGradebookCompactUIEnabled,
            })
          : `${label}: ${t("common:comment")}`,
        labelText: `${label}: ${t("common:comment")}`,
        entity: commentEntity,
      });
    }
  });

  if (
    isUsingNewGradingSetupInCourses &&
    hasMypCriteriaMappedGradeScales &&
    !_.isEmpty(criteriaColumns)
  ) {
    _.forEach(
      criterionColumns,
      ({
        entity: criterionEntity,
        gradeScaleEntity,
        label: criterionLabel,
      }) => {
        _.forEach(mypCriteriaGradeScales, gradeScale => {
          if (_.isEmpty(gradeScale)) {
            return;
          }

          const {
            id: gradeScaleId,
            displayName,
            templateName,
            gradeScaleEntityDetails,
          } = gradeScale || {};

          const key = getMypCriteriaHeaderKey({
            termId,
            label: `${criterionEntity}_${gradeScaleId}`,
          });

          const isMypIbDefinedGradeScale =
            gradeScaleId === mypIbDefinedGradeScale?.id;

          const entity = isMypIbDefinedGradeScale
            ? criterionEntity
            : gradeScaleEntity;

          const ratingOptions = getOrderedRatingOptionsByEntityType({
            entityType: entity,
            isUsingNewGradingSetupInCourses,
            mypIbDefinedGradeScale,
            mypIbDefinedAchievementCriteriaSet,
            gradeScaleEntityDetails: _.get(gradeScaleEntityDetails, "0"),
            hasDashOption: false,
          });

          const columnLabel = isMypIbDefinedGradeScale
            ? t("common:grade")
            : displayName || templateName;

          criteriaGradeScaleColumns.push({
            id: key,
            key,
            entity,
            academicCriteriaSet: gradeScale,
            validInputs: { options: ratingOptions },
            label: isGradebookCompactUIEnabled
              ? getHeaderLabel({
                  label: columnLabel,
                  isGradebookCompactUIEnabled,
                })
              : columnLabel,
            labelText: `${criterionLabel} - ${displayName || templateName}`,
            parentEntity: criterionEntity,
          });
        });
      }
    );
  }

  return { criteriaColumns, criteriaGradeScaleColumns };
};

export const getIsCategoryScoreValuesEmpty = ({
  categoryOptions,
  subjectLevelGenericTags,
}) => {
  let isCategoryScoreValuesEmpty = false;

  _.forEach(categoryOptions, categoryOption => {
    const { scoreValues } = categoryOption;
    if (_.size(scoreValues) < _.size(subjectLevelGenericTags)) {
      isCategoryScoreValuesEmpty = true;
      return;
    }

    _.forEach(scoreValues, scoreValue => {
      if (_.isNil(_.get(scoreValue, "value"))) {
        isCategoryScoreValuesEmpty = true;
        return;
      }
    });
  });

  return isCategoryScoreValuesEmpty;
};

export const getTermTotalColumns = _.memoize(
  params => {
    const {
      t,
      isCurriculumDP,
      isCurriculumMYP,
      isScoreV2,
      isTOK,
      columnConfiguration,
      selectedGradingPeriod,
      gradingPeriods,
      isOverallScoreEnabled,
      isCurriculumUBD,
      isOverallGradeEnabled,
      isLocalGradeCalculationEnabled,
      gradeCalculationMethod,
      finalGradeCalculationMethod,
      isModalMode = false,
      organizationId,
      curriculumProgramId,
      categoryOptions,
      categoryTotalDisplayType,
      subjectLevelGenericTags,
      criteriaNodes,
      subjects,
    } = params;

    const sortEntity = getTermColumnsSortEntity({
      isCurriculumUBD,
      isCurriculumDP,
      isCurriculumMYP,
      isOverallScoreEnabled,
      isOverallGradeEnabled,
      isLocalGradeCalculationEnabled,
      columnConfiguration,
    });

    const termLabel = _.find(gradingPeriods, { id: selectedGradingPeriod })
      ?.label;

    const termTotalColumns = [];

    const totalsHeaderItemStyle = {
      ...categoriesViewStyles.greyCell,
      minWidth: "200px",
    };

    const criteriaLabels = getCriteriaSetLabels({
      organizationId,
      curriculumProgramId,
      criteriaTypes: [FINAL_SCORE, FINAL_GRADE, LOCAL_GRADE],
    });

    const totalsHeaderItem = {
      key: "TOTALS",
      entity: sortEntity,
      parent: "root",
      children: [],
      level: 0,
      value: (
        <div className={gradebookClasses.assignmentNameContainer}>
          <div className={assignmentTitleClass}>
            {t("common:label_total", { label: termLabel })}
          </div>
          {isOverallScoreEnabled ? (
            <div className={assignmentTitleSubtextClass}>100%</div>
          ) : null}
        </div>
      ),
      alignment: "center",
      style: totalsHeaderItemStyle,
      columnType: "TERM_TOTAL_COLUMNS",
      sortType: "toggle",
      showFilterOnHover: true,
    };

    const totalsItems = [];

    const showTermTotalScoreColumn =
      isOverallScoreEnabled && _.get(columnConfiguration, "FINAL_SCORE", true);

    if (showTermTotalScoreColumn) {
      const isCategoriesWeighted = categoryTotalDisplayType === "WEIGHT";

      const isCategoryScoreValuesEmpty = getIsCategoryScoreValuesEmpty({
        categoryOptions,
        subjectLevelGenericTags,
      });

      totalsItems.push({
        key: "FINAL_SCORE",
        label: (
          <div style={styles.flexColumn}>
            <div style={styles.iconContainer}>
              {getHeaderLabel({
                label: _.get(criteriaLabels, FINAL_SCORE, t("common:score")),
              })}
              {_.isEmpty(gradeCalculationMethod) ||
              (isCategoriesWeighted && isCategoryScoreValuesEmpty) ? (
                <NoGradeCalculationMethodSelectedWarning
                  t={t}
                  entityType={SCORE}
                  columnType={"TERM"}
                  isCategoryScoreValuesEmpty={isCategoryScoreValuesEmpty}
                />
              ) : null}
            </div>
            {isModalMode && (
              <div className={textSubtleBody3}>
                {t("academicSetup:out_of_with_label", { label: "100%" })}
              </div>
            )}
          </div>
        ),
        entity: "FINAL_SCORE",
      });
    }

    const isCriteriaColumnsVisible = _.get(
      columnConfiguration,
      "CRITERIA_COLUMNS",
      true
    );

    const isMypPhaseSelectionVisible = getIsMypPhaseSelectionVisible({
      isMYP: isCurriculumMYP,
      criteriaNodes,
      subject: _.first(subjects),
    });

    if (isCriteriaColumnsVisible && isMypPhaseSelectionVisible) {
      const criteriaColumns = getTermLevelCriteriaColumns({
        t,
        columnConfiguration,
      });
      totalsItems.push(...criteriaColumns);
    }

    if (
      (isCurriculumDP || isCurriculumMYP) &&
      isOverallGradeEnabled &&
      isScoreV2 &&
      _.get(columnConfiguration, "FINAL_GRADE", true)
    ) {
      totalsItems.push({
        key: "FINAL_GRADE",
        label: (
          <div style={styles.flexColumn}>
            <div style={styles.iconContainer}>
              {getHeaderLabel({
                label: _.get(criteriaLabels, FINAL_GRADE, t("common:ib_grade")),
              })}
              {_.isEmpty(gradeCalculationMethod) &&
              finalGradeCalculationMethod === SCORE ? (
                <NoGradeCalculationMethodSelectedWarning
                  t={t}
                  entityType={"GRADE"}
                  columnType={"TERM"}
                />
              ) : null}
            </div>
            {isModalMode && (
              <div className={textSubtleBody3}>
                {t("academicSetup:out_of_with_label", { label: DP_MAX_GRADE })}
              </div>
            )}
          </div>
        ),
        entity: "FINAL_GRADE",
      });
    }

    const showLocalGradeCondition = getShowLocalGradeCondition({
      isCurriculumUBD,
      isLocalGradeCalculationEnabled,
      isOverallGradeEnabled,
    });

    if (
      showLocalGradeCondition &&
      isScoreV2 &&
      !isTOK &&
      _.get(columnConfiguration, "LOCAL_GRADE", true)
    ) {
      totalsItems.push({
        key: "LOCAL_GRADE",
        label: (
          <div style={styles.iconContainer}>
            {getHeaderLabel({
              label: _.get(
                criteriaLabels,
                LOCAL_GRADE,
                isCurriculumUBD
                  ? t("common:grade")
                  : t("common:local_grade_text")
              ),
            })}
            {isCurriculumUBD &&
            _.isEmpty(gradeCalculationMethod) &&
            finalGradeCalculationMethod === SCORE ? (
              <NoGradeCalculationMethodSelectedWarning
                t={t}
                entityType={"GRADE"}
                columnType={"TERM"}
              />
            ) : null}
          </div>
        ),
        entity: "LOCAL_GRADE",
      });
    }

    _.forEach(totalsItems, item => {
      const { key, entity, label } = item;

      const style = {
        ...categoriesViewStyles.totalHeader,
        ...(isCurriculumMYP ? { minWidth: "96px" } : {}),
      };

      const labelClass = classNames({
        "heading-6": isModalMode,
        "text-body-3": !isModalMode,
      });

      termTotalColumns.push({
        key,
        entity,
        columnKey: entity,
        parent: "TOTALS",
        children: [],
        level: 1,
        alignment: "center",
        style,
        value: <div className={labelClass}>{label}</div>,
      });
      totalsHeaderItem.children.push(key);
    });

    termTotalColumns.push(totalsHeaderItem);

    return termTotalColumns;
  },
  params => JSON.stringify(params)
);

export const getCanShowCategoryGradeColumnsInGradebook = ({
  isCurriculumDP,
  isCurriculumUBD,
  isCurriculumMYP,
  finalGradeCalculationMethod,
  isOverallGradeEnabled,
  canOverrideFinalGrade,
}) => {
  const isRnPEnabled = ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION);

  const curriculumWiseCondition =
    isCurriculumDP ||
    isCurriculumUBD ||
    (isCurriculumMYP &&
      (finalGradeCalculationMethod !== MYP_CRITERIA ||
        isRnPEnabled ||
        canOverrideFinalGrade));

  return curriculumWiseCondition && isOverallGradeEnabled;
};

export const getCanShowCategoryGradeColumnForCurrentCategory = ({
  isCurriculumUBD,
  hasGradeBoundary,
  isShowGradeBoundaries,
  finalGradeCalculationMethod,
}) => {
  let condition = isShowGradeBoundaries;

  if (isCurriculumUBD || finalGradeCalculationMethod === SCORE) {
    condition = condition && hasGradeBoundary;
  }

  return condition;
};

export const getCategoryTotalColumn = _.memoize(
  ({
    t,
    category,
    isCurriculumDP,
    isCurriculumMYP,
    conditionalPercentage,
    isTOK,
    columnConfiguration,
    isCumulative = false,
    isOverallScoreEnabled,
    isCurriculumUBD,
    categoryTotalDisplayType,
    isOverallGradeEnabled,
    isWeightageForCategoryEnabled,
    finalGradeCalculationMethod,
    gradeCalculationMethod,
    canOverrideFinalGrade,
    isModalMode = false,
    organizationId,
    curriculumProgramId,
    subjectLevelGenericTags,
    isGradebookCompactUIEnabled,
  }) => {
    const {
      categoryId,
      label,
      scoreValues,
      isShowGradeBoundaries,
      hasGradeBoundary,
    } = category;
    const orderedScoreValues = _.orderBy(
      scoreValues,
      "genericTag.label",
      "desc"
    );
    const categorySubtext = getCategorySubtext({
      t,
      isModalMode,
      orderedScoreValues,
      isCurriculumDP,
      conditionalPercentage,
      isTOK,
      categoryTotalDisplayType,
      isWeightageForCategoryEnabled,
    });

    const isCategoryTotalColumnVisible = _.get(
      columnConfiguration,
      "CATEGORY_TOTAL",
      true
    );

    const criteriaLabels = getCriteriaSetLabels({
      organizationId,
      curriculumProgramId,
      criteriaTypes: [CATEGORY_SCORE, CATEGORY_GRADE],
    });

    const categoryTotalsSubHeaders = [];

    const showCategoryTotalScoreColumn =
      isOverallScoreEnabled && isCategoryTotalColumnVisible;

    if (showCategoryTotalScoreColumn) {
      const isCategoriesWeighted = categoryTotalDisplayType === "WEIGHT";

      const isCategoryScoreValuesEmpty = getIsCategoryScoreValuesEmpty({
        categoryOptions: _.castArray(category),
        subjectLevelGenericTags,
      });

      categoryTotalsSubHeaders.push({
        key: `${categoryId}-total`,
        entity: "CATEGORY_TOTAL",
        label: (
          <div style={styles.flexColumn}>
            <div style={styles.iconContainer}>
              {getHeaderLabel({
                label: _.get(criteriaLabels, CATEGORY_SCORE, t("common:score")),
                isGradebookCompactUIEnabled,
              })}
              {_.isEmpty(gradeCalculationMethod) ||
              (isCategoriesWeighted && isCategoryScoreValuesEmpty) ? (
                <NoGradeCalculationMethodSelectedWarning
                  t={t}
                  entityType={SCORE}
                  columnType={"CATEGORY"}
                  isCategoryScoreValuesEmpty={isCategoryScoreValuesEmpty}
                />
              ) : null}
            </div>
            {isModalMode && (
              <div className={textSubtleBody3}>{categorySubtext}</div>
            )}
          </div>
        ),
      });
    }

    const canShowCategoryGradeColumnsInGradebook = getCanShowCategoryGradeColumnsInGradebook(
      {
        isCurriculumDP,
        isCurriculumUBD,
        isCurriculumMYP,
        finalGradeCalculationMethod,
        isOverallGradeEnabled,
        canOverrideFinalGrade,
        columnConfiguration,
      }
    );

    const canShowCategoryGradeColumnForCurrentCategory = getCanShowCategoryGradeColumnForCurrentCategory(
      {
        isCurriculumUBD,
        hasGradeBoundary,
        isShowGradeBoundaries,
        finalGradeCalculationMethod,
      }
    );

    if (
      canShowCategoryGradeColumnsInGradebook &&
      canShowCategoryGradeColumnForCurrentCategory &&
      _.get(columnConfiguration, "CATEGORY_GRADE", true)
    ) {
      categoryTotalsSubHeaders.push({
        key: `${categoryId}-gradeBoundary`,
        entity: "CATEGORY_GRADE",
        label: (
          <div style={styles.flexColumn}>
            <div style={styles.iconContainer}>
              {getHeaderLabel({
                label: _.get(
                  criteriaLabels,
                  CATEGORY_GRADE,
                  isCurriculumUBD ? t("common:grade") : t("common:ib_grade")
                ),
                isGradebookCompactUIEnabled,
              })}
              {_.isEmpty(gradeCalculationMethod) &&
              finalGradeCalculationMethod === SCORE ? (
                <NoGradeCalculationMethodSelectedWarning
                  t={t}
                  entityType={"GRADE"}
                  columnType={"CATEGORY"}
                />
              ) : null}
            </div>

            {isModalMode && (isCurriculumDP || isCurriculumMYP) && (
              <div className={textSubtleBody3}>
                {t("academicSetup:out_of_with_label", { label: DP_MAX_GRADE })}
              </div>
            )}
          </div>
        ),
      });
    }

    const sortEntity =
      isOverallScoreEnabled && isCategoryTotalColumnVisible
        ? `${categoryId}-total`
        : `${categoryId}-gradeBoundary`;

    return {
      id: `${categoryId}-total-l1`,
      title: isCumulative ? label : t("common:label_total", { label }),
      subtitle: categorySubtext,
      backgroundColor: colors.surfaceSubtle,
      subHeaders: categoryTotalsSubHeaders,
      categoryId,
      sortEntity,
      hasSort: !isCumulative,
    };
  },
  params => JSON.stringify(params)
);

export const getConditionalPercentageForCategoryHeader = ({
  categoryTotalDisplayType,
}) => {
  return categoryTotalDisplayType === "WEIGHT" ? "" : "%";
};

export const getShowLocalGradeCondition = ({
  isLocalGradeCalculationEnabled,
  isOverallGradeEnabled,
  isCurriculumUBD,
  isUsingNewGradingSetupInCourses = false,
}) => {
  if (isUsingNewGradingSetupInCourses) {
    return false;
  }
  return isCurriculumUBD
    ? isOverallGradeEnabled
    : isLocalGradeCalculationEnabled;
};

const getStudentFinalScoreRatings = ({
  studentWiseRatings,
  termId,
  subjectId,
  filterFinalScoreWithSubjectId,
}) => {
  return _.filter(studentWiseRatings, rating => {
    let condition =
      _.isNil(_.get(rating, "category.id")) &&
      _.isNil(_.get(rating, "globalCategory.id")) &&
      _.get(rating, "academicCriteriaSet.criteriaType") === FINAL_SCORE;

    if (!_.isEmpty(termId)) {
      condition =
        condition &&
        (_.get(rating, "academicTerm.id") === termId ||
          _.get(rating, "parent.id") === termId);
    }

    if (!_.isEmpty(subjectId) && filterFinalScoreWithSubjectId) {
      condition = condition && _.get(rating, "ibPYPElementId") === subjectId;
    }

    return condition;
  });
};

const getStudentCategoryScoreRatings = ({
  studentWiseRatings,
  categoryId,
  termId,
}) => {
  return _.filter(studentWiseRatings, rating => {
    // TODO(@abhinay-toddleapp): Depend on global category once new grading setup is live for all
    const ratingCategoryId =
      _.get(rating, "category.id") || _.get(rating, "globalCategory.id");
    const ratingCriteriaType = _.get(
      rating,
      "academicCriteriaSet.criteriaType"
    );

    let condition =
      ratingCategoryId === categoryId &&
      _.includes(["FINAL_SCORE", "ASSESSMENT_CATEGORY"], ratingCriteriaType);

    if (!_.isEmpty(termId)) {
      condition = condition && _.get(rating, "academicTerm.id") === termId;
    }

    return condition;
  });
};

const getConditionalPercentageForOverallScore = ({
  termId,
  overallScoreType,
  isCumulativePeriod,
  selectedGradingPeriod,
  categoryTotalDisplayType,
  cumulativeAssociatedTermTotalDisplayType,
}) => {
  switch (overallScoreType) {
    case FINAL_SCORE: {
      if (isCumulativePeriod) {
        return selectedGradingPeriod === termId ||
          cumulativeAssociatedTermTotalDisplayType === PERCENTAGE_UPPERCASE
          ? "%"
          : "";
      }

      return "%";
    }
    case CATEGORY_TOTAL: {
      return categoryTotalDisplayType === WEIGHT ? "" : "%";
    }
  }
};

export const getOverallScoreValue = ({
  termId,
  categoryId,
  studentWiseRatings,
  isCumulativePeriod,
  selectedGradingPeriod,
  valueDataInState,
  categoryTotalDisplayType,
  overallScoreType,
  cumulativeAssociatedTermTotalDisplayType,
  subjectId,
  filterFinalScoreWithSubjectId,
}) => {
  const getOverallScoreFunc =
    overallScoreType === FINAL_SCORE
      ? getStudentFinalScoreRatings
      : getStudentCategoryScoreRatings;

  const params =
    overallScoreType === FINAL_SCORE
      ? {
          studentWiseRatings,
          termId,
          subjectId,
          filterFinalScoreWithSubjectId,
        }
      : { studentWiseRatings, categoryId, termId };

  const ratings = getOverallScoreFunc(params);

  const computedRating = _.find(ratings, { isComputedRating: true });
  const overridenRating = _.find(ratings, { isComputedRating: false });

  const computedScore = _.get(computedRating, "value", "");
  const editedScore = _.get(overridenRating, "value", "");
  const isRemoved =
    _.get(computedRating, "isRemoved") || _.get(overridenRating, "isRemoved");

  let overallScore = editedScore || computedScore;

  if (isRemoved) {
    overallScore = "";
  }

  if (valueDataInState) {
    const valueInState = _.get(valueDataInState, "value");
    const hasValueInState = _.has(valueDataInState, "value");

    if (hasValueInState) {
      overallScore = valueInState;
    }
  }

  const conditionalPercentage = getConditionalPercentageForOverallScore({
    termId,
    overallScoreType,
    isCumulativePeriod,
    selectedGradingPeriod,
    categoryTotalDisplayType,
    cumulativeAssociatedTermTotalDisplayType,
  });

  return {
    isRemoved,
    overallScore,
    computedScore,
    editedScore,
    conditionalPercentage,
  };
};

const getGradeScaleRating = ({
  termId,
  academicCriteriaSet,
  studentWiseRatings,
  isComputedRating = false,
}) => {
  return _.find(studentWiseRatings, rating => {
    return (
      _.get(rating, "academicCriteriaSet.id") === academicCriteriaSet?.id &&
      _.get(rating, "academicTerm.id") === termId &&
      _.isNil(_.get(rating, "globalCategory.id")) &&
      _.get(rating, "isComputedRating") === isComputedRating &&
      _.get(rating, "ibPYPElementType") !== ELEMENT_TYPE_MYP_OBJECTIVES
    );
  });
};

const getCategoryGradeScaleRating = ({
  categoryId,
  termId,
  academicCriteriaSet,
  studentWiseRatings,
  isComputedRating = false,
}) => {
  return _.find(studentWiseRatings, rating => {
    return (
      _.get(rating, "academicCriteriaSet.id") === academicCriteriaSet?.id &&
      _.get(rating, "academicTerm.id") === termId &&
      _.get(rating, "globalCategory.id") === categoryId &&
      _.get(rating, "isComputedRating") === isComputedRating
    );
  });
};

export const getOverallGradeValue = ({
  isTOK,
  studentWiseRatings,
  entityId,
  termId,
  categoryId,
  overallGradeType,
  isCurriculumDP,
  isCurriculumMYP,
  valueDataInState,
  academicCriteriaSet,
}) => {
  let getOverallGradeFunc = _.noop;
  let params;
  switch (overallGradeType) {
    case "CATEGORY_GRADE":
      {
        getOverallGradeFunc =
          isCurriculumDP || isCurriculumMYP
            ? getCategoryWiseStudentFinalGrade
            : geStudentCategoryOverallGrade;
        params = {
          studentWiseRatings,
          categoryId: entityId,
        };
      }
      break;
    case "FINAL_GRADE":
      {
        const subType = isTOK ? "TOK" : "SUBJECT";
        getOverallGradeFunc = getStudentFinalGradeRating;
        params = {
          studentWiseRatings,
          subType,
          termId: entityId,
        };
      }
      break;
    case "LOCAL_GRADE": {
      getOverallGradeFunc = getLocalGradeRating;
      params = {
        termId: entityId,
        studentWiseRatings,
      };
      break;
    }
    case CATEGORY_GRADE_SCALE: {
      getOverallGradeFunc = getCategoryGradeScaleRating;
      params = {
        categoryId,
        termId,
        academicCriteriaSet,
        studentWiseRatings,
      };
      break;
    }
    case GRADE_SCALE: {
      getOverallGradeFunc = getGradeScaleRating;
      params = {
        termId: entityId,
        academicCriteriaSet,
        studentWiseRatings,
      };
    }
  }

  params.isComputedRating = true;
  const computedOverallGradeRating = getOverallGradeFunc(params);

  const getGradeDisplayValue = rating => {
    const isGradeScaleType =
      overallGradeType === GRADE_SCALE ||
      overallGradeType === CATEGORY_GRADE_SCALE;
    const key = isGradeScaleType ? "abbreviation" : "label";
    if (isGradeScaleType) {
      return (
        _.get(rating, `academicCriteriaValue.${key}`) || _.get(rating, "value")
      );
    }
    return (
      _.get(rating, "value") || _.get(rating, `academicCriteriaValue.${key}`)
    );
  };

  let computedOverallGrade = getGradeDisplayValue(computedOverallGradeRating);

  params.isComputedRating = false;
  const overridenOverallGradeRating = getOverallGradeFunc(params);

  let editedOverallGrade = getGradeDisplayValue(overridenOverallGradeRating);

  let isRemoved =
    _.get(computedOverallGradeRating, "isRemoved") ||
    _.get(overridenOverallGradeRating, "isRemoved");

  if (isRemoved) {
    editedOverallGrade = "-";
  }

  if (valueDataInState) {
    const valueInState = _.get(valueDataInState, "value");
    const computedValueInState = _.get(valueDataInState, "computedValue");

    const hasValueInState = _.has(valueDataInState, "value");
    const hasComputedValueInState = _.has(valueDataInState, "computedValue");

    if (hasComputedValueInState) {
      computedOverallGrade = computedValueInState;
    }

    if (hasValueInState) {
      editedOverallGrade = valueInState;
      isRemoved = _.get(valueDataInState, "input.isRemoved");
    }
  }

  const overallGrade = editedOverallGrade ?? computedOverallGrade;

  const displaySequence =
    _.get(
      overridenOverallGradeRating,
      "academicCriteriaValue.displaySequence"
    ) ??
    _.get(computedOverallGradeRating, "academicCriteriaValue.displaySequence");

  return {
    overallGrade,
    computedOverallGrade,
    editedOverallGrade,
    displaySequence,
    isRemoved,
  };
};

export const getCanEntityBeOverridden = ({
  entity,
  isCurriculumPYP,
  isCurriculumUBD,
  isCurriculumMYP,
  canOverrideFinalGrade,
  gradeCalculationMethod,
  finalGradeCalculationMethod,
  isOverallScoreEnabled,
  isGradingPeriodUnlocked = true,
  isViewOnly,
  isClassArchived = false,
}) => {
  if (isViewOnly || !isGradingPeriodUnlocked || isClassArchived) {
    return false;
  }

  const isGradeCalculationMethodSelected = !_.isEmpty(gradeCalculationMethod);

  switch (entity) {
    case CATEGORY_TOTAL:
    case FINAL_SCORE:
    case CATEGORY_GRADE_SCALE:
    case GRADE_SCALE: {
      return canOverrideFinalGrade;
    }

    case FINAL_GRADE:
    case CATEGORY_GRADE:
      {
        switch (finalGradeCalculationMethod) {
          case SCORE: {
            return isGradeCalculationMethodSelected && canOverrideFinalGrade;
          }
          case MYP_CRITERIA: {
            return canOverrideFinalGrade;
          }
          case MANUAL: {
            return true;
          }
        }
      }
      break;

    case LOCAL_GRADE: {
      if (isCurriculumUBD) {
        switch (finalGradeCalculationMethod) {
          case SCORE: {
            return isGradeCalculationMethodSelected && canOverrideFinalGrade;
          }
          case MANUAL: {
            return true;
          }
        }
      }

      if (isCurriculumPYP) {
        return !isOverallScoreEnabled || canOverrideFinalGrade;
      }

      return canOverrideFinalGrade;
    }

    case CRITERION_A:
    case CRITERION_B:
    case CRITERION_C:
    case CRITERION_D:
    case CRITERION_A_COMMENT:
    case CRITERION_B_COMMENT:
    case CRITERION_C_COMMENT:
    case CRITERION_D_COMMENT:
    case CRITERION_A_GRADE_SCALE:
    case CRITERION_B_GRADE_SCALE:
    case CRITERION_C_GRADE_SCALE:
    case CRITERION_D_GRADE_SCALE: {
      return isCurriculumMYP;
    }

    case SUBJECT_DESCRIPTION:
    case FINAL_COMMENT: {
      return true;
    }

    default: {
      return false;
    }
  }
};

export const getIsGradingEnabled = ({
  canManageGradebook,
  isAdminGradebook,
  isGradingPeriodUnlocked,
  doesTeacherHaveAccessToAssignment,
  isClassArchived = false,
}) => {
  return (
    canManageGradebook &&
    !isAdminGradebook &&
    isGradingPeriodUnlocked &&
    doesTeacherHaveAccessToAssignment &&
    !isClassArchived
  );
};

export const getAssessmentToolsModalProps = ({
  t,
  course,
  students,
  selectedToolTab,
  setSelectedToolTab,
  refetchStudentWiseRatings,
  assessmentToolsModalParams,
  selectedStudents: selectedStudentIds,
  getSortedStudents,
}) => {
  const isTeacherEvaluationEnabled = _.get(
    assessmentToolsModalParams,
    "isTeacherEvaluationEnable",
    false
  );
  const isEvaluatedByStudent = _.get(
    assessmentToolsModalParams,
    "isStudentEvaluationEnable",
    false
  );

  const assignmentId = _.get(assessmentToolsModalParams, "id", "");

  const studentId = _.get(assessmentToolsModalParams, "student.student.id", "");
  const templateId = _.get(
    assessmentToolsModalParams,
    "content.templateId",
    ""
  );
  const assessmentFields = _.get(
    assessmentToolsModalParams,
    "content.fields",
    []
  );
  const ibPYPElementRatings = _.get(
    assessmentToolsModalParams,
    "ibPYPElementRatings"
  );
  const unitPlanFields = _.get(
    assessmentToolsModalParams,
    "content.unitPlan.allFields",
    []
  );

  const assessmentToolId = _.get(
    assessmentToolsModalParams,
    "assessmentToolId",
    ""
  );
  const assessmentToolType = _.get(
    assessmentToolsModalParams,
    "assessmentToolType",
    ""
  );

  const toolResponsesData = _.get(
    assessmentToolsModalParams,
    "student.toolResponses",
    {}
  );
  const toolResponses = _.get(
    _.find(toolResponsesData, ({ toolId }) => toolId === assessmentToolId),
    "toolResponses",
    {}
  );

  const comment = getStaffElementRemarkMessage(
    _.get(assessmentToolsModalParams, "student.elementRemarks", [])
  );

  const modalTitle = t(
    _.get(
      _.find(ASSESSMENT_TOOLS, item => item.type == assessmentToolType),
      "locale",
      ""
    )
  );
  const firstName = _.get(
    assessmentToolsModalParams,
    "studentDetails.firstName",
    ""
  );
  const lastName = _.get(
    assessmentToolsModalParams,
    "studentDetails.lastName",
    ""
  );
  const assessmentName = _.get(
    assessmentToolsModalParams,
    "content.title.value",
    ""
  );
  const profileImage = _.get(
    assessmentToolsModalParams,
    "studentDetails.profileImage"
  );

  const filterConfig = _.get(assessmentToolsModalParams, "filterConfig", []);
  const assignedStudents = _.get(
    assessmentToolsModalParams,
    "assignedStudents",
    []
  );

  const selectedStudents = _.filter(students, ({ id }) => {
    return _.isEmpty(selectedStudentIds) || _.includes(selectedStudentIds, id);
  });

  const sortedAssignedStudents = getSortedAssignedStudents({
    assignedStudents,
    filterConfig,
    getSortedStudents,
    selectedStudents,
  });

  return {
    id: assessmentToolId,
    type: assessmentToolType,
    entityType: _.get(
      _.find(ASSESSMENT_TOOLS, { type: assessmentToolType }),
      "entityType",
      ""
    ),
    mode: "view",
    toolResponses,
    title: modalTitle,
    showChooseButton: false,
    course,
    isEvaluatedByStudent,
    assessmentFields,
    templateId,
    parentId: assignmentId,
    parentType: "STUDENT_ASSIGNMENT",
    unitPlanFields,
    studentId,
    ibPYPElementRatings,
    isTeacherEvaluationEnabled,
    commentMessage: comment,
    profileImage,
    firstName,
    lastName,
    assessmentName,
    selectedToolTab,
    sourceFrom: "GRADEBOOK",
    refetchStudentWiseRatings,
    defaultStudentNameSortPreference: _.get(
      assessmentToolsModalParams,
      "defaultStudentNameSortPreference",
      {}
    ),
    defaultStudentNameOrderPreference: _.get(
      assessmentToolsModalParams,
      "defaultStudentNameOrderPreference",
      {}
    ),
    assignedStudents: sortedAssignedStudents,
    updateSelectedToolTab: setSelectedToolTab,
    deadline: _.get(assessmentToolsModalParams, "deadline"),
    maxScore: _.get(assessmentToolsModalParams, "maxScore"),
    courseId: _.get(assessmentToolsModalParams, "course.id"),
    subjectId: _.get(assessmentToolsModalParams, "subjectId"),
    content: _.get(assessmentToolsModalParams, "content", {}),
    criterias: _.get(assessmentToolsModalParams, "criterias", []),
    variables: _.get(assessmentToolsModalParams, "variables", {}),
    objectives: _.get(assessmentToolsModalParams, "objectives", []),
    contentType: _.get(assessmentToolsModalParams, "contentType", ""),
    assignmentState: _.get(assessmentToolsModalParams, "state.state"),
    filterConfig: _.get(assessmentToolsModalParams, "filterConfig", {}),
    studentAssignmentId: _.get(assessmentToolsModalParams, "student.id"),
    criteriaNodes: _.get(assessmentToolsModalParams, "criteriaNodes", []),
    assessmentTools: _.get(assessmentToolsModalParams, "assessmentTools", []),
    onClickAssessment: _.get(assessmentToolsModalParams, "onClickAssessment"),
    showLoaderBar: false,
  };
};

export const getCategoryWiseAssessmentsMemoized = _.memoize(
  ({ academicCriteriaSets }) => {
    const categoryWiseAssessments = [];
    _.forEach(academicCriteriaSets, academicCriteriaSet => {
      _.forEach(
        _.get(academicCriteriaSet, "academicCriteriaValues", []),
        academicCriteriaValue => {
          const updatedAcademicCriteriaValue = _.cloneDeep(
            academicCriteriaValue
          );
          const assignments = _.get(academicCriteriaValue, "assignments", []);

          categoryWiseAssessments.push({
            ...updatedAcademicCriteriaValue,
            assignments,
            academicCriteriaSetId: _.get(academicCriteriaSet, "id"),
            isShowGradeBoundaries: _.get(
              academicCriteriaSet,
              "isShowGradeBoundaries",
              false
            ),
          });
        }
      );
    });
    return categoryWiseAssessments;
  },
  params => JSON.stringify(params)
);

const categoriesViewStyles = {
  studentHeader: { width: "260px", backgroundColor: colors.surfaceWhite },
  studentName: { whiteSpace: "pre-wrap" },
  studentCell: { backgroundColor: colors.surfaceWhite },
  greyCell: { backgroundColor: colors.surfaceSubtle },
  totalHeader: {
    backgroundColor: colors.surfaceSubtle,
    color: colors.textSubtle,
    textAlign: "center",
    minWidth: "132px",
    height: "32px",
    padding: "8px 12px",
  },
};

export const getCategorySubtext = _.memoize(
  ({
    t,
    orderedScoreValues,
    isCurriculumDP,
    curriculumProgramId,
    differentWeightsForLevels,
    conditionalPercentage,
    isTOK,
    categoryTotalDisplayType,
    isWeightageForCategoryEnabled,
    isModalMode = false,
    courseLevels = [],
  }) => {
    if (!isWeightageForCategoryEnabled) {
      return "";
    }
    const isCategoryDisplayTypePercentage =
      categoryTotalDisplayType === PERCENTAGE_UPPERCASE;

    if (isModalMode && isCategoryDisplayTypePercentage) {
      return t("out_of_with_label", { label: "100%" });
    }

    const categoryScoreValuePath = isCategoryDisplayTypePercentage
      ? "percentageValue"
      : "value";

    const showCategoryLevelWiseWeightage =
      shouldShowLevelsInGradingSetupV2(curriculumProgramId) &&
      differentWeightsForLevels;

    // 2.0, map over levels
    if (!_.isEmpty(courseLevels) && showCategoryLevelWiseWeightage) {
      return _.join(
        _.compact(
          _.map(courseLevels, level => {
            const levelId = _.get(level, "id");
            const levelAbbreviation = _.get(
              level,
              "abbreviation",
              _.get(level, "label", "")
            );

            const scoreValue = _.find(
              orderedScoreValues,
              score => _.get(score, "subjectLevel.id") === levelId
            );

            const value = _.get(scoreValue, categoryScoreValuePath);
            const prefix = !_.isEmpty(levelAbbreviation)
              ? `${levelAbbreviation} : `
              : "";

            return !_.isNil(value)
              ? `${prefix}${value}${conditionalPercentage}`
              : null;
          })
        ),
        ", "
      );
    }

    // 1.0, map over score values
    return _.join(
      _.compact(
        _.map(orderedScoreValues, scoreValue => {
          const genericTagLabel = showCategoryLevelWiseWeightage
            ? _.get(
                scoreValue,
                "subjectLevel.abbreviation",
                t("common:default")
              )
            : _.get(scoreValue, "genericTag.label", "");
          const value = _.get(scoreValue, categoryScoreValuePath);
          const prefix =
            ((isCurriculumDP && !isTOK) || showCategoryLevelWiseWeightage) &&
            !_.isEmpty(genericTagLabel)
              ? `${genericTagLabel} : `
              : "";

          return !_.isNil(value)
            ? `${prefix}${value}${conditionalPercentage}`
            : "";
        })
      ),
      ", "
    );
  },
  params => JSON.stringify(params)
);

export const getCategoryWiseStudentFinalGrade = ({
  studentWiseRatings,
  categoryId,
  isComputedRating = false,
}) => {
  return _.find(
    studentWiseRatings,
    rating =>
      _.get(rating, "category.id") === categoryId &&
      _.get(rating, "academicCriteriaSet.criteriaType") === FINAL_GRADE &&
      _.get(rating, "isComputedRating") === isComputedRating
  );
};

export const geStudentCategoryOverallGrade = ({
  studentWiseRatings,
  categoryId,
  isComputedRating = false,
}) => {
  return _.find(
    studentWiseRatings,
    rating =>
      _.get(rating, "category.id") === categoryId &&
      _.get(rating, "academicCriteriaSet.criteriaType") === "LOCAL_GRADE" &&
      _.get(rating, "isComputedRating") === isComputedRating
  );
};

const getStudentFinalGradeRatingByParentType = ({
  studentWiseRatings,
  subType = "SUBJECT",
  isComputedRating = false,
  termId,
  parentType = STUDENT_USER,
}) => {
  return _.find(studentWiseRatings, rating => {
    let condition =
      _.get(rating, "academicCriteriaSet.criteriaType") === "FINAL_GRADE" &&
      _.get(rating, "isComputedRating") === isComputedRating &&
      _.get(rating, "academicCriteriaSet.subType") === subType &&
      _.isNil(_.get(rating, "category.id"));

    if (!_.isEmpty(termId)) {
      condition =
        condition &&
        (_.get(rating, "academicTerm.id") === termId ||
          _.get(rating, "parent.id") === termId);
    }

    if (!_.isEmpty(parentType)) {
      condition = condition && _.get(rating, "parentType") === parentType;
    }

    return condition;
  });
};

export const getStudentFinalGradeRating = ({
  studentWiseRatings,
  subType = "SUBJECT",
  isComputedRating = false,
  termId,
}) => {
  const rating = getStudentFinalGradeRatingByParentType({
    studentWiseRatings,
    subType,
    isComputedRating,
    termId,
    parentType: STUDENT_USER,
  });

  return rating;
};

export const STUDENT_NAME_SORT_ENTITIES = [
  FIRST_NAME,
  LAST_NAME,
  MIDDLE_NAME,
  FULL_NAME,
];

export const getIsInitialAssessmentFilters = _.memoize(
  ({
    evaluationTypes,
    searchText,
    selectedAssessmentTools,
    selectedAssessmentTypes,
    selectedCategories,
    assessmentToolOptions,
    assessmentTypeOptions,
    students,
    selectedStudents,
  }) => {
    return (
      evaluationTypes === "ALL" &&
      searchText === "" &&
      _.isEmpty(selectedCategories) &&
      _.size(selectedAssessmentTools) === _.size(assessmentToolOptions) &&
      _.size(selectedAssessmentTypes) === _.size(assessmentTypeOptions) &&
      _.isEqual(
        selectedStudents,
        _.map(
          _.filter(students, student => !_.get(student, "isArchived")),
          "id"
        )
      )
    );
  },
  params => JSON.stringify(params)
);

export const getWeightageTextLabels = _.memoize(
  ({
    subjectLevelGenericTags,
    isAbsoluteWeightageType,
    t,
    isAssessmentCategory,
    isTOK,
    isCategoryGradeBoundary,
    isCategoryDisplayTypePercentage = true,
    isCurriculumUBD,
    criteriaLabels,
  }) => {
    const weightageTextLabels = [];
    if (isTOK && !isAssessmentCategory && !isCategoryGradeBoundary) {
      weightageTextLabels.push(
        <div>
          {_.get(criteriaLabels, FINAL_SCORE, t("common:final_score"))}
          <div className={tokFinalScoreSubtextClass}>
            {t("academicSetup:out_of_with_label", { label: "30" })}
          </div>
        </div>
      );
    }
    const finalScoreLabel = _.get(criteriaLabels, FINAL_SCORE, null);

    if (!_.isEmpty(subjectLevelGenericTags)) {
      _.forEach(subjectLevelGenericTags, tag => {
        const tagLabel = tag?.label;
        let weightageTextLabel = "";
        switch (true) {
          case isCategoryGradeBoundary:
            weightageTextLabel = `${_.get(
              criteriaLabels,
              CATEGORY_SCORE,
              t("common:score")
            )} (${tagLabel})`;
            break;
          case isAbsoluteWeightageType:
            weightageTextLabel = `${t("common:weight")} ${tagLabel}`;
            break;
          case !isCategoryDisplayTypePercentage:
            weightageTextLabel = tagLabel;
            break;
          default:
            weightageTextLabel = !_.isNil(finalScoreLabel)
              ? `${finalScoreLabel} (${tagLabel})`
              : `% ${tagLabel}`;
            break;
        }
        weightageTextLabels.push(weightageTextLabel);
      });
    } else {
      let weightageTextLabel = "";
      switch (true) {
        case isCurriculumUBD:
          weightageTextLabel = _.get(
            criteriaLabels,
            FINAL_SCORE,
            t("common:percentage_score")
          );
          break;
        case isCategoryGradeBoundary:
          weightageTextLabel = `${t("common:score")}`;
          break;
        case !isCategoryDisplayTypePercentage:
          weightageTextLabel = t("common:weight");
          break;
        case isCategoryDisplayTypePercentage:
          weightageTextLabel = t("progressReport:percentage");
          break;
        case isAbsoluteWeightageType && !isTOK:
          weightageTextLabel = t("academicSetup:absolute_weight");
          break;
        case isAssessmentCategory:
          weightageTextLabel = "%";
          break;
        default:
          weightageTextLabel = t("progressReport:percentage");
      }
      weightageTextLabels.push(weightageTextLabel);
    }
    return weightageTextLabels;
  },
  params => JSON.stringify(params)
);

export const getAssessmentsGradebookSubjectSettingsEmptyStateText = ({
  isAssessmentCategoryTabSelected,
  isEditingAllowed,
  t,
  subjectLabel,
  gradeLabel,
  handleShowModal,
  organizationPointSet,
  termId,
  termLabel,
  curriculumType,
  criteriaLabels,
  isGradingPeriodUnlocked = true,
}) => {
  let templateLabel = "";
  let primaryButtonLabel = "";
  let emptyStateSubtitle = "";

  const isCurriculumUBD = curriculumType === CURRICULUM_TYPE_UBD;

  const onClickDefineTemplate = () =>
    handleShowModal({
      showEmptyState: true,
      organizationPointSet,
      termId,
      termLabel,
    });

  if (isAssessmentCategoryTabSelected) {
    templateLabel = t("common:category_set");
    primaryButtonLabel = t("progressSummary:create_a_with_label", {
      label: templateLabel,
    });
    if (isEditingAllowed) {
      emptyStateSubtitle = t(
        "progressSummary:define_categories_and_weightages_with_label",
        { label: subjectLabel }
      );
    } else {
      emptyStateSubtitle = t(
        "schoolSetup:once_school_admin_creates_category_set"
      );
    }
  } else {
    templateLabel = t("academicSetup:grade_boundary");
    primaryButtonLabel = t("progressSummary:define_with_label", {
      label: templateLabel,
    });
    if (isEditingAllowed) {
      const fromLabel = _.get(
        criteriaLabels,
        FINAL_SCORE,
        isCurriculumUBD ? t("common:final_score") : t("common:overall_score")
      );
      const toLabel = isCurriculumUBD
        ? _.get(criteriaLabels, LOCAL_GRADE, t("common:overall_grade"))
        : _.get(criteriaLabels, FINAL_GRADE, t("common:ib_grade"));

      emptyStateSubtitle = t(
        "progressSummary:convert_final_score_to_final_grade_by_defining_grade_boundary_for_grade_and_subject_with_criteria_labels",
        {
          from: fromLabel,
          to: toLabel,
          grade: gradeLabel,
          subject: subjectLabel,
        }
      );
    } else {
      emptyStateSubtitle = t(
        "progressSummary:once_school_admin_defines_grade_boundary"
      );
    }
  }

  const emptyStateTitle = t("progressSummary:no_label_for_this_term_yet", {
    label: templateLabel,
  });

  const primaryButton = isEditingAllowed ? (
    <ConditionalWrapper
      condition={!isGradingPeriodUnlocked}
      wrapper={
        <Tooltip
          tooltip={t("academicSetup:you_cannot_with_label_since_locked", {
            label: primaryButtonLabel,
          })}
          overlayInnerStyle={styles.lockingGradeTooltipOverlayInner}
        />
      }
      type={"wrapperAsElement"}
    >
      <Button
        onClick={onClickDefineTemplate}
        disabled={!isGradingPeriodUnlocked}
        dsVersion="2.0"
        data-testid={`Gradebook-gradingSetup-createButton-${templateLabel}`}
      >
        {primaryButtonLabel}
      </Button>
    </ConditionalWrapper>
  ) : null;

  return {
    title: emptyStateTitle,
    subtitle: emptyStateSubtitle,
    illustration: NoChecklistIllustration,
    primaryButton,
  };
};

export const getAssessmentsGradebookSubjectSettingsEditModalText = _.memoize(
  ({
    termLabel,
    subjectName,
    showEmptyState,
    selectedGrade,
    t,
    isAssessmentCategoryTabSelected,
    isCurriculumPYP,
  }) => {
    const localeKeyLabelsObj = {
      term: termLabel,
      subject: subjectName,
    };

    const templateLabel = isAssessmentCategoryTabSelected
      ? t("common:category_set")
      : t("academicSetup:grade_boundary");

    const assessmentCategoryLocaleKey = showEmptyState
      ? "schoolSetup:assessment_categories_edit_modal_text"
      : "schoolSetup:edit_assessment_category_set_with_subject_and_term_name";
    const gradeBoundaryLocaleKey = showEmptyState
      ? "progressSummary:define_grade_boundary_with_term_and_subject_name"
      : "progressSummary:edit_grade_boundary_with_term_and_subject_name";

    const editAssessmentCategoryModalTitle = t(
      assessmentCategoryLocaleKey,
      localeKeyLabelsObj
    );
    const editGradeBoundaryModalTitle = t(
      gradeBoundaryLocaleKey,
      localeKeyLabelsObj
    );
    const modalAlertText = isCurriculumPYP
      ? t(
          "schoolSetup:pyp_assessment_category_alert_text_for_subject_settings",
          {
            subjectName,
            gradeName: selectedGrade?.label,
          }
        )
      : t(
          "progressSummary:please_note_that_this_label_will_be_applied_to_all_classes_subject",
          {
            label: templateLabel,
            grade: selectedGrade?.label,
            subject: subjectName,
          }
        );

    return {
      editAssessmentCategoryModalTitle,
      editGradeBoundaryModalTitle,
      modalAlertText,
    };
  },
  params => JSON.stringify(params)
);

export const getUpdatedSortedStudents = ({
  tableData,
  sortEntity,
  direction,
  selectedStudents,
}) => {
  const dataToBeSorted = [];

  _.forEach(tableData, row => {
    const rowData = _.get(row, "rowData", []);
    _.forEach(rowData, data => {
      if (data?.key === sortEntity) {
        const value = _.get(data, "sortValue") ?? _.get(data, "value");

        dataToBeSorted.push({
          student: _.find(
            selectedStudents,
            selectedStudent => selectedStudent?.id === _.get(row, "id")
          ),
          value,
        });
      }
    });
  });

  const updatedStudents = _.map(
    _.orderBy(
      dataToBeSorted,
      ["value", "student.firstName"],
      [_.toLower(direction), "asc"]
    ),
    "student.id"
  );

  return updatedStudents;
};

export const getStudentsByFilterConfig = ({
  selectedStudents,
  filterConfig,
  getSortedStudents,
  allStudents,
}) => {
  const students = _.isEmpty(selectedStudents) ? allStudents : selectedStudents;
  const selectedSortConfig = _.get(filterConfig, "0.columnKey", "students");

  return selectedSortConfig === "students"
    ? getSortedStudents({
        students,
        sortOptions: _.first(filterConfig),
      })
    : students;
};

export const getTerms = ({ selectedGradingPeriodObj, gradingPeriods }) => {
  const terms = [];
  const associatedTerms = _.get(
    selectedGradingPeriodObj,
    "associatedAcademicTermEntities"
  );
  _.forEach(gradingPeriods, period => {
    const associatedTermEntity = _.find(
      associatedTerms,
      term => term?.academicTerm?.id === period?.id
    );
    const isAssociatedTerm = !_.isEmpty(associatedTermEntity);
    if (isAssociatedTerm) {
      const percentage = _.get(
        associatedTermEntity,
        "cumulativeAcademicTermWeightages.0.percentage"
      );
      const weight = _.get(
        associatedTermEntity,
        "cumulativeAcademicTermWeightages.0.weightage"
      );
      terms.push({ ...period, percentage, weight });
    }
    const isSelectedTerm = period?.id === selectedGradingPeriodObj?.id;
    if (isSelectedTerm) {
      terms.push(period);
    }
  });
  return terms;
};

export const getTermsV2 = ({
  selectedGradingPeriodObj,
  gradingPeriods,
  cumulativeTermWeights,
}) => {
  const terms = [];

  const cumulativeTermWeightsMap = _.keyBy(
    cumulativeTermWeights,
    "cumulativeTermId"
  );

  const associatedTermWeights = _.get(
    cumulativeTermWeightsMap,
    [selectedGradingPeriodObj?.id, "associatedTermWeights"],
    []
  );

  _.forEach(gradingPeriods, period => {
    const associatedTermEntity = _.find(
      associatedTermWeights,
      termWeight => termWeight?.termId === period?.id
    );

    const isAssociatedTerm = !_.isEmpty(associatedTermEntity);

    if (isAssociatedTerm) {
      const totalWeights = _.reduce(
        associatedTermWeights,
        (sum, item) => {
          const weight = _.get(item, "weight") || 0;

          return sum + parseFloat(weight);
        },
        0
      );

      const associatedTermWeightsMap = _.keyBy(associatedTermWeights, "termId");

      const associatedTermWeight = _.get(associatedTermWeightsMap, [
        period?.id,
        "weight",
      ]);

      const percentageValue = associatedTermWeight
        ? _.round((associatedTermWeight / totalWeights) * 100, 2)
        : 0;

      terms.push({
        ...period,
        percentage: percentageValue,
        weight: associatedTermWeight,
      });
    }

    const isSelectedTerm = period?.id === selectedGradingPeriodObj?.id;
    if (isSelectedTerm) {
      terms.push(period);
    }
  });

  return terms;
};

export const getCumulativeViewTermHeaderSubtext = ({
  t,
  termId,
  selectedGradingPeriod,
  termPercentage,
  selectedGradingPeriodLabel,
  termWeightage,
  cumulativeAssociatedTermTotalDisplayType,
  isOverallScoreEnabled,
}) => {
  if (!isOverallScoreEnabled) {
    return "";
  }

  if (termId === selectedGradingPeriod) {
    return "100%";
  }

  if (cumulativeAssociatedTermTotalDisplayType === "WEIGHT") {
    return termWeightage ? `${t("common:weight")} - ${termWeightage}` : "";
  } else {
    return termPercentage
      ? t("progressSummary:percentage_of_label", {
          percentage: `${_.round(termPercentage, 2)}%`,
          label: selectedGradingPeriodLabel,
        })
      : "";
  }
};

export const getAssociatedTerms = _.memoize(({ term }) => {
  const associatedAcademicTermEntities = _.get(
    term,
    "associatedAcademicTermEntities"
  );
  if (!_.isEmpty(associatedAcademicTermEntities)) {
    return _.map(associatedAcademicTermEntities, item =>
      _.get(item, "academicTerm.id")
    );
  } else {
    return [];
  }
}, JSON.stringify);

export const getHasBothSLandHLStudents = _.memoize(
  ({ students }) => {
    return (
      _.some(students, student => {
        const studentTagLabel = _.get(student, "tag.label", "");
        return studentTagLabel === "HL";
      }) &&
      _.some(students, student => {
        const studentTagLabel = _.get(student, "tag.label", "");
        return studentTagLabel === "SL";
      })
    );
  },
  params => JSON.stringify(params)
);

export const getAssessmentGradebookSettingsTabOptions = ({
  t,
  isCumulativePeriod,
  isUsingNewGradingSetupInCourses,
  isCommonDisplaySettingsEnabled,
}) => {
  const options = [
    {
      value: "CONFIGURE_COLUMNS",
      label: t("common:configure_columns"),
      isHidden: isCumulativePeriod && isUsingNewGradingSetupInCourses,
    },
    {
      value: "DISPLAY_SETTINGS",
      label: t("progressSummary:display_settings"),
      isHidden: isCommonDisplaySettingsEnabled,
    },
  ];

  return _.filter(options, ({ isHidden = false }) => !isHidden);
};

const getCumulativePeriodConfigureColumnOptions = ({
  gradingPeriods,
  selectedGradingPeriod,
  t,
  isCurriculumUBD,
  isCurriculumDP,
  isCurriculumMYP,
  isOverallScoreEnabled,
  isOverallGradeEnabled,
  scoreText,
  ibGradeText,
  isLocalGradeCalculationEnabled,
  criteriaLabels,
  isAssessmentGradebookHeaderEnhancements = false,
  isAllSubjectsSelected,
  finalGradeCalculationMethod,
  canOverrideFinalGrade,
  hiddenGradebookEntities,
  isForColumnConfigurations,
  isUsingNewGradingSetupInCourses,
}) => {
  const selectedGradingPeriodObj = _.find(
    gradingPeriods,
    period => period?.id === selectedGradingPeriod
  );

  const terms = getTerms({ selectedGradingPeriodObj, gradingPeriods });

  const local_grade_text = _.get(
    criteriaLabels,
    LOCAL_GRADE,
    isCurriculumUBD ? t("common:grade") : t("common:local_grade_text")
  );

  return _.map(terms, term => {
    const { id: termId, label: termLabel, type } = term;

    const leafHeaderParent = isAssessmentGradebookHeaderEnhancements
      ? `${termId}-total`
      : termId;

    const scoreItem = isOverallScoreEnabled
      ? [
          {
            id: `${termId}-FINAL_SCORE`,
            entity: FINAL_SCORE,
            label: _.get(criteriaLabels, FINAL_SCORE, scoreText),
            parent: leafHeaderParent,
          },
        ]
      : [];

    const criteriaColumns = isCurriculumMYP
      ? getConfigureColumnsMypCriteriaColumnOptions({ t, termId })
      : [];

    const finalGradeItem =
      isOverallGradeEnabled && (isCurriculumDP || isCurriculumMYP)
        ? [
            {
              id: `${termId}-FINAL_GRADE`,
              entity: FINAL_GRADE,
              label: _.get(criteriaLabels, FINAL_GRADE, ibGradeText),
              parent: leafHeaderParent,
            },
          ]
        : [];

    const commentColumns = ACLStore.can(
      "FeatureFlag:EnableSubjectCommentsSupportInGradebook"
    )
      ? [
          {
            id: `${termId}-${SUBJECT_DESCRIPTION}`,
            entity: SUBJECT_DESCRIPTION,
            label: t("classRoom:subject_description"),
            parent: TERM_TOTAL_COLUMNS,
          },
          {
            id: `${termId}-${FINAL_COMMENT}`,
            entity: FINAL_COMMENT,
            label: t("classRoom:final_comment"),
            parent: TERM_TOTAL_COLUMNS,
          },
        ]
      : [];

    const showLocalGradeCondition = getShowLocalGradeCondition({
      isCurriculumUBD,
      isLocalGradeCalculationEnabled,
      isOverallGradeEnabled,
      isUsingNewGradingSetupInCourses,
    });

    const localGradeItem = showLocalGradeCondition
      ? [
          {
            id: `${termId}-LOCAL_GRADE`,
            entity: LOCAL_GRADE,
            label: local_grade_text,
            parent: leafHeaderParent,
          },
        ]
      : [];

    if (isAssessmentGradebookHeaderEnhancements) {
      const showRootWithoutChildren = isUsingNewGradingSetupInCourses;

      const children = _.filter(
        [
          ...scoreItem,
          ...criteriaColumns,
          ...finalGradeItem,
          ...localGradeItem,
          ...commentColumns,
        ],
        ({ entity }) => !_.get(hiddenGradebookEntities, entity)
      );

      const termTotal = {
        id: `${termId}-total`,
        label: t("common:total"),
        parent: termId,
        children: showRootWithoutChildren ? [] : children,
      };

      const categoryColumns = _.isEqual(type, "CUMULATIVE")
        ? []
        : getConfigureColumnsCategoryColumnOptions({
            t,
            isCurriculumDP,
            isCurriculumMYP,
            isCurriculumUBD,
            isCategoriesView: false,
            assessmentColumnsOptions: [],
            isOverallScoreEnabled,
            isOverallGradeEnabled,
            scoreText,
            ibGradeText,
            isAllSubjectsSelected,
            finalGradeCalculationMethod,
            canOverrideFinalGrade,
            criteriaLabels,
            termId,
            hiddenGradebookEntities,
            isForColumnConfigurations,
            isUsingNewGradingSetupInCourses,
          });

      return [
        {
          id: termId,
          label: termLabel,
          parent: null,
          children: [...categoryColumns, termTotal],
        },
      ];
    }

    return [
      {
        id: termId,
        label: t("common:label_total", { label: termLabel }),
        parent: null,
        children: [
          ...scoreItem,
          ...criteriaColumns,
          ...finalGradeItem,
          ...localGradeItem,
          ...commentColumns,
        ],
      },
    ];
  });
};

const getAssessmentColumnsOptions = ({
  t,
  isCategoriesView,
  isCurriculumMYP,
  isCurriculumDP,
  isForColumnConfigurations = false,
}) => {
  if (isForColumnConfigurations) {
    return [
      {
        id: "ASSESSMENT_COLUMNS",
        label: t("common:assessment_label_plural"),
        parent: isCategoriesView ? "CATEGORY_COLUMNS" : null,
      },
    ];
  }

  const assessmentColumns = [
    {
      id: "ASSESSMENT_COLUMNS",
      label: t("progressSummary:assessments_and_assessment_tools"),
      parent: isCategoriesView ? "CATEGORY_COLUMNS" : null,
      children: [
        {
          id: SCORE,
          label: t("common:score"),
          parent: "ASSESSMENT_COLUMNS",
        },
        {
          id: "COMMENT",
          label: t("common:final_remarks"),
          parent: "ASSESSMENT_COLUMNS",
        },
        {
          id: "RUBRIC",
          label: t("common:rubric"),
          parent: "ASSESSMENT_COLUMNS",
        },
        {
          id: "CHECKLIST",
          label: t("common:checklist"),
          parent: "ASSESSMENT_COLUMNS",
        },
        {
          id: "SINGLE_POINT_RUBRIC",
          label: t("common:single_point_rubric"),
          parent: "ASSESSMENT_COLUMNS",
        },
        {
          id: "LEARNING_GOALS",
          label: t("common:learning_goals"),
          parent: "ASSESSMENT_COLUMNS",
        },
      ],
    },
  ];

  if (isCurriculumDP) {
    _.get(assessmentColumns, "0.children").splice(
      1,
      0,
      {
        id: "IB_GRADE",
        label: t("common:ib_grade"),
        parent: "ASSESSMENT_COLUMNS",
      },
      {
        id: "DP_ASSESSMENT_RUBRIC",
        label: t("classRoom:dp_criteria"),
        parent: "ASSESSMENT_COLUMNS",
      }
    );
  }

  if (isCurriculumMYP) {
    _.get(assessmentColumns, "0.children").splice(
      1,
      0,
      {
        id: "MYP_OBJECTIVE_RUBRIC",
        label: t("common:myp_criteria_rubric"),
        parent: "ASSESSMENT_COLUMNS",
      },
      {
        id: "MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC",
        label: t("common:interdisciplinary_criteria_rubric"),
        parent: "ASSESSMENT_COLUMNS",
      }
    );
  }

  return assessmentColumns;
};

const getConfigureColumnsCategoryColumnOptions = ({
  t,
  isCurriculumDP,
  isCurriculumMYP,
  isCurriculumUBD,
  isCategoriesView,
  hiddenGradebookEntities,
  assessmentColumnsOptions,
  isOverallScoreEnabled,
  isOverallGradeEnabled,
  scoreText,
  ibGradeText,
  isAllSubjectsSelected,
  finalGradeCalculationMethod,
  canOverrideFinalGrade,
  criteriaLabels,
  termId,
  isForColumnConfigurations,
  isUsingNewGradingSetupInCourses,
}) => {
  if (isAllSubjectsSelected) {
    return;
  }

  const addPrefixAsTermId = key => {
    return _.isEmpty(termId) ? key : _.join([termId, key], "-");
  };

  const categoryColumnsLeaves = [];

  const showCategoryScore =
    !_.get(hiddenGradebookEntities, CATEGORY_SCORE) && isOverallScoreEnabled;
  if (showCategoryScore) {
    categoryColumnsLeaves.push({
      id: addPrefixAsTermId(CATEGORY_TOTAL),
      label: _.get(criteriaLabels, CATEGORY_SCORE, scoreText),
      parent: addPrefixAsTermId(CATEGORY_COLUMNS),
    });
  }

  const canShowCategoryGradeColumns = getCanShowCategoryGradeColumnsInGradebook(
    {
      isCurriculumDP,
      isCurriculumUBD,
      isCurriculumMYP,
      finalGradeCalculationMethod,
      isOverallGradeEnabled,
      canOverrideFinalGrade,
    }
  );

  const showCategoryGrade =
    !_.get(hiddenGradebookEntities, CATEGORY_GRADE) &&
    canShowCategoryGradeColumns &&
    !isUsingNewGradingSetupInCourses;

  if (showCategoryGrade) {
    categoryColumnsLeaves.push({
      id: addPrefixAsTermId(CATEGORY_GRADE),
      label: _.get(
        criteriaLabels,
        CATEGORY_GRADE,
        isCurriculumUBD ? t("common:grade") : ibGradeText
      ),
      parent: addPrefixAsTermId(CATEGORY_COLUMNS),
    });
  }

  const categoryTotalLabel = t("common:label_total", {
    label: t("academicSetup:category"),
  });

  const children = [];

  const showRootWithoutChildren = isUsingNewGradingSetupInCourses;

  if (isCategoriesView) {
    const categoryTotalSubsection = [];
    if (!_.isEmpty(categoryColumnsLeaves) || showRootWithoutChildren) {
      categoryTotalSubsection.push({
        id: "CATEGORY_TOTAL_SUBSECTION",
        label: categoryTotalLabel,
        children: showRootWithoutChildren ? [] : categoryColumnsLeaves,
      });
    }
    children.push(...assessmentColumnsOptions, ...categoryTotalSubsection);
  } else {
    children.push(...(showRootWithoutChildren ? [] : categoryColumnsLeaves));
  }

  const categoryColumns = [];

  if (!_.isEmpty(children) || showRootWithoutChildren) {
    categoryColumns.push({
      id: addPrefixAsTermId(CATEGORY_COLUMNS),
      label: isCategoriesView
        ? t("academicSetup:category")
        : categoryTotalLabel,
      parent: !_.isEmpty(termId) ? termId : null,
      children,
    });
  }

  return categoryColumns;
};

const getConfigureColumnsMypCriteriaColumnOptions = ({ t, termId = "" }) => {
  const criteriaColumns = [];
  const criteriaColumnsParentOptionId = getMypCriteriaHeaderKey({
    termId,
    label: "CRITERIA_COLUMNS",
  });

  const isSubjectCommentsSupportOnGradebookEnabled = ACLStore.can(
    "FeatureFlag:EnableSubjectCommentsSupportInGradebook"
  );

  const children = [
    {
      id: getMypCriteriaHeaderKey({
        termId,
        label: CRITERION_A,
      }),
      label: `${t("common:criterion")} A`,
      parent: criteriaColumnsParentOptionId,
    },
    {
      id: getMypCriteriaHeaderKey({
        termId,
        label: CRITERION_A_COMMENT,
      }),
      label: getSubjectCommentsLabelByType({
        t,
        type: CRITERION_A_COMMENT,
      }),
      parent: criteriaColumnsParentOptionId,
      isHidden: !isSubjectCommentsSupportOnGradebookEnabled,
    },
    {
      id: getMypCriteriaHeaderKey({
        termId,
        label: CRITERION_B,
      }),
      label: `${t("common:criterion")} B`,
      parent: criteriaColumnsParentOptionId,
    },
    {
      id: getMypCriteriaHeaderKey({
        termId,
        label: CRITERION_B_COMMENT,
      }),
      label: getSubjectCommentsLabelByType({
        t,
        type: CRITERION_B_COMMENT,
      }),
      parent: criteriaColumnsParentOptionId,
      isHidden: !isSubjectCommentsSupportOnGradebookEnabled,
    },
    {
      id: getMypCriteriaHeaderKey({
        termId,
        label: CRITERION_C,
      }),
      label: `${t("common:criterion")} C`,
      parent: criteriaColumnsParentOptionId,
    },
    {
      id: getMypCriteriaHeaderKey({
        termId,
        label: CRITERION_C_COMMENT,
      }),
      label: getSubjectCommentsLabelByType({
        t,
        type: CRITERION_C_COMMENT,
      }),
      parent: criteriaColumnsParentOptionId,
      isHidden: !isSubjectCommentsSupportOnGradebookEnabled,
    },
    {
      id: getMypCriteriaHeaderKey({
        termId,
        label: CRITERION_D,
      }),
      label: `${t("common:criterion")} D`,
      parent: criteriaColumnsParentOptionId,
    },
    {
      id: getMypCriteriaHeaderKey({
        termId,
        label: CRITERION_D_COMMENT,
      }),
      label: getSubjectCommentsLabelByType({
        t,
        type: CRITERION_D_COMMENT,
      }),
      parent: criteriaColumnsParentOptionId,
      isHidden: !isSubjectCommentsSupportOnGradebookEnabled,
    },
    {
      id: getMypCriteriaHeaderKey({
        termId,
        label: CRITERIA_TOTAL,
      }),
      label: t("progressSummary:myp_score"),
      parent: "CRITERIA_COLUMNS",
    },
  ];

  const filteredChildren = _.filter(
    children,
    ({ isHidden = false }) => !isHidden
  );

  criteriaColumns.push({
    id: criteriaColumnsParentOptionId,
    label: t("progressSummary:myp_criteria_grading"),
    parent: termId ? termId : TERM_TOTAL_COLUMNS,
    children: filteredChildren,
  });

  return criteriaColumns;
};

const getConfigureColumnsTermColumnOptions = ({
  t,
  scoreText,
  ibGradeText,
  isCurriculumUBD,
  isOverallGradeEnabled,
  isOverallScoreEnabled,
  isCurriculumDP,
  isCurriculumMYP,
  isLocalGradeCalculationEnabled,
  isAllSubjectsSelected,
  criteriaLabels,
  hiddenGradebookEntities,
  isForColumnConfigurations,
  isUsingNewGradingSetupInCourses,
}) => {
  if (isAllSubjectsSelected) {
    return;
  }

  const termTotalColumns = [];
  const children = [];

  if (isOverallScoreEnabled) {
    children.push({
      id: FINAL_SCORE,
      label: _.get(criteriaLabels, FINAL_SCORE, scoreText),
      parent: TERM_TOTAL_COLUMNS,
    });
  }

  if (isCurriculumMYP) {
    const criteriaColumns = getConfigureColumnsMypCriteriaColumnOptions({ t });
    children.push(...criteriaColumns);
  }

  if (isOverallGradeEnabled && (isCurriculumDP || isCurriculumMYP)) {
    children.push({
      id: FINAL_GRADE,
      label: _.get(criteriaLabels, FINAL_GRADE, ibGradeText),
      parent: TERM_TOTAL_COLUMNS,
    });
  }

  const showLocalGradeCondition = getShowLocalGradeCondition({
    isCurriculumUBD,
    isLocalGradeCalculationEnabled,
    isOverallGradeEnabled,
    isUsingNewGradingSetupInCourses,
  });

  if (showLocalGradeCondition) {
    children.push({
      id: LOCAL_GRADE,
      label: _.get(
        criteriaLabels,
        LOCAL_GRADE,
        isCurriculumUBD ? t("common:grade") : t("common:local_grade_text")
      ),
      parent: TERM_TOTAL_COLUMNS,
    });
  }

  if (ACLStore.can("FeatureFlag:EnableSubjectCommentsSupportInGradebook")) {
    children.push({
      id: SUBJECT_DESCRIPTION,
      label: t("classRoom:subject_description"),
      parent: TERM_TOTAL_COLUMNS,
    });

    children.push({
      id: FINAL_COMMENT,
      label: t("classRoom:final_comment"),
      parent: TERM_TOTAL_COLUMNS,
    });
  }

  const showRootWithoutChildren = isUsingNewGradingSetupInCourses;

  const filteredChildren = _.filter(
    children,
    ({ id }) => !_.get(hiddenGradebookEntities, id)
  );

  if (!_.isEmpty(filteredChildren) || showRootWithoutChildren) {
    termTotalColumns.push({
      id: TERM_TOTAL_COLUMNS,
      label: t("common:term_total"),
      parent: null,
      children: showRootWithoutChildren ? [] : filteredChildren,
    });
  }

  return termTotalColumns;
};

export const getAssessmentGradebookSettingsConfigureColumnOptions = ({
  t,
  isCategoriesView,
  isCurriculumDP,
  isCurriculumMYP,
  isCumulativePeriod,
  selectedGradingPeriod,
  gradingPeriods,
  isCurriculumUBD,
  isOverallScoreEnabled,
  isOverallGradeEnabled,
  isLocalGradeCalculationEnabled,
  isAllSubjectsSelected,
  finalGradeCalculationMethod,
  canOverrideFinalGrade,
  criteriaLabels,
  isAssessmentGradebookHeaderEnhancements,
  hiddenGradebookEntities,
  isForColumnConfigurations,
  isUsingNewGradingSetupInCourses,
}) => {
  const scoreText = t("common:score");
  const ibGradeText = t("common:ib_grade");
  if (isCumulativePeriod) {
    return getCumulativePeriodConfigureColumnOptions({
      gradingPeriods,
      selectedGradingPeriod,
      t,
      isCurriculumUBD,
      isCurriculumDP,
      isCurriculumMYP,
      isOverallScoreEnabled,
      isOverallGradeEnabled,
      scoreText,
      ibGradeText,
      isLocalGradeCalculationEnabled,
      criteriaLabels,
      isAssessmentGradebookHeaderEnhancements,
      isAllSubjectsSelected,
      finalGradeCalculationMethod,
      canOverrideFinalGrade,
      hiddenGradebookEntities,
      isForColumnConfigurations,
      isUsingNewGradingSetupInCourses,
    });
  } else {
    const assessmentColumnsOptions = getAssessmentColumnsOptions({
      t,
      isCategoriesView,
      isCurriculumMYP,
      isCurriculumDP,
      isForColumnConfigurations,
    });

    const categoryColumns = getConfigureColumnsCategoryColumnOptions({
      t,
      isCurriculumDP,
      isCurriculumMYP,
      isCurriculumUBD,
      isCategoriesView,
      assessmentColumnsOptions,
      isOverallScoreEnabled,
      isOverallGradeEnabled,
      scoreText,
      ibGradeText,
      isAllSubjectsSelected,
      finalGradeCalculationMethod,
      canOverrideFinalGrade,
      criteriaLabels,
      hiddenGradebookEntities,
      isForColumnConfigurations,
      isUsingNewGradingSetupInCourses,
    });

    const termColumns = getConfigureColumnsTermColumnOptions({
      t,
      scoreText,
      ibGradeText,
      isCurriculumUBD,
      isOverallGradeEnabled,
      isOverallScoreEnabled,
      isCurriculumDP,
      isCurriculumMYP,
      isLocalGradeCalculationEnabled,
      isAllSubjectsSelected,
      criteriaLabels,
      hiddenGradebookEntities,
      isForColumnConfigurations,
      isUsingNewGradingSetupInCourses,
    });

    const optionsArray = [];

    if (!_.isEmpty(categoryColumns)) {
      optionsArray.push(categoryColumns);
    }

    if (!_.isEmpty(termColumns)) {
      optionsArray.push(termColumns);
    }

    if (!isCategoriesView && !_.isEmpty(assessmentColumnsOptions)) {
      optionsArray.unshift(assessmentColumnsOptions);
    }

    return optionsArray;
  }
};

const getAllVisibleOptions = ({ optionsList }) => {
  const visibleOptions = [];
  const getVisibleOptions = options => {
    _.forEach(options, option => {
      const optionId = _.get(option, "id", "");
      if (!_.isEmpty(optionId)) {
        visibleOptions.push(optionId);
      }
      const optionChildren = _.get(option, "children", []);
      if (!_.isEmpty(optionChildren)) {
        getVisibleOptions(optionChildren);
      }
    });
  };
  const flattenedOptionsList = _.flatten(optionsList);
  getVisibleOptions(flattenedOptionsList);
  return visibleOptions;
};

export const getAssessmentGradebookSettingsDragAndDropListItems = ({
  NestedChecklist,
  nestedChecklistProps,
  selectedViewBy,
  t,
  isCurriculumDP,
  isCumulativePeriod,
  isCurriculumMYP,
  selectedGradingPeriod,
  gradingPeriods,
  isCurriculumUBD,
  isOverallScoreEnabled,
  isOverallGradeEnabled,
  isLocalGradeCalculationEnabled,
  isAllSubjectsSelected,
  finalGradeCalculationMethod,
  canOverrideFinalGrade,
  criteriaLabels,
  isGroupByCategoryView = false,
  hiddenGradebookEntities,
  isUsingNewGradingSetupInCourses,
}) => {
  const isAssessmentGradebookHeaderEnhancements = getIsAssessmentGradebookHeaderEnhancements();

  const isCategoriesView = isAssessmentGradebookHeaderEnhancements
    ? isGroupByCategoryView
    : selectedViewBy === "CATEGORIES";

  const optionsList = getAssessmentGradebookSettingsConfigureColumnOptions({
    t,
    isCategoriesView,
    isCurriculumDP,
    isCurriculumMYP,
    isCumulativePeriod,
    selectedGradingPeriod,
    gradingPeriods,
    isCurriculumUBD,
    isOverallScoreEnabled,
    isOverallGradeEnabled,
    isLocalGradeCalculationEnabled,
    isAllSubjectsSelected,
    finalGradeCalculationMethod,
    canOverrideFinalGrade,
    criteriaLabels,
    hiddenGradebookEntities,
    isAssessmentGradebookHeaderEnhancements,
    isForColumnConfigurations: true,
    isUsingNewGradingSetupInCourses,
  });

  const dragAndDropListItems = _.map(optionsList, options => {
    return {
      key: _.get(options, "0.id", ""),
      value: (
        <NestedChecklist
          {...nestedChecklistProps}
          optionsToRender={options}
          allOptions={options}
        />
      ),
    };
  });

  const allVisibleOptions = getAllVisibleOptions({ optionsList });

  return {
    dragAndDropListItems,
    allVisibleOptions,
  };
};

export const getAssessmentGradebookSettingsCheckboxState = _.memoize(
  ({ columnConfiguration, option }) => {
    const optionChildren = _.get(option, "children", []);
    const optionHasChildren = !_.isEmpty(optionChildren);

    const optionLeafChildren = [];
    _.forEach(optionChildren, child => {
      if (!_.isEmpty(child?.children)) {
        optionLeafChildren.push(...child.children);
      } else {
        optionLeafChildren.push(child);
      }
    });

    const isCheckboxChecked = optionHasChildren
      ? _.every(optionLeafChildren, child => {
          const isChildChecked = _.get(columnConfiguration, [child.id], true);
          return isChildChecked === true;
        })
      : _.get(columnConfiguration, [option.id], true);

    const isCheckboxIndeterminate = optionHasChildren
      ? _.some(optionLeafChildren, child =>
          _.get(columnConfiguration, [child.id], true)
        ) &&
        _.some(
          optionLeafChildren,
          child => !_.get(columnConfiguration, [child.id], true)
        )
      : false;

    return { isCheckboxChecked, isCheckboxIndeterminate };
  },
  params => JSON.stringify(params)
);

export const getAssociatedTermsData = _.memoize(({ term }) => {
  const associatedAcademicTermEntities = _.get(
    term,
    "associatedAcademicTermEntities"
  );
  if (!_.isEmpty(associatedAcademicTermEntities)) {
    return _.orderBy(
      _.map(associatedAcademicTermEntities, item => ({
        termId: _.get(item, "academicTerm.id"),
        termWeightage: _.get(
          item,
          "cumulativeAcademicTermWeightages.0.weightage"
        ),
        termPercentage: _.get(
          item,
          "cumulativeAcademicTermWeightages.0.percentage"
        ),
        termLabel: _.get(item, "academicTerm.label"),
        startDate: _.get(item, "academicTerm.startDate"),
        termType: _.get(item, "academicTerm.type"),
      })),
      "startDate",
      "asc"
    );
  } else {
    return [];
  }
}, JSON.stringify);

export const getAssociatedTermsDataV2 = ({ term, cumulativeTermWeights }) => {
  const associatedAcademicTermEntities = _.get(
    term,
    "associatedAcademicTermEntities"
  );

  const selectedCumulativeTermWeights =
    _.find(
      cumulativeTermWeights,
      weight => weight?.cumulativeTermId === term?.id
    ) || {};

  const associatedTermWeights = _.get(
    selectedCumulativeTermWeights,
    "associatedTermWeights",
    []
  );

  const totalWeights = _.reduce(
    associatedTermWeights,
    (sum, item) => {
      const weight = _.get(item, "weight") || 0;

      return sum + parseFloat(weight);
    },
    0
  );

  const associatedTermWeightsMap = _.keyBy(associatedTermWeights, "termId");

  if (!_.isEmpty(associatedAcademicTermEntities)) {
    return _.orderBy(
      _.map(associatedAcademicTermEntities, item => {
        const associatedTermWeight = _.get(associatedTermWeightsMap, [
          _.get(item, "academicTerm.id"),
          "weight",
        ]);

        const percentageValue = associatedTermWeight
          ? _.round((associatedTermWeight / totalWeights) * 100, 2)
          : 0;

        return {
          termId: _.get(item, "academicTerm.id"),
          termLabel: _.get(item, "academicTerm.label"),
          startDate: _.get(item, "academicTerm.startDate"),
          termType: _.get(item, "academicTerm.type"),
          termWeightage: associatedTermWeight,
          termPercentage: percentageValue,
        };
      }),
      "startDate",
      "asc"
    );
  } else {
    return [];
  }
};

export const getFilteredGradeBoundary = _.memoize(
  ({ organizationPointSet, termId }) => {
    return _.filter(
      organizationPointSet,
      ({ node }) =>
        _.get(
          _.find(
            node?.associatedEntities,
            entity => entity?.__typename === "AcademicTerm"
          ),
          "id"
        ) === termId
    );
  }
);

export const getAssessmentToolTypesForGradebookDownload = _.memoize(
  values => {
    const IBGradeValues = [
      "ASSESSMENT_GRADE_BOUNDARY",
      "ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY",
      "ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY",
      "ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY",
    ];

    return _.filter(
      _.flatten(
        _.map(values, value => (value === "IB_GRADE" ? IBGradeValues : value))
      ),
      val => val !== "ALL"
    );
  },
  params => JSON.stringify(params)
);

export const getGradebookTableNewColumnDisplayOrder = _.memoize(
  ({ displayOrder, sourceIdx, destinationIdx, draggableId }) => {
    const newDisplayOrder = [...displayOrder];
    if (sourceIdx < destinationIdx) {
      for (let i = sourceIdx + 1; i <= destinationIdx; i++) {
        newDisplayOrder[i - 1] = newDisplayOrder[i];
      }
    } else {
      for (let i = sourceIdx - 1; i >= destinationIdx; i--) {
        newDisplayOrder[i + 1] = newDisplayOrder[i];
      }
    }
    newDisplayOrder[destinationIdx] = draggableId;
    return newDisplayOrder;
  },
  params => JSON.stringify(params)
);

export const getGradebookTableColumnConfigurationOptionParentObject = ({
  options,
  parentId,
}) => {
  let result = null;
  _.forEach(options, option => {
    if (option?.id === parentId) {
      result = option;
      return false;
    }
    const children = option?.children;
    if (!_.isEmpty(children)) {
      const parentObj = getGradebookTableColumnConfigurationOptionParentObject({
        options: children,
        parentId,
      });
      if (!_.isEmpty(parentObj)) {
        result = parentObj;
        return false;
      }
    }
  });
  return result;
};

export const getSelectedAssessmentToolTypeValue = ({
  selectedAssessmentToolType,
  isChecked,
  prvAssessmentToolTypes,
  allAssessmentToolTypes,
}) => {
  if (isChecked) {
    let selectedValues;
    if (selectedAssessmentToolType === "ALL") {
      selectedValues = [];
    } else {
      selectedValues = _.filter(
        prvAssessmentToolTypes,
        v => v !== selectedAssessmentToolType && v !== "ALL"
      );
    }
    return selectedValues;
  } else {
    // select all assessment tools if ALL option is selected or
    // all tools were selected except ALL option
    const filteredPrvAssessmentToolTypes = _.filter(
      prvAssessmentToolTypes,
      value => value !== "ALL"
    );

    if (
      selectedAssessmentToolType === "ALL" ||
      _.size(filteredPrvAssessmentToolTypes) ===
        _.size(allAssessmentToolTypes) - 2
    ) {
      return _.map(allAssessmentToolTypes, "value");
    } else {
      return [...prvAssessmentToolTypes, selectedAssessmentToolType];
    }
  }
};

export const getGradingPeriods = ({ academicYears, showCumulativeTerms }) => {
  const skipCumulativePeriod =
    !ACLStore.can("FeatureFlag:MYPCumulativeReports") || !showCumulativeTerms;

  return _.reduce(
    academicYears,
    (gradingPeriodList, academicYear) => {
      const { terms = [], startDate, endDate } = academicYear || {};

      const year = getYearDurationFormat({ startDate, endDate });

      _.forEach(terms, term => {
        const skipGradingPeriod =
          _.get(term, "type") == "CUMULATIVE" && skipCumulativePeriod;
        if (!skipGradingPeriod) {
          gradingPeriodList.push({
            ...term,
            year,
          });
        }
      });
      return gradingPeriodList;
    },
    []
  );
};

export const getGradebookSubjectSettingsFilteredCategoryValues = _.memoize(
  ({ category, subjectLevelGenericTags }) => {
    if (!_.isEmpty(subjectLevelGenericTags)) {
      return _.filter(
        _.orderBy(category?.values, ["genericTag.label"], ["desc"]),
        value =>
          _.includes(
            _.map(subjectLevelGenericTags, "id"),
            _.get(value, "genericTag.id")
          )
      );
    } else {
      return _.get(category, "values", []);
    }
  },
  params => JSON.stringify(params)
);

export const getAssessmentsGradebookCumulativeTermHeaderTextLabels = _.memoize(
  ({ associatedTermsData }) => {
    const lastAssociatedTermLabel = _.get(
      _.last(associatedTermsData),
      "termLabel"
    );

    const commaSeparatedAssociatedTermsExceptLast = _.map(
      associatedTermsData,
      "termLabel"
    )
      ?.slice(0, -1)
      ?.join(", ");

    return { lastAssociatedTermLabel, commaSeparatedAssociatedTermsExceptLast };
  },
  params => JSON.stringify(params)
);

export const getAssessmentGradebookSettingsModalInitialSettings = _.memoize(
  ({
    t,
    assessmentGradebookSettings,
    isCumulativePeriod,
    selectedGradingPeriod,
    gradingPeriods,
    assessmentToolOptions,
    selectedAssessmentTools,
    isHideTasksExcludedFromTermTotal,
    isHideTasksFromOtherClasses,
    showSameSubjectGradeTasksInGradebook,
    organizationId,
    curriculumProgramId,
    isCurriculumUBD,
  }) => {
    if (isCumulativePeriod) {
      const configurationFromSettings = _.get(
        assessmentGradebookSettings,
        ["cumulativePeriodColumnConfig", selectedGradingPeriod],
        {}
      );

      if (!_.isEmpty(configurationFromSettings)) {
        return configurationFromSettings;
      } else {
        const selectedGradingPeriodObj = _.find(gradingPeriods, {
          id: selectedGradingPeriod,
        });
        const terms = getTerms({ selectedGradingPeriodObj, gradingPeriods });
        const cumulativePeriodDefaultColumnDisplayOrder = _.map(terms, "id");
        const defaultColumnConfiguration = _.reduce(
          terms,
          (result, term) => {
            const { id: termId } = term;
            result = {
              ...result,
              [termId]: true,
            };
            return result;
          },
          {}
        );
        const cumulativePeriodDefaultColumnConfig = {
          columnDisplayOrder: cumulativePeriodDefaultColumnDisplayOrder,
          columnConfiguration: defaultColumnConfiguration,
        };

        return cumulativePeriodDefaultColumnConfig;
      }
    } else {
      const assessmentToolOptionsKeys = _.map(assessmentToolOptions, "value");

      const toolsConfiguration = _.reduce(
        assessmentToolOptionsKeys,
        (config, tool) => {
          const toolKey = _.includes(IB_GRADE_TOOLS, tool) ? "IB_GRADE" : tool;

          config[toolKey] = _.includes(selectedAssessmentTools, tool);

          return config;
        },
        {}
      );

      const displaySettingOptions = getDisplaySettingOptions({
        t,
        isHideTasksExcludedFromTermTotal,
        isHideTasksFromOtherClasses,
        showSameSubjectGradeTasksInGradebook,
        organizationId,
        curriculumProgramId,
        isCurriculumUBD,
      });

      return {
        ...assessmentGradebookSettings,
        displaySettingOptions,
        columnConfiguration: {
          ...assessmentGradebookSettings.columnConfiguration,
          ...toolsConfiguration,
        },
      };
    }
  },

  params => JSON.stringify(params)
);

const getAssessmentSettingsConfigurationDefaultValue = ({ configuration }) => {
  switch (configuration) {
    case USE_WEIGHTAGE_FOR_CATEGORY_SETTING:
    case OVERALL_SCORE_FOR_GRADING_SETUP_SETTING:
      return true;
    case FINAL_GRADE_CALCULATION_SETTING:
      return SCORE;
  }
};

export const getIsAssessmentSettingsConfigurationEnabledByName = ({
  curriculumProgramId,
  settingName,
  gradeId,
}) => {
  const isNewAssessmentSettingsEnabled = getIsNewAssessmentSettingsEnabled();

  if (!isNewAssessmentSettingsEnabled) {
    const defaultValue = getAssessmentSettingsConfigurationDefaultValue({
      configuration: settingName,
    });
    return defaultValue;
  }

  const value = getSettingValue({
    name: settingName,
    currentCurriculumProgramId: curriculumProgramId,
    gradeId,
  });

  return value;
};

export const getCategoriesWithPercentageValues = ({
  categories: _categories,
  subjectLevelGenericTags,
  categoryValuePath = "values",
}) => {
  const categories = _.cloneDeep(_categories);

  const genericTagIds = _.map(subjectLevelGenericTags, "id");
  const categoryValues = _.flatten(_.map(categories, categoryValuePath));
  const categoryTotalsObj = {};
  const isGenericTagsEmpty = _.isEmpty(subjectLevelGenericTags);

  if (isGenericTagsEmpty) {
    categoryTotalsObj["total"] = _.sumBy(categoryValues, "value");
  } else {
    _.forEach(categoryValues, categoryValue => {
      const genericTagId = _.get(categoryValue, "genericTag.id");
      if (_.includes(genericTagIds, genericTagId)) {
        categoryTotalsObj[genericTagId] =
          _.get(categoryTotalsObj, genericTagId, 0) +
          _.get(categoryValue, "value", 0);
      }
    });
  }

  _.forEach(categories, category => {
    const categoryValues = _.get(category, categoryValuePath, []);
    _.forEach(categoryValues, categoryValue => {
      const categoryTotalkey = isGenericTagsEmpty
        ? "total"
        : _.get(categoryValue, "genericTag.id", "total");
      const categoryTotal = _.get(categoryTotalsObj, categoryTotalkey, 0);
      const percentage = categoryTotal
        ? _.round((_.get(categoryValue, "value", 0) / categoryTotal) * 100, 2)
        : 0;
      categoryValue["percentageValue"] = percentage;
    });
  });

  return categories;
};

export const getAssessmentGradebookSettingsToUpdateUserPreferences = _.memoize(
  ({
    assessmentGradebookSettings,
    settings,
    isCumulativePeriod,
    selectedGradingPeriod,
  }) => {
    if (isCumulativePeriod) {
      return {
        ...assessmentGradebookSettings,
        cumulativePeriodColumnConfig: {
          ...assessmentGradebookSettings.cumulativePeriodColumnConfig,
          [selectedGradingPeriod]: settings,
        },
      };
    } else {
      return settings;
    }
  },
  params => JSON.stringify(params)
);

export const getGradebookSubjectSettingsOrderedCategories = _.memoize(
  ({
    isCategoryDisplayTypePercentage,
    templateNode,
    subjectLevelGenericTags,
    isNewAssessmentSettingsEnabled,
  }) => {
    let categories = _.orderBy(
      _.get(templateNode, "categories"),
      ["displaySequence"],
      ["asc"]
    );

    if (isCategoryDisplayTypePercentage && isNewAssessmentSettingsEnabled) {
      categories = getCategoriesWithPercentageValues({
        categories,
        subjectLevelGenericTags,
      });
    }

    return categories;
  },
  params => JSON.stringify(params)
);

export const getIBPYPElementType = ({
  curriculumType,
  isCourseBasedGrading,
}) => {
  if (isCourseBasedGrading) {
    return "LEARNING_COURSE";
  } else {
    switch (curriculumType) {
      case CURRICULUM_TYPE_DP:
        return ELEMENT_TYPE_DP_SUBJECT;
      case CURRICULUM_TYPE_MYP:
        return ELEMENT_TYPE_MYP_SUBJECT;
      case CURRICULUM_TYPE_PYP:
        return ELEMENT_TYPE_PYP_SUBJECT;
      case CURRICULUM_TYPE_UBD:
        return ELEMENT_TYPE_UBD_SUBJECT;
      default:
        return ELEMENT_TYPE_UBD_SUBJECT;
    }
  }
};

export const getTemplateLastUpdatedByText = ({ templateNode, t }) => {
  const user =
    _.get(templateNode, "updatedBy") || _.get(templateNode, "createdBy");

  const updatedTimeStamp = moment(_.get(templateNode, "updatedAt", ""));

  const updatedDate = updatedTimeStamp.format("MMMM DD, YYYY");
  const updatedTime = updatedTimeStamp.format("h:mm a");

  const name = getFormattedUserName({ user: user, type: STAFF });

  return `(${t("progressSummary:last_updated_by_name_on_date_at_time", {
    name,
    date: updatedDate,
    time: updatedTime,
  })})`;
};

export const getIbDefaultGradeScaleDummyCriteriaSet = () => {
  const IB_GRADE_SCALE = ["1", "2", "3", "4", "5", "6", "7"];
  return _.map(IB_GRADE_SCALE, item => ({ id: item, label: item }));
};

export const getLocalGradeRating = ({
  termId,
  studentWiseRatings,
  isComputedRating = false,
}) => {
  return _.find(studentWiseRatings, rating => {
    let condition =
      _.get(rating, "academicCriteriaSet.criteriaType") === "LOCAL_GRADE" &&
      _.isNil(_.get(rating, "category")) &&
      _.get(rating, "isComputedRating") === isComputedRating;

    if (!_.isEmpty(termId)) {
      condition =
        condition &&
        (_.get(rating, "academicTerm.id") === termId ||
          _.get(rating, "parent.id") === termId);
    }

    return condition;
  });
};

export const getAssessmentToolTypeOptions = ({ t, curriculumType }) => {
  const isCurriculumDP = _.isEqual(curriculumType, CURRICULUM_TYPE_DP);
  const isCurriculumMYP = _.isEqual(curriculumType, CURRICULUM_TYPE_MYP);

  const tools = [{ label: t("common:score"), key: "score", value: SCORE }];

  if (getIsScoreBasedRubricEnabled()) {
    tools.push({
      key: SCORE_BASED_RUBRIC,
      value: SCORE_BASED_RUBRIC,
      label: t("common:score_based_rubric"),
    });
  }

  if (getIsNewGradingSetupEnabled()) {
    tools.push({
      key: STANDARD_BASED_RUBRIC,
      value: STANDARD_BASED_RUBRIC,
      label: t("common:standard_based_rubric"),
    });
  }

  if (getIsAssessmentGradeScaleToolEnabled()) {
    tools.push({
      key: GRADE_SCALE_TOOL,
      value: GRADE_SCALE_TOOL,
      label: t("common:grade_scale"),
    });
  }

  if (isCurriculumDP) {
    tools.push(
      {
        label: t("common:ib_grade"),
        key: ASSESSMENT_GRADE_BOUNDARY,
        value: ASSESSMENT_GRADE_BOUNDARY,
      },
      {
        label: t("common:dp_assessment_criteria"),
        key: "DP_ASSESSMENT_RUBRIC",
        value: "DP_ASSESSMENT_RUBRIC",
      }
    );
  }

  if (isCurriculumMYP) {
    tools.push(
      {
        key: "MYP_OBJECTIVE_RUBRIC",
        value: "MYP_OBJECTIVE_RUBRIC",
        label: t("common:myp_criteria_rubric"),
      },
      {
        key: "MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC",
        value: "MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC",
        label: t("common:interdisciplinary_criteria_rubric"),
      }
    );
  }

  tools.push(
    {
      label: t("common:final_remarks"),
      key: "COMMENT",
      value: "COMMENT",
    },
    {
      label: t("common:rubric"),
      key: "RUBRIC",
      value: "RUBRIC",
    },
    { label: t("common:checklist"), key: "CHECKLIST", value: "CHECKLIST" },
    {
      label: t("common:single_point_rubric"),
      key: "SINGLE_POINT_RUBRIC",
      value: "SINGLE_POINT_RUBRIC",
    }
  );

  tools.push({
    label: t("common:learning_goals"),
    key: "LEARNING_GOALS",
    value: "LEARNING_GOALS",
  });

  return tools;
};

export const getNoGradeCalculationMethodSelectedEmptyStateProps = ({
  finalGradeCalculationMethod,
  t,
  goToRelativeRoute,
  curriculumProgramId,
  curriculumType,
  getCurriculumBasedRoute,
  isGradingPeriodUnlocked = true,
}) => {
  const noFinalGradeCalculationMethodSelected = _.isEmpty(
    finalGradeCalculationMethod
  );

  const isAdmin = getIsAdminByRole();

  let title, subtitle, illustration, primaryButton;

  if (isAdmin) {
    title = noFinalGradeCalculationMethodSelected
      ? t("academicSetup:setup_overall_grade_calculation_method")
      : t("academicSetup:set_a_calculation_method");

    subtitle = noFinalGradeCalculationMethodSelected
      ? t(
          "academicSetup:no_overall_grade_calculation_method_selected_empty_state_subtitle_admin"
        )
      : t(
          "academicSetup:no_grade_calculation_method_selected_empty_state_subtitle_admin"
        );

    const buttonLabel = noFinalGradeCalculationMethodSelected
      ? t("academicSetup:go_to_overall_grade_setup")
      : t("academicSetup:go_to_assessment_setup");

    const routeFragment = noFinalGradeCalculationMethodSelected
      ? "overall-setup"
      : "assessment-setup";

    const buttonAction = () =>
      goToRelativeRoute({
        route: `/platform/administrator/${curriculumProgramId}/${getCurriculumBasedRoute(
          { curriculumType }
        )}/grading-setup/${routeFragment}`,
      });

    primaryButton = (
      <ConditionalWrapper
        condition={!isGradingPeriodUnlocked}
        wrapper={
          <Tooltip
            tooltip={t("academicSetup:you_cannot_with_label_since_locked", {
              label: buttonLabel,
            })}
            overlayInnerStyle={styles.lockingGradeTooltipOverlayInner}
          />
        }
        type={"wrapperAsElement"}
      >
        <Button
          variant="outlined"
          onClick={buttonAction}
          disabled={!isGradingPeriodUnlocked}
          dsVersion="2.0"
          testId={`Gradebook-gradingSetup-rerouteButton-${buttonLabel}`}
        >
          {buttonLabel}
        </Button>
      </ConditionalWrapper>
    );

    illustration = NoAssignmentIllustration;
  } else {
    title = noFinalGradeCalculationMethodSelected
      ? t("academicSetup:no_overall_grade_calculation_method_setup_yet")
      : t("academicSetup:no_calculation_method_defined_yet");

    subtitle = noFinalGradeCalculationMethodSelected
      ? t(
          "academicSetup:no_overall_grade_calculation_method_selected_empty_state_subtitle_teacher"
        )
      : t(
          "academicSetup:no_grade_calculation_method_selected_empty_state_subtitle_teacher"
        );

    illustration = NoChecklistIllustration;
    primaryButton = null;
  }

  return {
    title,
    subtitle,
    primaryButton,
    illustration,
  };
};

export const getOrgPointSetVariablesAssociatedEntities = _.memoize(
  ({
    isCourseApplicableToCategorySet,
    isCumulativePeriod,
    associatedTerms,
    selectedGradingPeriod,
    courseId,
    subjectIds,
    gradeId,
  }) => {
    const associatedEntities = [
      getAssociatedEntitiesVariables({
        isCourseApplicableToCategorySet,
        courseId,
        gradeId,
      }),
    ];

    const orgPointSetVariablesTermIds = !_.isEmpty(selectedGradingPeriod)
      ? isCumulativePeriod
        ? associatedTerms
        : selectedGradingPeriod
      : undefined;

    if (orgPointSetVariablesTermIds) {
      associatedEntities.push({
        type: "ACADEMIC_TERM",
        ids: orgPointSetVariablesTermIds,
      });
    }

    if (subjectIds) {
      associatedEntities.push({ type: "SUBJECT", ids: subjectIds });
    }

    return associatedEntities;
  },
  JSON.stringify
);

export const getShowTermTotalColumns = ({
  curriculumType,
  isOverallGradeEnabled,
  isOverallScoreEnabled,
  isLocalGradeCalculationEnabled,
}) => {
  switch (curriculumType) {
    case CURRICULUM_TYPE_MYP:
    case CURRICULUM_TYPE_DP: {
      return true;
    }
    case CURRICULUM_TYPE_PYP: {
      return isOverallScoreEnabled || isLocalGradeCalculationEnabled;
    }
    case CURRICULUM_TYPE_UBD: {
      return isOverallScoreEnabled || isOverallGradeEnabled;
    }
  }
};

export const getCategoryOptionsForAssessmentGradebook = _.memoize(
  ({ subjects, categoryOptions, isAllSubjectsSelected, curriculumType }) => {
    const isCurriculumPYP = curriculumType === CURRICULUM_TYPE_PYP;

    let updatedCategoryOptions = [];

    // only group the options by subject if the curriculum is PYP and all subjects are selected
    if (isCurriculumPYP && isAllSubjectsSelected) {
      const categoryOptionsMapBySubjectId = _.groupBy(
        categoryOptions,
        "subjectId"
      );

      _.forEach(subjects, ({ id, name }) => {
        const subjectCategoryOptions = categoryOptionsMapBySubjectId[id];

        if (!_.isEmpty(subjectCategoryOptions)) {
          updatedCategoryOptions.push({
            label: name,
            value: id,
            options: subjectCategoryOptions,
          });
        }
      });

      if (!_.isEmpty(updatedCategoryOptions)) {
        updatedCategoryOptions = [
          {
            isDivider: true,
            value: "divider",
          },
          ...updatedCategoryOptions,
        ];
      }
    } else {
      updatedCategoryOptions = categoryOptions;
    }

    return updatedCategoryOptions;
  },
  params => JSON.stringify(params)
);

export const getCategoryOptionsForAssessmentGradebookV2 = ({
  subjects,
  categoryOptions,
  isAllSubjectsSelected,
  isCurriculumDP,
  t,
  categoryTotalDisplayType,
  isWeightageForCategoryEnabled,
  isTOK,
  isCurriculumPYP,
  isCumulativePeriod,
  isUsingNewGradingSetupInCourses,
  cumulativeTotalCalculationMethod,
  differentCategoriesForGradingPeriods,
}) => {
  // in case of global category, same category can be used for different grading periods
  // hence use _.uniqBy to remove duplicates based on category value which is unique for each category
  const updatedCategoryOptions = _.uniqBy(
    _.map(categoryOptions, (category = {}) => {
      const { scoreValues } = category;

      const orderedScoreValues = _.orderBy(
        scoreValues,
        "genericTag.label",
        "desc"
      );

      const conditionalPercentage = getConditionalPercentageForCategoryHeader({
        categoryTotalDisplayType,
      });

      // when categories are different for grading periods, and we're in cumulative period view with FROM_ASSIGNMENTS calculation method
      // the weight for all categories will be respective to their grading periods and will add up to more than 100%
      // and it also will not be used in calculation, hence hide it
      const hideSubtext =
        isUsingNewGradingSetupInCourses &&
        isCumulativePeriod &&
        differentCategoriesForGradingPeriods &&
        cumulativeTotalCalculationMethod === "FROM_ASSIGNMENTS";
      const categorySubText = hideSubtext
        ? null
        : getCategorySubtext({
            t,
            orderedScoreValues,
            isCurriculumDP,
            conditionalPercentage,
            isTOK,
            categoryTotalDisplayType,
            isWeightageForCategoryEnabled,
          });

      return { ...category, subLabel: categorySubText };
    }),
    "value"
  );

  // only group the options by subject if the curriculum is PYP and all subjects are selected
  if (isCurriculumPYP && isAllSubjectsSelected) {
    const categoryOptionsGroupBySubjects = [];

    const categoryOptionsMapBySubjectId = _.groupBy(
      updatedCategoryOptions,
      "subjectId"
    );

    _.forEach(subjects, ({ id, name }) => {
      const subjectCategoryOptions = categoryOptionsMapBySubjectId[id];

      if (!_.isEmpty(subjectCategoryOptions)) {
        categoryOptionsGroupBySubjects.push({
          label: name,
          value: id,
          options: subjectCategoryOptions,
        });
      }
    });

    return categoryOptionsGroupBySubjects;
  } else {
    return updatedCategoryOptions;
  }
};

export const getMYPTermLevelCriteriaValue = ({
  academicCriteriaSet,
  studentWiseRatings,
  termId,
  achievementLevelCriteriaSetId,
  mypIbDefinedAchievementCriteriaSetId,
  valueDataInState,
  isUsingNewGradingSetupInCourses,
}) => {
  if (valueDataInState) {
    return _.get(valueDataInState, "value", "");
  }

  const acsId = !_.isEmpty(academicCriteriaSet)
    ? _.get(academicCriteriaSet, "id")
    : mypIbDefinedAchievementCriteriaSetId;

  const rating = _.find(studentWiseRatings, rating => {
    let condition =
      _.get(rating, "ibPYPElementId") === achievementLevelCriteriaSetId &&
      _.get(rating, "academicCriteriaSet.id") === acsId;

    if (termId) {
      condition = condition && _.get(rating, "academicTerm.id") === termId;
    }

    return condition;
  });

  if (isUsingNewGradingSetupInCourses) {
    return _.get(rating, "academicCriteriaValue.abbreviation");
  }

  return _.get(rating, "academicCriteriaValue.label");
};

export const getAllAssignmentsForGradingHistory = ({
  ratings,
  achievementLevelCriteriaSetId,
  isMYPCriteriaRatingHistory,
}) => {
  const filteredRatings = isMYPCriteriaRatingHistory
    ? _.filter(ratings, {
        ibPYPElementId: achievementLevelCriteriaSetId,
      })
    : _.filter(ratings, rating => !_.isEmpty(_.get(rating, "value")));

  const assignments = _.map(filteredRatings, rating => {
    const assignment = _.get(rating, "parent.assignment", {});
    const criteriaValue = _.get(rating, "academicCriteriaValue.label", "");
    const publishedAt = _.get(rating, "publishedAt");
    const value = _.get(rating, "value");

    const { id } = assignment;

    return {
      id,
      category: _.get(assignment, "content.category.label", "-"),
      assessmentType: _.get(assignment, "content.assessmentType.value", "le"),
      assessmentTitle: _.get(assignment, "content.title.value", ""),
      value: isMYPCriteriaRatingHistory ? criteriaValue : value,
      publishedAt,
    };
  });

  return _.orderBy(assignments, ["publishedAt"], ["asc"]);
};

export const getMypIbDefinedCriteriaSet = ({
  academicCriteriaSets,
  curriculumProgramId,
}) => {
  return _.find(academicCriteriaSets, set => {
    const criteriaType = _.get(set, "criteriaType", "");
    const curriculumProgramIdOfSet = _.get(set, "curriculumProgram.id", "");

    return (
      criteriaType === "IB_DEFINED" &&
      curriculumProgramIdOfSet === curriculumProgramId
    );
  });
};

export const getYearLevelCriteriaSetForStudent = ({
  termGradeStudentWiseCriteria,
  studentId,
  termId,
}) => {
  const objectiveSetForCurrentStudent = _.find(
    termGradeStudentWiseCriteria,
    set => {
      let condition = _.get(set, "student.id") === studentId;

      if (termId) {
        condition = condition && _.get(set, "academicTerm.id") === termId;
      }

      return condition;
    }
  );

  return _.get(objectiveSetForCurrentStudent, "criteria", {});
};

export const getAchievementLevelCriteriaSet = ({
  criteriaNodes,
  yearLevelCriteriaId,
  criteriaLabel,
}) => {
  const achievementLevelCriteriaSet = _.find(
    criteriaNodes,
    ({ parent, label }) => {
      return parent === yearLevelCriteriaId && label === criteriaLabel;
    }
  );

  return achievementLevelCriteriaSet || {};
};

export const getMypCriteriaTotal = ({
  termId,
  studentId,
  updatedValues,
  criteriaNodes,
  studentWiseRatings,
  termGradeStudentWiseCriteria,
  mypIbDefinedAchievementCriteriaSetId,
  mypCriteriaTotalCalculationMethod,
}) => {
  const mypCriteria = ["A", "B", "C", "D"];
  const { id: yearLevelCriteriaId } = getYearLevelCriteriaSetForStudent({
    termGradeStudentWiseCriteria,
    studentId,
    termId,
  });

  const criteriaValues = _.map(mypCriteria, criterion => {
    const {
      id: achievementLevelCriteriaSetId,
    } = getAchievementLevelCriteriaSet({
      criteriaNodes,
      yearLevelCriteriaId,
      criteriaLabel: criterion,
    });

    const valueDataInState = _.get(updatedValues, [
      studentId,
      achievementLevelCriteriaSetId,
    ]);

    const criteriaValue = getMYPTermLevelCriteriaValue({
      studentWiseRatings,
      termId,
      achievementLevelCriteriaSetId,
      mypIbDefinedAchievementCriteriaSetId,
      valueDataInState,
    });

    return criteriaValue;
  });

  if (
    (_.some(criteriaValues, _.isEmpty) &&
      mypCriteriaTotalCalculationMethod ==
        MYP_CRITERIA_TOTAL_TYPE_ALL_CRITERIA) ||
    _.every(criteriaValues, _.isEmpty)
  ) {
    return "-";
  }

  const criteriaTotal = _.chain(criteriaValues)
    .filter(value => _.isNumber(+value) && !_.isNaN(+value) && value)
    .sumBy(value => parseInt(value))
    .value();

  return criteriaTotal;
};

export const getCumulativeScoreCalculationNotSetUpEmptyStateSubtitle = ({
  isAdmin,
  t,
}) => {
  if (isAdmin) {
    return t(
      "progressSummary:please_setup_how_cumulative_scores_will_be_calculated"
    );
  }

  return t(
    "progressSummary:once_school_admin_defines_cumulative_score_calculation_subtitle"
  );
};

const getTermTotalLabelByType = ({
  t,
  type,
  isCurriculumUBD,
  criteriaLabels,
}) => {
  switch (type) {
    case "FINAL_SCORE":
      return _.get(
        criteriaLabels,
        FINAL_SCORE,
        t("academicSetup:overall_score")
      );
    case "FINAL_GRADE":
      return _.get(criteriaLabels, FINAL_GRADE, t("common:overall_grade"));
    case "LOCAL_GRADE":
      return _.get(
        criteriaLabels,
        LOCAL_GRADE,
        isCurriculumUBD
          ? t("common:overall_grade")
          : t("common:local_grade_text")
      );
    default:
      return "";
  }
};

export const getIsMypCriteriaCalculationMethodAllCriteria = ({
  mypCriteriaTotalCalculationMethod,
}) => {
  return (
    mypCriteriaTotalCalculationMethod == MYP_CRITERIA_TOTAL_TYPE_ALL_CRITERIA
  );
};

export const getMYPTermLevelCriteriaTotal = ({
  termId,
  studentWiseRatings,
  academicCriteriaSet,
}) => {
  const acsId = _.get(academicCriteriaSet, "id");

  const rating = _.find(studentWiseRatings, rating => {
    let condition;

    if (!_.isEmpty(academicCriteriaSet)) {
      condition = _.get(rating, "academicCriteriaSet.id") === acsId;
    } else {
      condition =
        _.get(rating, "academicCriteriaSet.criteriaType") ===
        MYP_CRITERIA_FINAL_SCORE;
    }

    if (termId) {
      condition = condition && _.get(rating, "academicTerm.id") === termId;
    }

    return condition;
  });

  return _.get(rating, "value");
};

const getMypCriteriaDataForGradeSummary = ({
  t,
  classes,
  termGradeStudentWiseCriteria,
  criteriaNodes,
  mypIbDefinedAchievementCriteriaSetId,
  studentWiseRatings,
  termId,
  gradeScales,
  gradingMethodologyTypes,
  mypCriteriaTotalCalculationMethod,
  mypIbDefinedGradeScale,
  mypCriteriaTotalGradeScale,
  mypCriteriaRegularGradeScales,
  isUsingNewGradingSetupInCourses,
}) => {
  const mypCriteriaData = [];

  const criteriaLabels = ["A", "B", "C", "D"];
  let nonNullCriteriaValueCount = 0;

  const yearLevelCriteriaId = _.get(
    termGradeStudentWiseCriteria,
    "criteria.id"
  );

  _.forEach(criteriaLabels, label => {
    const {
      id: achievementLevelCriteriaSetId,
      label: achievementLevelCriteriaSetLabel,
    } = getAchievementLevelCriteriaSet({
      criteriaNodes,
      yearLevelCriteriaId,
      criteriaLabel: label,
    });

    const criteriaValue = getMYPTermLevelCriteriaValue({
      studentWiseRatings,
      termId,
      achievementLevelCriteriaSetId,
      mypIbDefinedAchievementCriteriaSetId,
      academicCriteriaSet: isUsingNewGradingSetupInCourses
        ? mypIbDefinedGradeScale
        : undefined,
      isUsingNewGradingSetupInCourses,
    });

    const showMappedGradeScaleValues =
      isUsingNewGradingSetupInCourses &&
      !_.isEmpty(mypCriteriaRegularGradeScales);

    const regularGradeScaleValues = showMappedGradeScaleValues
      ? _.compact(
          _.map(mypCriteriaRegularGradeScales, mappedGradeScale => {
            const { id, displayName } = mappedGradeScale || {};

            const value = getMYPTermLevelCriteriaValue({
              studentWiseRatings,
              termId,
              achievementLevelCriteriaSetId,
              mypIbDefinedAchievementCriteriaSetId,
              academicCriteriaSet: mappedGradeScale,
              isUsingNewGradingSetupInCourses,
            });

            if (!value) {
              return null;
            }

            return {
              id,
              value,
              label: displayName,
            };
          })
        )
      : [];
    const hasRegularGradeScaleValues = !_.isEmpty(regularGradeScaleValues);

    if (criteriaValue || hasRegularGradeScaleValues) {
      nonNullCriteriaValueCount++;

      const tooltip = hasRegularGradeScaleValues ? (
        <div>
          {criteriaValue ? (
            <div>
              {`${_.get(
                mypIbDefinedGradeScale,
                "displayName"
              )} : ${criteriaValue}`}
            </div>
          ) : null}

          {!_.isEmpty(regularGradeScaleValues)
            ? _.map(regularGradeScaleValues, ({ label, value, id }) => {
                return <div key={id}>{`${label} : ${value}`}</div>;
              })
            : null}
        </div>
      ) : null;

      mypCriteriaData.push({
        id: achievementLevelCriteriaSetLabel,
        label: `${t("common:criteria")} ${achievementLevelCriteriaSetLabel}`,
        criteriaValue,
        value: (
          <Tooltip tooltip={tooltip}>
            <div className={classes.valueContainer}>
              <div>
                <span className={classes.valueText}>{criteriaValue}</span>
              </div>

              {hasRegularGradeScaleValues &&
                _.map(regularGradeScaleValues, ({ id, value }) => {
                  return (
                    <React.Fragment key={id}>
                      <div className={classes.dot} />
                      <div>{value}</div>
                    </React.Fragment>
                  );
                })}
            </div>
          </Tooltip>
        ),
      });
    }
  });

  let mypTotalScore = getMYPTermLevelCriteriaTotal({
    studentWiseRatings,
    termId,
    academicCriteriaSet: isUsingNewGradingSetupInCourses
      ? mypCriteriaTotalGradeScale
      : undefined,
  });

  if (
    nonNullCriteriaValueCount > 0 &&
    nonNullCriteriaValueCount !== _.size(criteriaLabels) &&
    getIsMypCriteriaCalculationMethodAllCriteria({
      mypCriteriaTotalCalculationMethod,
    })
  ) {
    mypTotalScore = "-";
  }

  if (mypTotalScore) {
    mypCriteriaData.push({
      id: "MYP_SCORE",
      label: t("studentProgressSummary:criteria_total"),
      value: (
        <div>
          <span className={classes.valueText}>{mypTotalScore}</span>
          {!ACLStore.can("FeatureFlag:RemoveOutOfTotalInMypCriteriaTotal") ||
          nonNullCriteriaValueCount == _.size(criteriaLabels) ? (
            <span className={classes.valueSuffixText}>/32</span>
          ) : null}
        </div>
      ),
    });
  }

  if (isUsingNewGradingSetupInCourses) {
    _.forEach(gradeScales, gradeScale => {
      const isMYPBased = getIsMYPBasedGradeScale({
        gradeScale,
        gradingMethodologyTypes,
      });

      if (!isMYPBased) {
        return;
      }

      const { displayName } = gradeScale || {};

      const { overallGrade } = getOverallGradeValue({
        studentWiseRatings,
        entityId: termId,
        overallGradeType: GRADE_SCALE,
        academicCriteriaSet: gradeScale,
      });

      if (overallGrade && overallGrade !== "-") {
        mypCriteriaData.push({
          id: MYP_FINAL_GRADE,
          label: displayName,
          value: <div className={classes.valueText}>{overallGrade}</div>,
        });
      }
    });
  }

  return mypCriteriaData;
};

export const getStudentGradeSummaryData = ({
  t,
  isTOK,
  term,
  termId,
  userType,
  curriculumType,
  categoryOptions,
  studentWiseRatings,
  isOverallScoreEnabled,
  subjectLevelGenericTags,
  categoryTotalDisplayType,
  isWeightageForCategoryEnabled,
  classes,
  termGradeStudentWiseCriteria,
  criteriaNodes,
  mypIbDefinedAchievementCriteriaSetId,
  isFinalGradeCalculatedFromMYPCriteria,
  organizationId,
  curriculumProgramId,
  mypCriteriaTotalCalculationMethod,
  isUsingNewGradingSetupInCourses,
  mypFinalGradeScale,
  mypIbDefinedGradeScale,
  mypCriteriaTotalGradeScale,
  gradeScales,
  visibilitySettings,
  isVisibilitySettingsV2Enabled,
  hiddenGradebookEntities,
  gradingMethodologyTypes,
  mypCriteriaRegularGradeScales,
}) => {
  const isCategoryGradeScalesEnabled = ACLStore.can(
    "FeatureFlag:EnableCategoryGradeScales"
  );
  const studentGradeSummaryData = [];

  const finalGradesVisibilitySettings = _.get(
    visibilitySettings,
    "FINAL_GRADES_SETTINGS"
  );
  const assessmentGradebookVisibilitySettings = _.get(
    visibilitySettings,
    "ASSESSMENT_GRADES_SETTINGS"
  );

  const isCategoryScoreVisible = isUsingNewGradingSetupInCourses
    ? getIsRatingVisible({
        term,
        userType,
        ratingType: SCORE,
        isVisibilitySettingsV2Enabled,
        visibilitySettings: assessmentGradebookVisibilitySettings,
      })
    : true;

  const criteriaLabels = getCriteriaSetLabels({
    organizationId,
    curriculumProgramId,
    criteriaTypes: [
      FINAL_SCORE,
      FINAL_GRADE,
      LOCAL_GRADE,
      CATEGORY_GRADE,
      CATEGORY_SCORE,
    ],
  });

  const mypCriteriaData = getMypCriteriaDataForGradeSummary({
    t,
    classes,
    termGradeStudentWiseCriteria,
    criteriaNodes,
    mypIbDefinedAchievementCriteriaSetId,
    studentWiseRatings,
    termId,
    gradeScales,
    gradingMethodologyTypes,
    mypCriteriaTotalCalculationMethod,
    mypFinalGradeScale,
    mypIbDefinedGradeScale,
    mypCriteriaTotalGradeScale,
    mypCriteriaRegularGradeScales,
    isUsingNewGradingSetupInCourses,
  });

  const ibPYPElementType = getIBPYPElementType({ curriculumType });

  const isCategoryDisplayTypePercentage =
    categoryTotalDisplayType === PERCENTAGE_UPPERCASE;

  const conditionalPercentage = isCategoryDisplayTypePercentage ? "%" : "";

  const categoryScoreValuePath = isCategoryDisplayTypePercentage
    ? "percentageValue"
    : "value";

  const isCurriculumDP = _.isEqual(curriculumType, CURRICULUM_TYPE_DP);
  const isCurriculumMYP = _.isEqual(curriculumType, CURRICULUM_TYPE_MYP);
  const isCurriculumUBD = _.isEqual(curriculumType, CURRICULUM_TYPE_UBD);

  const isGradingSetupDecimalConfigEnabled = getIsGradingSetupDecimalConfigEnabled();

  _.forEach(categoryOptions, category => {
    const categoryId = _.get(category, "value", "");
    const categoryLabel = _.get(category, "label", "");

    const defaultScoreValue = _.find(_.get(category, "scoreValues"), value => {
      return _.isEmpty(_.get(value, "genericTag.id"));
    });
    const scoreValueForLevel = _.find(_.get(category, "scoreValues"), value => {
      return (
        _.get(value, "genericTag.id") === _.get(subjectLevelGenericTags, "0.id")
      );
    });
    const scoreValue = isUsingNewGradingSetupInCourses
      ? scoreValueForLevel || defaultScoreValue
      : scoreValueForLevel;

    const categoryWeightage = _.get(scoreValue, categoryScoreValuePath);

    const { overallScore: categoryTotal } = getOverallScoreValue({
      studentWiseRatings,
      categoryId,
      overallScoreType: "CATEGORY_TOTAL",
    });

    const { overallGrade } = getOverallGradeValue({
      isTOK,
      studentWiseRatings,
      termId,
      ibPYPElementType,
      entityId: categoryId,
      overallGradeType: "CATEGORY_GRADE",
      isCurriculumDP,
      isCurriculumMYP,
      categoryOptions,
    });

    const { overallGrade: categoryGradeScaleRating } = getOverallGradeValue({
      isTOK,
      studentWiseRatings,
      termId,
      categoryId,
      ibPYPElementType,
      entityId: categoryId,
      overallGradeType: CATEGORY_GRADE_SCALE,
      isCurriculumDP,
      isCurriculumMYP,
      categoryOptions,
      academicCriteriaSet: _.get(category, "gradeScales.0"),
    });
    const categoryGradeScaleLabel = _.get(
      category,
      "gradeScales.0.displayName"
    );

    const showCategoryScore =
      !_.get(hiddenGradebookEntities, CATEGORY_SCORE) &&
      isCategoryScoreVisible &&
      categoryTotal;

    const showCategoryGrade =
      !_.get(hiddenGradebookEntities, CATEGORY_GRADE) &&
      overallGrade &&
      overallGrade !== "-";

    const showCategoryGradeScaleRating =
      isCategoryGradeScalesEnabled &&
      getIsRatingVisible({
        term,
        userType,
        gradeScaleId: _.get(category, "gradeScales.0.id"),
        ratingType: CATEGORY_GRADE_SCALE,
        isVisibilitySettingsV2Enabled,
        visibilitySettings: assessmentGradebookVisibilitySettings,
      }) &&
      categoryGradeScaleRating &&
      categoryGradeScaleRating !== "-";

    if (
      showCategoryScore ||
      showCategoryGrade ||
      showCategoryGradeScaleRating
    ) {
      const categoryLabelSuffix =
        isOverallScoreEnabled && isWeightageForCategoryEnabled
          ? `(${categoryWeightage}${conditionalPercentage})`
          : "";

      const tooltip = (
        <div>
          {showCategoryScore ? (
            <div>
              {`${_.get(
                criteriaLabels,
                CATEGORY_SCORE,
                t("academicSetup:category_score")
              )} : ${categoryTotal}${conditionalPercentage}`}
            </div>
          ) : null}
          {showCategoryGrade ? (
            <div>
              {`${_.get(
                criteriaLabels,
                CATEGORY_GRADE,
                t("academicSetup:category_grade")
              )} : ${overallGrade}`}
            </div>
          ) : null}
          {showCategoryGradeScaleRating ? (
            <div>
              {`${categoryGradeScaleLabel} : ${categoryGradeScaleRating}`}
            </div>
          ) : null}
        </div>
      );

      studentGradeSummaryData.push({
        id: categoryId,
        label: categoryLabel,
        labelSuffix: categoryLabelSuffix,
        value: (
          <Tooltip tooltip={tooltip}>
            <div className={classes.valueContainer}>
              {showCategoryScore ? (
                <div>
                  <span className={classes.valueText}>{categoryTotal}</span>
                  <span className={classes.valueSuffixText}>
                    {isCategoryDisplayTypePercentage
                      ? "/100%"
                      : `/${categoryWeightage}`}
                  </span>
                </div>
              ) : null}

              {showCategoryGrade ? (
                <>
                  {showCategoryScore ? <div className={classes.dot} /> : null}
                  <div className={classes.valueText}>{overallGrade}</div>
                </>
              ) : null}

              {showCategoryGradeScaleRating ? (
                <>
                  {showCategoryScore ? <div className={classes.dot} /> : null}
                  <div className={classes.valueText}>
                    {categoryGradeScaleRating}
                  </div>
                </>
              ) : null}
            </div>
          </Tooltip>
        ),
      });
    }
  });

  const termTotalCols = getTermColumns({
    t,
    term,
    userType,
    gradeScales,
    isCurriculumDP,
    isCurriculumMYP,
    isCurriculumUBD,
    criteriaLabels,
    hiddenGradebookEntities,
    gradingMethodologyTypes,
    finalGradesVisibilitySettings,
    isVisibilitySettingsV2Enabled,
    isUsingNewGradingSetupInCourses,
  });

  _.forEach(termTotalCols, ({ id, type, label, academicCriteriaSet }) => {
    if (type === "FINAL_SCORE") {
      const { overallScore: total } = getOverallScoreValue({
        studentWiseRatings,
        termId,
        overallScoreType: FINAL_SCORE,
      });

      if (total) {
        studentGradeSummaryData.push({
          id,
          label,
          value: (
            <div>
              <span className={classes.valueText}>
                {isGradingSetupDecimalConfigEnabled ? total : _.round(total, 2)}
              </span>
              <span className={classes.valueSuffixText}>/100%</span>
            </div>
          ),
        });
      }
    } else {
      const { overallGrade } = getOverallGradeValue({
        isTOK,
        studentWiseRatings,
        termId,
        ibPYPElementType,
        entityId: termId,
        overallGradeType: type,
        isCurriculumDP,
        isCurriculumMYP,
        categoryOptions,
        academicCriteriaSet,
      });

      if (overallGrade && overallGrade !== "-") {
        const overallGradeData = {
          id,
          label,
          value: <div className={classes.valueText}>{overallGrade}</div>,
        };

        if (type === FINAL_GRADE && isFinalGradeCalculatedFromMYPCriteria) {
          mypCriteriaData.push(overallGradeData);
        } else {
          studentGradeSummaryData.push(overallGradeData);
        }
      }
    }
  });

  return { studentGradeSummaryData, mypCriteriaData };
};

export const getGradebookTableProps = props => {
  const {
    isData,
    isLoading,
    students,
    gradingPeriods,
    courseId,
    curriculumProgramId,
    curriculumType,
    selectedGradingPeriod,
    associatedTerms,
    isCumulativePeriod,
    searchText,
    selectedViewBy,
    orgPointSetVariables,
    course,
    grades,
    isCourseApplicableToCategorySet,
    selectedSubject,
    selectedCategories,
    assessmentToolOptions,
    selectedAssessmentTools,
    assessmentTypeOptions,
    selectedAssessmentTypes,
    plannerElementSetId,
    selectedAcademicYears,
    rightPaneWidth,
    selectedStudents,
    evaluationTypes,
    associatedCriteriaInput,
    plannerElementSetFilter,
    defaultStudentNameSortPreference,
    studentWiseRatingsVariables,
    isScoreV2,
    isCurriculumUBD,
    isCurriculumDP,
    ibPYPElementType,
    showCategories,
    criteriaSet,
    isTOK,
    subjects,
    isCurrentAcademicYearSelected,
    isArchivedClass,
    isAbsoluteWeightageType,
    isCustomWeights,
    categoryTotalDisplayType,
    cumulativeAssociatedTermTotalDisplayType,
    canOverrideFinalGrade,
    selectedViewType,
    userId,
    subjectName,
    assessmentGradebookSettings,
    gradebookSettings,
    associatedTermsData,
    selectedGradingPeriodLabel,
    defaultGradeScaleSetVariables,
    isCurriculumMYP,
    isCurriculumPYP,
    organizationId,
    finalGradeCalculationMethod,
    subjectLevelGenericTags,
    isOverallScoreEnabled,
    isOverallGradeEnabled,
    isWeightageForCategoryEnabled,
    localGradeCalculationMethod,
    isLocalGradeCalculationEnabled,
    gradeCalculationMethod,
    gradeId,
    isAllSubjectsSelected,
    selectedGradingPeriodObj,
    showTermTotalColumns,
    canDefineGradeBoundary,
    associatedChildPlannerElementFilters,
    termGradeStudentWiseCriteriaFilter,
    mypIbDefinedAchievementCriteriaSet,
    isAssociatedTermsWeightageDefined,
    isAdmin,
    gradesData,
    categoryOptions,
    categoryOptionsRefetch,
    hasCategories,
    yearFiltersOptions,
    hasYearFilterOptions,
    criteriaNodes,
    interdisciplinaryCriteriaNodes,
    courseStudentRatings,
    refetchStudentWiseRatings,
    defaultGradeScaleSet,
    termGradeStudentWiseCriteria,
    createIbPypElementRatings,
    deleteIBPYPElementRatingById,
    showSameSubjectGradeTasksInGradebook,
    doesTeacherHaveEditAccessToOtherClassTasks,
    isAdminGradebook,
    isOtherCourseSubjectSelected,
    shouldReadCriteriaFromTask,
    isFinalGradeManuallyAssigned,
    canOverrideOverallScore,
    updateSettings,
    finalScoreCriteriaSet,
    finalGradeCriteriaSet,
    localGradeCriteriaSet,
    termGradeBoundarySet,
    studentNameSortBySettingValue,
    mypCriteriaTotalCalculationMethod,
    isAssessmentGradebookHeaderEnhancements,
    canManageGradebook,
    isRnPEnabled,
    evaluatedButNotSharedAssignmentsCountByTermMap,
    isOverallTotalsEnableForStudents,
    isOverallTotalsEnableForFamily,
    skipCountOfEvaluatedButNotSharedAssignments,
    gradingPeriodsStatusMap,
    isCourseBasedGrading,
    gradeScales,
    isUsingNewGradingSetupInCourses,
    isSubjectCommentsSupportOnGradebookEnabled,
    learningCourseId,
    courseStudentsElementRemarks,
    studentElementRemarksVariables,
    refetchStudentElementRemarks,
    differentWeightsForLevels,
    cumulativeTermWeights,
    isCategoriesEnabled,
    cumulativeTotalCalculationMethod,
    differentCategoriesForGradingPeriods,
    mypIbDefinedGradeScale,
    mypCriteriaTotalGradeScale,
    gradingMethodologyTypes,
    hiddenGradebookEntities,
    courseLevels,
    isCategoryGradeScalesEnabled,
    enableCategoryGrade,
    mypCriteriaRegularGradeScales,
    isClassArchived,
    savedTermColumnsOrder,
    isCommonDisplaySettingsEnabled,
  } = props;

  return {
    isData,
    isLoading,
    students,
    gradingPeriods,
    courseId,
    curriculumProgramId,
    curriculumType,
    selectedGradingPeriod,
    associatedTerms,
    isCumulativePeriod,
    searchText,
    selectedViewBy,
    orgPointSetVariables,
    course,
    grades,
    isCourseApplicableToCategorySet,
    selectedSubject,
    selectedCategories,
    assessmentToolOptions,
    selectedAssessmentTools,
    assessmentTypeOptions,
    selectedAssessmentTypes,
    plannerElementSetId,
    selectedAcademicYears,
    rightPaneWidth,
    selectedStudents,
    evaluationTypes,
    associatedCriteriaInput,
    plannerElementSetFilter,
    defaultStudentNameSortPreference,
    studentWiseRatingsVariables,
    isScoreV2,
    isCurriculumUBD,
    isCurriculumDP,
    ibPYPElementType,
    showCategories,
    criteriaSet,
    isTOK,
    subjects,
    isCurrentAcademicYearSelected,
    isArchivedClass,
    isAbsoluteWeightageType,
    isCustomWeights,
    categoryTotalDisplayType,
    cumulativeAssociatedTermTotalDisplayType,
    canOverrideFinalGrade,
    selectedViewType,
    userId,
    subjectName,
    assessmentGradebookSettings,
    gradebookSettings,
    associatedTermsData,
    selectedGradingPeriodLabel,
    defaultGradeScaleSetVariables,
    isCurriculumMYP,
    isCurriculumPYP,
    organizationId,
    finalGradeCalculationMethod,
    subjectLevelGenericTags,
    isOverallScoreEnabled,
    isOverallGradeEnabled,
    isWeightageForCategoryEnabled,
    localGradeCalculationMethod,
    isLocalGradeCalculationEnabled,
    gradeCalculationMethod,
    gradeId,
    isAllSubjectsSelected,
    selectedGradingPeriodObj,
    showTermTotalColumns,
    canDefineGradeBoundary,
    associatedChildPlannerElementFilters,
    termGradeStudentWiseCriteriaFilter,
    mypIbDefinedAchievementCriteriaSet,
    isAssociatedTermsWeightageDefined,
    isAdmin,
    gradesData,
    categoryOptions,
    categoryOptionsRefetch,
    hasCategories,
    yearFiltersOptions,
    hasYearFilterOptions,
    criteriaNodes,
    interdisciplinaryCriteriaNodes,
    courseStudentRatings,
    refetchStudentWiseRatings,
    defaultGradeScaleSet,
    termGradeStudentWiseCriteria,
    createIbPypElementRatings,
    deleteIBPYPElementRatingById,
    showSameSubjectGradeTasksInGradebook,
    doesTeacherHaveEditAccessToOtherClassTasks,
    isAdminGradebook,
    isOtherCourseSubjectSelected,
    shouldReadCriteriaFromTask,
    isFinalGradeManuallyAssigned,
    canOverrideOverallScore,
    updateSettings,
    finalScoreCriteriaSet,
    finalGradeCriteriaSet,
    localGradeCriteriaSet,
    termGradeBoundarySet,
    studentNameSortBySettingValue,
    mypCriteriaTotalCalculationMethod,
    isAssessmentGradebookHeaderEnhancements,
    canManageGradebook,
    isRnPEnabled,
    evaluatedButNotSharedAssignmentsCountByTermMap,
    isOverallTotalsEnableForStudents,
    isOverallTotalsEnableForFamily,
    skipCountOfEvaluatedButNotSharedAssignments,
    gradingPeriodsStatusMap,
    isCourseBasedGrading,
    gradeScales,
    isUsingNewGradingSetupInCourses,
    isSubjectCommentsSupportOnGradebookEnabled,
    learningCourseId,
    courseStudentsElementRemarks,
    studentElementRemarksVariables,
    differentWeightsForLevels,
    refetchStudentElementRemarks,
    cumulativeTermWeights,
    isCategoriesEnabled,
    cumulativeTotalCalculationMethod,
    differentCategoriesForGradingPeriods,
    mypIbDefinedGradeScale,
    mypCriteriaTotalGradeScale,
    gradingMethodologyTypes,
    hiddenGradebookEntities,
    courseLevels,
    enableCategoryGrade,
    isCategoryGradeScalesEnabled,
    mypCriteriaRegularGradeScales,
    isClassArchived,
    savedTermColumnsOrder,
    isCommonDisplaySettingsEnabled,
  };
};

export const getLearningGoalsTabOptions = ({
  t,
  overviewChildren,
  isAdminGradebook,
}) => {
  let tabOptions = _.map(
    _.filter(
      overviewChildren,
      child => child?.learningGoalType == "Progress_Summary"
    ),
    learningGoal => ({
      key: learningGoal?.route,
      label: learningGoal.dynamicLabel || learningGoal?.label,
      value: learningGoal?.route,
      plannerElementTypeExport: learningGoal?.plannerElementTypeExport,
    })
  );

  if (isAdminGradebook) {
    tabOptions = [
      {
        key: "assessments",
        label: t("progressSummary:assessments"),
        value: "assessments",
      },
      ...tabOptions,
    ];
  }

  return tabOptions;
};

export const getPlannerElementSetFilters = ({
  selectedAcademicYears,
  yearFiltersOptions,
  plannerElementSetId,
  selectedSubject,
  shouldReadCriteriaFromTask,
}) => {
  let parentNodes = _.map(yearFiltersOptions, ({ key }) => key);

  if (!_.isEmpty(selectedAcademicYears)) {
    if (shouldReadCriteriaFromTask) {
      parentNodes = _.map(yearFiltersOptions, "key");
    } else {
      parentNodes = selectedAcademicYears;
    }
  }

  return {
    id: plannerElementSetId,
    filters: {
      upToDepth: 2,
      startDepth: 2,
      associatedParents: {
        ids: [selectedSubject],
        type: "SUBJECT",
      },
      parentNodes,
    },
  };
};

export const getAssignmentSelectedYearLevelCriteriaLabel = ({
  assignment,
  selectedSubject,
}) => {
  const assessmentTools = _.get(
    assignment,
    "content.assessmentTools.edges",
    []
  );

  const isAssignmentWithMYPObjectiveRubric = _.some(assessmentTools, {
    node: {
      assessmentToolType: MYP_OBJECTIVE_RUBRIC,
    },
  });

  if (!isAssignmentWithMYPObjectiveRubric) {
    return;
  }

  const assignmentFields = _.get(assignment, "content.fields", []);
  const assignmentMYPObjectives = _.get(
    _.find(assignmentFields, { uid: "objectivesMYP" }),
    "resolvedMinimalTree.nodes",
    []
  );
  const assignmentSelectedYearLevelCriteria = _.find(
    assignmentMYPObjectives,
    item => {
      const associatedParents = _.get(item, "associatedParents");
      const associatedSubject = _.find(associatedParents, {
        __typename: "Subject",
      });

      return _.get(associatedSubject, "id") === selectedSubject;
    }
  );
  const assignmentSelectedYearLevelCriteriaLabel = _.get(
    assignmentSelectedYearLevelCriteria,
    "label"
  );

  return assignmentSelectedYearLevelCriteriaLabel;
};

export const getOverrideRatingsModalTableHeaders = params => {
  const {
    t,
    mode,
    categoryOptions,
    categoryId,
    categoryTotalDisplayType,
    isWeightageForCategoryEnabled,
    isCurriculumDP,
    isCurriculumMYP,
    isScoreV2,
    isTOK,
    columnConfiguration,
    gradingPeriods,
    selectedGradingPeriod,
    isOverallScoreEnabled,
    isCurriculumUBD,
    isOverallGradeEnabled,
    isLocalGradeCalculationEnabled,
    gradeCalculationMethod,
    finalGradeCalculationMethod,
    canOverrideFinalGrade,
    organizationId,
    curriculumProgramId,
    criteriaNodes,
    subjects,
  } = params;

  const studentsColumn = {
    key: "students",
    columnKey: "students",
    alignment: "left",
    isSticky: true,
    value: <span className="heading-6">{t("common:student_plural")}</span>,
    style: {
      minWidth: "260px",
    },
  };

  switch (mode) {
    case "term": {
      const termTotalColumns = getTermTotalColumns({
        t,
        isModalMode: true,
        isCurriculumDP,
        isCurriculumMYP,
        isScoreV2,
        isTOK,
        columnConfiguration,
        gradingPeriods,
        selectedGradingPeriod,
        isOverallScoreEnabled,
        isCurriculumUBD,
        isOverallGradeEnabled,
        isLocalGradeCalculationEnabled,
        gradeCalculationMethod,
        finalGradeCalculationMethod,
        organizationId,
        curriculumProgramId,
        criteriaNodes,
        subjects,
      });

      const termTotalColumnsLeaves = _.filter(termTotalColumns, { level: 1 });

      const ratingColumns = _.map(termTotalColumnsLeaves, column => {
        const { alignment, key, value, columnKey = "", entity } = column;

        return {
          alignment,
          key,
          value,
          columnKey,
          entity,
          style: styles.overrideRatingsModalTableCols,
        };
      });

      return [studentsColumn, ...ratingColumns];
    }
    case "category": {
      const category = _.find(
        categoryOptions,
        categoryOption => _.get(categoryOption, "value") === categoryId
      );

      const conditionalPercentage = getConditionalPercentageForCategoryHeader({
        categoryTotalDisplayType,
        isAbsoluteWeightageType: false,
      });

      const categoryCols = getCategoryTotalColumn({
        t,
        category: { ...category, categoryId },
        isCurriculumDP,
        isCurriculumMYP,
        isScoreV2,
        conditionalPercentage,
        isTOK,
        columnConfiguration,
        isOverallScoreEnabled,
        isCurriculumUBD,
        categoryTotalDisplayType,
        isOverallGradeEnabled,
        finalGradeCalculationMethod,
        isWeightageForCategoryEnabled,
        gradeCalculationMethod,
        isModalMode: true,
        canOverrideFinalGrade,
        organizationId,
        curriculumProgramId,
      });

      const ratingColumns = _.map(
        _.get(categoryCols, "subHeaders", []),
        col => {
          const { entity, label } = col;

          return {
            key: entity,
            columnKey: entity,
            alignment: "center",
            value: label,
            style: styles.overrideRatingsModalTableCols,
          };
        }
      );

      return [studentsColumn, ...ratingColumns];
    }
  }
};

export const getAssignedStudents = ({
  assignment,
  isSameCourseAssignment,
  courseStudents,
}) => {
  const assignedStudents = _.get(assignment, "assignedStudents", []);

  if (isSameCourseAssignment) {
    return assignedStudents;
  }

  const courseStudentIds = _.map(courseStudents, "id");

  return _.filter(assignedStudents, ({ student }) =>
    _.includes(courseStudentIds, _.get(student, "id"))
  );
};

export const getAcademicCriteriaValueIdForOverridingGradeInput = ({
  key,
  isCurriculumUBD,
  categoryGradeBoundaryId,
  overallGradeType,
}) => {
  if (
    isCurriculumUBD ||
    (overallGradeType === "CATEGORY_GRADE" &&
      _.isEmpty(categoryGradeBoundaryId))
  ) {
    return null;
  }

  return key;
};

export const getScoreGradeHistoryPopoverTabs = getMemoizedLangFunction(
  ({ t }) => {
    return [
      {
        value: SCORE,
        label: t("common:score"),
      },
      {
        value: "GRADE",
        label: t("common:grade"),
      },
    ];
  }
);

export const getCategoryWeight = ({
  category,
  studentTag,
  categoryTotalDisplayType,
}) => {
  const isCategoryDisplayTypePercentage =
    categoryTotalDisplayType === PERCENTAGE_UPPERCASE;

  const conditionalPercentage = isCategoryDisplayTypePercentage ? "%" : "";

  const categoryScoreValuePath = isCategoryDisplayTypePercentage
    ? "percentageValue"
    : "value";

  const scoreValues = _.get(category, "scoreValues");

  const scoreValue = studentTag
    ? _.find(scoreValues, {
        genericTag: { label: _.get(studentTag, "label") },
      })
    : _.first(scoreValues);

  return {
    categoryWeight: _.get(scoreValue, categoryScoreValuePath),
    conditionalPercentage,
  };
};

const _getUpdatedAssignments = ({ assignments, students }) => {
  const updatedAssignments = [];

  _.forEach(assignments, assignment => {
    const { assignedStudents } = assignment?.node || {};

    const updatedAssignedStudents = [];

    _.forEach(assignedStudents, assignedStudent => {
      const studentId = _.get(assignedStudent, "student.id");

      const student = _.find(
        students,
        currStudent => _.get(currStudent, "id") === studentId
      );

      if (student) {
        const updatedStudentObj = {
          ...student,
          courseMap: student?.tag ? [{ tags: [student?.tag] }] : [],
        };

        updatedAssignedStudents.push({
          ...assignedStudent,
          student: updatedStudentObj,
        });
      }
    });

    updatedAssignments.push({
      node: {
        ..._.get(assignment, "node", {}),
        assignedStudents: updatedAssignedStudents,
      },
    });
  });

  return updatedAssignments;
};

export const getUpdatedAssignmentsByView = ({
  assignments,
  students,
  isCategoriesView,
}) => {
  let updatedAssignments = [];

  if (isCategoriesView) {
    _.forEach(assignments, categoryAssignment => {
      const categoryAssignments = _.get(categoryAssignment, "assignments", []);

      updatedAssignments.push({
        ...categoryAssignment,
        assignments: _getUpdatedAssignments({
          assignments: categoryAssignments,
          students,
          isCategoriesView,
        }),
      });
    });
  } else {
    updatedAssignments = _getUpdatedAssignments({
      assignments,
      students,
      isCategoriesView,
    });
  }

  return updatedAssignments;
};

export const getUpdatedAssignmentsByViewV2 = ({
  t,
  assignments,
  students,
  shouldGroupByCategories,
  categoryOptions = [],
  isUsingNewGradingSetupInCourses,
}) => {
  let updatedAssignments = [];

  if (shouldGroupByCategories) {
    const categoryIdPath = isUsingNewGradingSetupInCourses
      ? "content.globalCategories.0.id"
      : "content.category.id";
    const assignmentsGroupedByCategoryId = _.groupBy(
      assignments,
      ({ node }) => _.get(node, categoryIdPath) || "no-category"
    );

    const categoryIds = _.without(
      _.keys(assignmentsGroupedByCategoryId),
      "no-category"
    );

    // Add categorized assignments
    updatedAssignments = _.map(
      _.filter(categoryOptions, ({ value }) => _.includes(categoryIds, value)),
      ({ value: categoryId, label: categoryLabel }) => ({
        id: categoryId,
        label: categoryLabel,
        assignments: _getUpdatedAssignments({
          assignments: _.get(assignmentsGroupedByCategoryId, categoryId, []),
          students,
        }),
      })
    );

    // Add no-category assignments if they exist
    const uncategorizedAssignments = _.get(
      assignmentsGroupedByCategoryId,
      "no-category",
      []
    );
    if (!_.isEmpty(uncategorizedAssignments)) {
      updatedAssignments.push({
        id: "no-category",
        label: t("progressSummary:no_category_associated"),
        assignments: _getUpdatedAssignments({
          assignments: uncategorizedAssignments,
          students,
        }),
      });
    }
  } else {
    updatedAssignments = _getUpdatedAssignments({
      assignments,
      students,
    });
  }

  return updatedAssignments;
};

export const getDisplaySettingOptions = ({
  t,
  isHideTasksExcludedFromTermTotal,
  isHideTasksFromOtherClasses,
  showSameSubjectGradeTasksInGradebook,
  organizationId,
  curriculumProgramId,
  isCurriculumUBD,
}) => {
  const criteriaLabels = getCriteriaSetLabels({
    organizationId,
    curriculumProgramId,
    criteriaTypes: [FINAL_GRADE, LOCAL_GRADE],
  });

  const finalGradeLabel = isCurriculumUBD
    ? _.get(criteriaLabels, LOCAL_GRADE, t("common:final_grade_text"))
    : _.get(criteriaLabels, FINAL_GRADE, t("common:final_grade_text"));

  const displaySettingOptions = [
    {
      key: "HideTasksExcludedFromTermTotalInGradebook",
      title: t(
        "progressSummary:hide_tasks_that_are_excluded_from_the_term_total"
      ),
      description: t(
        "progressSummary:enable_checkbox_to_hide_task_from_gradebook_with_label",
        {
          finalGradeLabel,
        }
      ),
      isChecked: isHideTasksExcludedFromTermTotal,
    },
  ];

  if (showSameSubjectGradeTasksInGradebook) {
    displaySettingOptions.push({
      key: "HideTasksFromOtherClassesInGradebook",
      title: t("progressSummary:hide_tasks_from_other_classes"),
      description: t(
        "progressSummary:hide_those_assignments_from_showing_in_gradebook_of_this_class."
      ),
      isChecked: isHideTasksFromOtherClasses,
    });
  }

  return displaySettingOptions;
};

export const getLocalGradeAcademicCriteriaValuesFromFinalGradeSet = ({
  finalGradeCriteriaSet,
  gradeId,
  filterByGrade = false,
}) => {
  const finalGradeCriteriaValues = _.get(
    finalGradeCriteriaSet,
    "academicCriteriaValues",
    []
  );

  const localGradeAcademicCriteriaValues = [];

  _.forEach(finalGradeCriteriaValues, ({ localGrades, displaySequence }) => {
    _.forEach(localGrades, ({ localGradeCriteriaValue = {}, grade }) => {
      const { id } = grade || {};
      const localGradeAcademicCriteriaValue = {
        ...localGradeCriteriaValue,
        displaySequence,
      };

      if (filterByGrade) {
        if (id === gradeId) {
          localGradeAcademicCriteriaValues.push(
            localGradeAcademicCriteriaValue
          );
        }
      } else {
        localGradeAcademicCriteriaValues.push(localGradeAcademicCriteriaValue);
      }
    });
  });

  return localGradeAcademicCriteriaValues;
};

export const getAcademicCriteriaValues = ({
  gradeId,
  criteriaSet,
  overallGradeType,
  finalGradeCriteriaSet,
  localGradeCriteriaSet,
  curriculumProgramType,
  localGradeCalculationMethod,
}) => {
  if (overallGradeType !== LOCAL_GRADE) {
    return _.get(criteriaSet, "academicCriteriaValues", []);
  }

  const isLocalGradeCalculatedFromScore = _.includes(
    [FINAL_SCORE, "SCORE_BASED_ASSESSMENT", "WEIGHTED_SCORE"],
    localGradeCalculationMethod
  );

  switch (curriculumProgramType) {
    case CURRICULUM_TYPE_DP: {
      const academicCriteriaValues = _.get(
        localGradeCriteriaSet,
        "academicCriteriaValues",
        []
      );

      if (isLocalGradeCalculatedFromScore) {
        return academicCriteriaValues;
      }

      const localGradeAcademicCriteriaValues = getLocalGradeAcademicCriteriaValuesFromFinalGradeSet(
        {
          finalGradeCriteriaSet: criteriaSet,
        }
      );

      return localGradeAcademicCriteriaValues;
    }
    case CURRICULUM_TYPE_MYP: {
      if (isLocalGradeCalculatedFromScore) {
        return _.get(localGradeCriteriaSet, "academicCriteriaValues", []);
      }

      const localGradeAcademicCriteriaValues = getLocalGradeAcademicCriteriaValuesFromFinalGradeSet(
        {
          finalGradeCriteriaSet,
          gradeId,
          filterByGrade: true,
        }
      );

      return localGradeAcademicCriteriaValues;
    }
    default: {
      return _.get(criteriaSet, "academicCriteriaValues", []);
    }
  }
};

export const getAcademicCriteriaSetId = ({
  curriculumProgramType,
  overallGradeType,
  defaultGradeScaleSetId,
  localGradeCriteriaSet,
  criteriaSetId,
}) => {
  if (curriculumProgramType === CURRICULUM_TYPE_UBD) {
    return defaultGradeScaleSetId;
  }

  switch (overallGradeType) {
    case FINAL_GRADE:
    case "CATEGORY_GRADE": {
      return criteriaSetId;
    }
    case LOCAL_GRADE: {
      if (localGradeCriteriaSet) {
        return _.get(localGradeCriteriaSet, "id");
      }

      return criteriaSetId;
    }
  }
};

export const getAcademicTermIdForOverridingRatingsInput = ({
  isCumulativePeriod,
  termId,
  categoryId,
  selectedGradingPeriod,
  categoryOptions,
  ratingType,
}) => {
  if (!isCumulativePeriod) {
    return selectedGradingPeriod;
  }

  const category = _.find(categoryOptions, { value: categoryId });

  let academicTermId = selectedGradingPeriod;

  if (ratingType === "CATEGORY_GRADE" || ratingType === "CATEGORY_TOTAL") {
    const categoryAssociatedEntities = _.get(
      category,
      "associatedEntities",
      []
    );
    academicTermId = _.find(categoryAssociatedEntities, {
      __typename: "AcademicTerm",
    })?.id;
  } else {
    academicTermId = termId;
  }

  return academicTermId;
};

export const getShowTryDifferentFilterEmptyState = ({
  evaluationTypes,
  searchText,
  selectedAssessmentTypes,
  selectedCategories,
  assessmentTypeOptions,
  students,
  selectedStudentIds,
  emptyStateCondition,
  selectedAssessmentTools,
  assessmentToolOptions,
}) => {
  return (
    emptyStateCondition &&
    (evaluationTypes !== "ALL" ||
      searchText !== "" ||
      !_.isEmpty(selectedCategories) ||
      _.size(selectedAssessmentTools) !== _.size(assessmentToolOptions) ||
      _.size(selectedAssessmentTypes) !== _.size(assessmentTypeOptions) ||
      !_.isEqual(
        selectedStudentIds,
        _.map(
          _.filter(students, student => !_.get(student, "isArchived")),
          "id"
        )
      ))
  );
};

export const getAdditionalEntitiesToUpdate = ({
  columnKey,
  columnKeys,
  finalGradeCalculationMethod,
  localGradeCalculationMethod,
  curriculumProgramType,
}) => {
  switch (columnKey) {
    case "CATEGORY_TOTAL": {
      return _.filter(columnKeys, key => {
        if (key === "CATEGORY_GRADE") {
          return finalGradeCalculationMethod === SCORE;
        }

        return false;
      });
    }
    case FINAL_SCORE: {
      return _.filter(columnKeys, key => {
        const isFinalGradeCalculatedFromScore =
          finalGradeCalculationMethod === SCORE;
        if (key === FINAL_GRADE) {
          return isFinalGradeCalculatedFromScore;
        }

        if (key === LOCAL_GRADE) {
          if (curriculumProgramType === CURRICULUM_TYPE_UBD) {
            return finalGradeCalculationMethod === SCORE;
          } else {
            const isLocalGradeCalculatedFromScore = _.includes(
              [FINAL_SCORE, "SCORE_BASED_ASSESSMENT", "WEIGHTED_SCORE"],
              localGradeCalculationMethod
            );

            const isLocalGradeCalculatedFromFinalGrade = _.includes(
              [FINAL_GRADE, "MYP_FINAL_GRADE"],
              localGradeCalculationMethod
            );

            return (
              isLocalGradeCalculatedFromScore ||
              (isFinalGradeCalculatedFromScore &&
                isLocalGradeCalculatedFromFinalGrade)
            );
          }
        }

        return false;
      });
    }
    case FINAL_GRADE: {
      return _.filter(columnKeys, key => {
        if (key === LOCAL_GRADE) {
          return _.includes(
            [FINAL_GRADE, "MYP_FINAL_GRADE"],
            localGradeCalculationMethod
          );
        }

        return false;
      });
    }
    case "CRITERION_A":
    case "CRITERION_B":
    case "CRITERION_C":
    case "CRITERION_D": {
      return _.filter(columnKeys, key => {
        const isFinalGradeCalculatedFromMYPCriteria =
          finalGradeCalculationMethod === MYP_CRITERIA;
        if (key === FINAL_GRADE) {
          return isFinalGradeCalculatedFromMYPCriteria;
        }

        if (key === LOCAL_GRADE) {
          const isLocalGradeCalculatedFromFinalGrade = _.includes(
            [FINAL_GRADE, "MYP_FINAL_GRADE"],
            localGradeCalculationMethod
          );

          return (
            isFinalGradeCalculatedFromMYPCriteria &&
            isLocalGradeCalculatedFromFinalGrade
          );
        }

        return false;
      });
    }

    default: {
      return null;
    }
  }
};

const getCurrentFinalGradeValueForUpdatingAdditionalEntities = ({
  termId,
  studentWiseRatings,
  ibPYPElementType,
  isTOK,
  isCurriculumDP,
  isCurriculumMYP,
  newUpdatedValues,
  studentId,
  input,
  currentValue,
  currentColumnKey,
  currentComputedValue,
}) => {
  if (currentColumnKey === FINAL_GRADE) {
    return currentValue || currentComputedValue;
  }

  const { editedOverallGrade } = getOverallGradeValue({
    isTOK,
    studentWiseRatings,
    ibPYPElementType,
    entityId: termId,
    overallGradeType: FINAL_GRADE,
    isCurriculumDP,
    isCurriculumMYP,
    valueDataInState: _.get(newUpdatedValues, [studentId, FINAL_GRADE]),
  });

  const currentComputedFinalGradeValue = _.get(input, [
    FINAL_GRADE,
    "computedValue",
  ]);

  return editedOverallGrade || currentComputedFinalGradeValue;
};

const orderGradeBoundarySetFunc = ({
  item,
  studentTag,
  valuesPath = "values",
}) => {
  const values = _.get(item, valuesPath);

  if (studentTag) {
    const valueData = _.find(values, ({ genericTag }) => {
      return _.get(genericTag, "label") === _.get(studentTag, "label");
    });

    const minValue = _.get(valueData, "minValue");

    return minValue;
  }

  return _.get(values, "0.minValue");
};

const getOrderedGradeBoundarySet = ({
  gradeBoundarySet,
  studentTag,
  valuesPath = "values",
}) => {
  return _.orderBy(
    gradeBoundarySet,
    item => orderGradeBoundarySetFunc({ item, studentTag, valuesPath }),
    "desc"
  );
};

const getGradeBoundaryValueData = ({
  orderedGradeBoundarySet,
  studentTag,
  currentValue,
  maxScore,
  valuesPath = "values",
  comparePercentage = false,
}) => {
  const gradeBoundaryValueData = _.find(orderedGradeBoundarySet, item => {
    const values = _.get(item, valuesPath);

    const valueData = studentTag
      ? _.find(values, value => {
          const { genericTag } = value;
          return _.get(genericTag, "label") === _.get(studentTag, "label");
        })
      : _.first(values);

    const minValue = _.get(valueData, "minValue");

    if (comparePercentage) {
      const currentValuePercentage = (currentValue / maxScore) * 100;
      return currentValuePercentage >= minValue;
    } else {
      return currentValue >= minValue;
    }
  });

  return gradeBoundaryValueData;
};

const getInputData = ({ gradeBoundaryValueData }) => {
  const gradeBoundaryLabel = _.get(gradeBoundaryValueData, "label");

  return {
    computedValue: gradeBoundaryLabel,
  };
};

export const getUpdateAdditonalEntitiesInput = ({
  isTOK,
  ibPYPElementType,
  isCurriculumDP,
  isCurriculumMYP,
  termId,
  gradeId,
  maxScore,
  studentId,
  studentTag,
  currentValue,
  criteriaNodes,
  gradeBoundarySet,
  currentColumnKey,
  newUpdatedValues,
  studentWiseRatings,
  defaultGradeScaleSet,
  currentComputedValue,
  localGradeCriteriaSet,
  finalGradeCriteriaSet,
  curriculumProgramType,
  additionalEntitiesToUpdate,
  localGradeCalculationMethod,
  finalGradeCalculationMethod,
  termGradeStudentWiseCriteria,
  mypIbDefinedAchievementCriteriaSetId,
  mypCriteriaTotalCalculationMethod,
}) => {
  const input = {};

  _.forEach(additionalEntitiesToUpdate, entity => {
    if (
      _.includes([FINAL_SCORE, "CATEGORY_TOTAL"], currentColumnKey) &&
      _.isEmpty(currentValue)
    ) {
      input[entity] = {
        computedValue: "",
      };
      return;
    }

    switch (entity) {
      case "CATEGORY_GRADE":
        {
          const orderedGradeBoundarySet = getOrderedGradeBoundarySet({
            gradeBoundarySet,
            studentTag,
          });

          const gradeBoundaryValueData = getGradeBoundaryValueData({
            orderedGradeBoundarySet,
            studentTag,
            currentValue,
            maxScore,
            comparePercentage: true,
          });

          const inputData = getInputData({
            gradeBoundaryValueData,
          });

          input[entity] = inputData;
        }
        break;
      case FINAL_GRADE:
        {
          switch (finalGradeCalculationMethod) {
            case SCORE:
              {
                const orderedGradeBoundarySet = getOrderedGradeBoundarySet({
                  gradeBoundarySet,
                  studentTag,
                });

                const gradeBoundaryValueData = getGradeBoundaryValueData({
                  orderedGradeBoundarySet,
                  studentTag,
                  currentValue,
                });

                const inputData = getInputData({
                  gradeBoundaryValueData,
                });

                input[entity] = inputData;
              }
              break;
            case MYP_CRITERIA: {
              const mypCriteriaTotal = getMypCriteriaTotal({
                termId,
                studentId,
                updatedValues: newUpdatedValues,
                criteriaNodes,
                studentWiseRatings,
                termGradeStudentWiseCriteria,
                mypIbDefinedAchievementCriteriaSetId,
                mypCriteriaTotalCalculationMethod,
              });

              const finalGradeCriteriaSetCriteriaValues = _.get(
                finalGradeCriteriaSet,
                "academicCriteriaValues"
              );

              const gradeBoundaryValueData = _.find(
                finalGradeCriteriaSetCriteriaValues,
                ({ boundaryGuidelines }) => {
                  return _.includes(
                    boundaryGuidelines,
                    parseInt(mypCriteriaTotal)
                  );
                }
              );

              const inputData = getInputData({
                gradeBoundaryValueData,
              });

              input[entity] = inputData;
            }
          }
        }
        break;
      case LOCAL_GRADE: {
        if (curriculumProgramType === CURRICULUM_TYPE_UBD) {
          if (finalGradeCalculationMethod === SCORE) {
            const gradeBoundarySetUbd = _.isEmpty(gradeBoundarySet)
              ? _.get(defaultGradeScaleSet, "categories")
              : gradeBoundarySet;

            const orderedGradeBoundarySet = getOrderedGradeBoundarySet({
              gradeBoundarySet: gradeBoundarySetUbd,
              studentTag,
            });

            const gradeBoundaryValueData = getGradeBoundaryValueData({
              orderedGradeBoundarySet,
              currentValue,
            });

            const inputData = getInputData({ gradeBoundaryValueData });

            input[entity] = inputData;
          }
        }

        switch (localGradeCalculationMethod) {
          case FINAL_SCORE:
          case "SCORE_BASED_ASSESSMENT":
          case "WEIGHTED_SCORE":
            {
              const localGradeCriteriaValues = _.get(
                localGradeCriteriaSet,
                "academicCriteriaValues"
              );

              const orderedGradeBoundarySet = getOrderedGradeBoundarySet({
                gradeBoundarySet: localGradeCriteriaValues,
                valuesPath: "scoreValues",
              });

              const gradeBoundaryValueData = getGradeBoundaryValueData({
                orderedGradeBoundarySet,
                currentValue,
                valuesPath: "scoreValues",
              });

              const inputData = getInputData({
                gradeBoundaryValueData,
              });

              input[entity] = inputData;
            }
            break;
          case "MYP_FINAL_GRADE":
          case FINAL_GRADE: {
            const currentFinalGradeValue = getCurrentFinalGradeValueForUpdatingAdditionalEntities(
              {
                currentComputedValue,
                termId,
                studentWiseRatings,
                ibPYPElementType,
                isTOK,
                isCurriculumDP,
                isCurriculumMYP,
                newUpdatedValues,
                studentId,
                input,
                currentValue,
                currentColumnKey,
              }
            );

            const finalGradeCriteriaValues = _.get(
              finalGradeCriteriaSet,
              "academicCriteriaValues"
            );

            const currentFinalGradeCriteriaValue = _.find(
              finalGradeCriteriaValues,
              ({ label }) => {
                return label === currentFinalGradeValue;
              }
            );

            const localGrades = _.get(
              currentFinalGradeCriteriaValue,
              "localGrades"
            );

            const localGrade =
              curriculumProgramType === CURRICULUM_TYPE_MYP
                ? _.find(localGrades, ({ grade }) => {
                    return _.get(grade, "id") === gradeId;
                  })
                : _.first(localGrades);

            const localGradeLabel = _.get(
              localGrade,
              "localGradeCriteriaValue.label"
            );

            const inputData = {
              computedValue: localGradeLabel,
            };

            input[entity] = inputData;
          }
        }
      }
    }
  });

  return input;
};

export const getShowIndicationForOverriddenScore = ({
  editedScore,
  isRemoved,
}) => {
  return !_.isEmpty(editedScore) || isRemoved;
};

export const getShowIndicationForOverriddenGrade = ({
  editedOverallGrade,
  isOverallScoreEnabled,
  curriculumProgramType,
  finalGradeCalculationMethod,
  overallGradeType,
  finalGradeCalculationMethodology,
  isRemoved,
}) => {
  const condition = !_.isEmpty(editedOverallGrade) || isRemoved;

  switch (overallGradeType) {
    case LOCAL_GRADE: {
      switch (curriculumProgramType) {
        case CURRICULUM_TYPE_PYP: {
          return condition && isOverallScoreEnabled;
        }
        case CURRICULUM_TYPE_UBD: {
          return condition && finalGradeCalculationMethod !== MANUAL;
        }
        default: {
          return condition;
        }
      }
    }
    case "CATEGORY_GRADE": {
      if (curriculumProgramType === CURRICULUM_TYPE_MYP) {
        return condition && finalGradeCalculationMethod === SCORE;
      }

      return condition && finalGradeCalculationMethod !== MANUAL;
    }
    case GRADE_SCALE:
    case CATEGORY_GRADE_SCALE: {
      return condition && finalGradeCalculationMethodology !== MANUAL;
    }
    default: {
      return condition && finalGradeCalculationMethod !== MANUAL;
    }
  }
};

export const getGradeBoundaryMappings = ({
  t,
  curriculumProgramType,
  subjectLevelGenericTags: _subjectLevelGenericTags,
  overallGradeType,
  localGradeCalculationMethod,
  finalGradeCalculationMethod,
  criteriaLabels,
}) => {
  const subjectLevelGenericTags = _.orderBy(_subjectLevelGenericTags, "label");

  switch (overallGradeType) {
    case FINAL_GRADE: {
      const mappingLabel =
        finalGradeCalculationMethod === MYP_CRITERIA
          ? t("progressSummary:myp_score")
          : _.get(
              criteriaLabels,
              FINAL_SCORE,
              `% ${t("common:score_lowercase")}`
            );

      if (_.isEmpty(subjectLevelGenericTags)) {
        return [mappingLabel];
      } else {
        return _.map(
          subjectLevelGenericTags,
          ({ label }) => `${mappingLabel} ${label}`
        );
      }
    }
    case "CATEGORY_GRADE": {
      if (_.isEmpty(subjectLevelGenericTags)) {
        return [_.get(criteriaLabels, CATEGORY_SCORE, t("common:score"))];
      } else {
        return _.map(
          subjectLevelGenericTags,
          ({ label }) =>
            `${_.get(
              criteriaLabels,
              CATEGORY_SCORE,
              t("common:score")
            )} ${label}`
        );
      }
    }
    case LOCAL_GRADE: {
      if (curriculumProgramType === CURRICULUM_TYPE_UBD) {
        return [_.get(criteriaLabels, FINAL_SCORE, t("common:overall_score"))];
      }

      const mappingLabel = _.includes(
        [FINAL_SCORE, "WEIGHTED_SCORE", "SCORE_BASED_ASSESSMENT"],
        localGradeCalculationMethod
      )
        ? _.get(criteriaLabels, FINAL_SCORE, t("common:overall_score"))
        : _.get(criteriaLabels, FINAL_GRADE, t("common:overall_grade"));

      return [mappingLabel];
    }
  }
};

export const getCriteriaValuesFromGradeBoundary = ({
  gradeId,
  overallGradeType,
  gradeBoundarySet,
  defaultGradeScaleSet,
  localGradeCalculationMethod,
  finalGradeCriteriaSet,
  localGradeCriteriaSet,
  curriculumProgramType,
}) => {
  if (
    overallGradeType === LOCAL_GRADE &&
    curriculumProgramType !== CURRICULUM_TYPE_UBD
  ) {
    switch (localGradeCalculationMethod) {
      case FINAL_SCORE:
      case "WEIGHTED_SCORE":
      case "SCORE_BASED_ASSESSMENT": {
        const criteriaValues = _.get(
          localGradeCriteriaSet,
          "academicCriteriaValues",
          []
        );

        return _.map(criteriaValues, criteriaValue => {
          const { scoreValues } = criteriaValue || {};
          return {
            ...criteriaValue,
            values: scoreValues,
          };
        });
      }
      case "MYP_FINAL_GRADE":
      case FINAL_GRADE: {
        const criteriaValues = [];

        const finalGradeCriteriaValues = _.get(
          finalGradeCriteriaSet,
          "academicCriteriaValues",
          []
        );

        _.forEach(finalGradeCriteriaValues, criteriaValue => {
          const finalGradeLabel = _.get(criteriaValue, "label");

          const localGrades = _.get(criteriaValue, "localGrades");

          const localGrade =
            curriculumProgramType === CURRICULUM_TYPE_MYP
              ? _.find(
                  localGrades,
                  ({ grade }) => _.get(grade, "id") === gradeId
                )
              : _.first(localGrades);

          const localGradeCriteriaValue = _.get(
            localGrade,
            "localGradeCriteriaValue"
          );

          const localGradeLabel = _.get(localGradeCriteriaValue, "label");

          criteriaValues.push({
            label: localGradeLabel,
            finalGradeLabel,
          });
        });

        return criteriaValues;
      }
    }
  }

  const categories = _.isEmpty(gradeBoundarySet)
    ? _.get(defaultGradeScaleSet, "categories", [])
    : gradeBoundarySet;

  return categories;
};

export const getCanShowInfoButton = ({
  overallGradeType,
  finalGradeCalculationMethod,
  curriculumProgramType,
  isOverallScoreEnabled,
}) => {
  switch (overallGradeType) {
    case "CATEGORY_GRADE":
    case FINAL_GRADE: {
      return finalGradeCalculationMethod === SCORE;
    }
    case LOCAL_GRADE: {
      if (curriculumProgramType === CURRICULUM_TYPE_UBD) {
        return finalGradeCalculationMethod === SCORE;
      }

      if (curriculumProgramType === CURRICULUM_TYPE_PYP) {
        return isOverallScoreEnabled;
      }

      return true;
    }
    default: {
      return false;
    }
  }
};

export const getOrderedCriteriaValues = ({
  isTOK,
  overallGradeType,
  criteriaValues,
  curriculumProgramType,
  isLocalGradeCalculatedFromOverallGrade,
}) => {
  switch (overallGradeType) {
    case FINAL_GRADE: {
      return _.orderBy(criteriaValues, "label", isTOK ? "asc" : "desc");
    }
    case "CATEGORY_GRADE": {
      if (curriculumProgramType === CURRICULUM_TYPE_UBD) {
        return _.orderBy(
          criteriaValues,
          item => _.get(item, "values.0.minValue"),
          "desc"
        );
      }

      return _.orderBy(criteriaValues, "label", isTOK ? "asc" : "desc");
    }
    case LOCAL_GRADE: {
      if (isLocalGradeCalculatedFromOverallGrade) {
        return _.orderBy(criteriaValues, "finalGradeLabel", "desc");
      }

      return _.orderBy(
        criteriaValues,
        item => _.get(item, "values.0.minValue"),
        "desc"
      );
    }
  }
};

export const getAggregateScoreValue = ({
  ratings,
  academicCriteriaSetId,
  aggregateCriteriaTypes,
  academicTermId,
  key = "value",
}) => {
  return _.get(
    _.find(ratings, rating => {
      let condition =
        _.get(rating, "ibPYPElementId") === academicCriteriaSetId &&
        _.get(rating, "ibPYPElementType") === "ACADEMIC_CRITERIA_SET" &&
        _.get(rating, "academicCriteriaSet.criteriaType") === "AGGREGATE" &&
        _.includes(
          aggregateCriteriaTypes,
          _.get(rating, "academicCriteriaSet.subType")
        );

      if (!_.isEmpty(academicTermId)) {
        condition =
          condition && _.get(rating, "academicTerm.id") === academicTermId;
      }

      return condition;
    }),
    key
  );
};

export const getIsConfigurationExistForCurrentTermAndCriteria = ({
  termCriteriaAggregateSetsMap,
  academicTermId,
  criteriaSetId,
  aggregateTypes,
}) => {
  return _.some(
    _.get(termCriteriaAggregateSetsMap, [academicTermId, criteriaSetId], []),
    aggregateCriteriaSet =>
      _.includes(aggregateTypes, _.get(aggregateCriteriaSet, "subType"))
  );
};

export const getIsTotalEnabledForFinalGrade = ({
  termCriteriaAggregateSetsMap,
  finalGradeCriteriaSet,
  academicTermIds,
}) => {
  return _.some(academicTermIds, academicTermId => {
    return getIsConfigurationExistForCurrentTermAndCriteria({
      termCriteriaAggregateSetsMap,
      academicTermId,
      criteriaSetId: _.get(finalGradeCriteriaSet, "id"),
      aggregateTypes: ["SUM"],
    });
  });
};

export const getIsScoreAggregationEnabled = ({
  academicYearId,
  curriculumProgramId,
}) => {
  const isUsingNewGradingSetupInCourses = getIsConfigurationEnabledForCPAY({
    configuration: USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
    curriculumProgramId: curriculumProgramId,
    academicYearId,
  });

  // currently score aggregation is only applicable for old grading setup
  // update this when score aggregation is supported for new grading setup
  return (
    !isUsingNewGradingSetupInCourses &&
    getSettingValue({
      name: "EnableScoreAggregation",
      currentCurriculumProgramId: curriculumProgramId,
    })
  );
};

/**
 * Find a V2 aggregate rating by matching on metadata fields.
 * V2 aggregates use metadata.gradingType + metadata.aggregationType
 * instead of ibPYPElementId-based matching used by the old system.
 */
export const getV2AggregateRating = ({
  ratings,
  gradingType,
  gradeScaleId,
  aggregationType,
  academicTermId,
  key = "value",
}) => {
  return _.get(
    _.find(ratings, rating => {
      const criteriaType = _.get(rating, "academicCriteriaSet.criteriaType");
      const metadata = _.get(rating, "metadata") || {};

      const isAggregate = criteriaType === "AGGREGATE";
      const matchesGradingType = metadata.gradingType === gradingType;
      const matchesAggregationType =
        metadata.aggregationType === aggregationType;
      const matchesTerm = _.get(rating, "academicTerm.id") === academicTermId;

      let matchesGradeScale = true;
      if (gradingType === "GRADE_SCALE" && gradeScaleId) {
        matchesGradeScale =
          _.toString(metadata.gradeScaleId) === _.toString(gradeScaleId);
      }

      return (
        isAggregate &&
        matchesGradingType &&
        matchesAggregationType &&
        matchesTerm &&
        matchesGradeScale
      );
    }),
    key
  );
};

/**
 * Check if a V2 aggregation type is configured for a specific term.
 * Grade scales not in gradeScalesForAggregation always return false — even
 * for alpha+AVERAGE — so orphaned configs don't render aggregate cells.
 * Alpha grade scales with AVERAGE return true without needing a config entry.
 * Term matching is driven by isPerTermGradingTypeMap: per-term configs require
 * an exact term match, otherwise only the global (null-term) config is used.
 */
export const getIsV2AggregationConfiguredForTerm = ({
  aggregationConfigs,
  academicTermId,
  gradingType,
  gradeScaleId,
  aggregationType,
  isAlpha,
  scoreGradeAggregationSettings,
}) => {
  const { gradeScalesForAggregation, isPerTermGradingTypeMap } =
    scoreGradeAggregationSettings || {};

  if (
    gradingType === "GRADE_SCALE" &&
    gradeScaleId &&
    !_.includes(gradeScalesForAggregation, gradeScaleId)
  ) {
    return false;
  }

  if (isAlpha && aggregationType === "AVERAGE") {
    return true;
  }

  const perTermKey = gradingType === "GRADE_SCALE" ? gradeScaleId : gradingType;
  const isPerTerm = !!_.get(isPerTermGradingTypeMap, perTermKey, false);

  const matchingConfig = _.find(aggregationConfigs, config => {
    const matchesGradingType = config.gradingType === gradingType;

    let matchesGradeScale = true;
    if (gradingType === "GRADE_SCALE" && gradeScaleId) {
      matchesGradeScale =
        _.toString(_.get(config, "gradeScale.id")) === _.toString(gradeScaleId);
    }

    const configTermId = _.get(config, "academicTerm.id");
    const matchesTerm = isPerTerm
      ? configTermId === academicTermId
      : _.isNil(configTermId);

    return matchesGradingType && matchesGradeScale && matchesTerm;
  });

  return _.includes(
    _.get(matchingConfig, "aggregationTypes", []),
    aggregationType
  );
};

export const getIsProgressSummaryAppEnabled = curriculumProgramId =>
  getSettingValue({
    name: "FeatureFlag:EnableProgressSummaryApp",
    currentCurriculumProgramId: curriculumProgramId,
  });

export const getStudentRatingsRefetchQueryVariables = ({
  studentId,
  studentWiseRatingsVariables,
  excludedCriteriaTypes,
}) => {
  return {
    id: studentId,
    filtersForGeneric: {
      ..._.get(studentWiseRatingsVariables, "filtersForGeneric", {}),
      excludedCriteriaTypes,
    },
  };
};

export const writeFinalAndLocalGradeInCache = ({
  studentId,
  academicTermId,
  studentWiseRatings,
  updatedStudentRatings,
  studentWiseRatingsVariables,
}) => {
  let updatedStudentWiseRatingsData = _.cloneDeep(
    getStudentWiseRatingsFromCache({
      variables: studentWiseRatingsVariables,
    })
  );

  const courseStudentRatings = _.map(
    _.get(updatedStudentWiseRatingsData, "node.userMap.edges", []),
    "node.user"
  );

  const index = _.findIndex(
    courseStudentRatings,
    node => node?.id === studentId
  );

  const finalGradeRating = _.find(
    studentWiseRatings,
    rating =>
      rating.academicTerm?.id === academicTermId &&
      rating.academicCriteriaSet?.criteriaType === "FINAL_GRADE" &&
      _.isNil(rating.category?.id) &&
      rating.parent?.id == studentId &&
      rating.isComputedRating === true
  );

  const localGradeRating = _.find(
    studentWiseRatings,
    rating =>
      rating.academicTerm?.id === academicTermId &&
      rating.academicCriteriaSet?.criteriaType === "LOCAL_GRADE" &&
      _.isNil(rating.category?.id) &&
      rating.parent?.id == studentId &&
      rating.isComputedRating === true
  );

  if (_.isEmpty(finalGradeRating)) {
    const updatedFinalGradeRating = _.find(
      updatedStudentRatings,
      rating =>
        rating.academicTerm?.id === academicTermId &&
        rating.academicCriteriaSet?.criteriaType === "FINAL_GRADE" &&
        _.isNil(rating.category?.id) &&
        rating.parent?.id == studentId &&
        rating.isComputedRating === true
    );

    if (!_.isEmpty(updatedFinalGradeRating)) {
      updatedStudentWiseRatingsData = _.update(
        updatedStudentWiseRatingsData,
        `node.userMap.edges.${index}.node.user.ibPYPElementRatings`,
        ratings => {
          return [...ratings, updatedFinalGradeRating];
        }
      );
    }
  }

  if (_.isEmpty(localGradeRating)) {
    const updatedLocalGradeRating = _.find(
      updatedStudentRatings,
      rating =>
        rating.academicTerm?.id === academicTermId &&
        rating.academicCriteriaSet?.criteriaType === "LOCAL_GRADE" &&
        _.isNil(rating.category?.id) &&
        rating.parent?.id == studentId &&
        rating.isComputedRating === true
    );

    if (!_.isEmpty(updatedLocalGradeRating)) {
      updatedStudentWiseRatingsData = _.update(
        updatedStudentWiseRatingsData,
        `node.userMap.edges.${index}.node.user.ibPYPElementRatings`,
        ratings => {
          return [...ratings, updatedLocalGradeRating];
        }
      );
    }
  }

  writeStudentWiseRatingsToCache({
    variables: studentWiseRatingsVariables,
    data: updatedStudentWiseRatingsData,
  });
};

export const getStudentsDataMemoized = _.memoize(
  ({ courseData, curriculumType, shouldShowLevelsInGradingSetupV2 }) => {
    return _.map(_.get(courseData, "userMap.edges", []), ({ node }) => {
      const user = _.get(node, "user", {});
      const tags = _.get(node, "tags", []);
      const isArchived = getUserArchivalStatus(user);
      const isRemovedFromClass = _.get(node, "isRemovedFromClass", false);

      const tag =
        shouldShowLevelsInGradingSetupV2 || curriculumType == CURRICULUM_TYPE_DP
          ? _.first(tags)
          : null;

      return { ...user, tag, isArchived: isArchived || isRemovedFromClass };
    });
  },
  params => JSON.stringify(params)
);

export const getIsAssessmentGradebookDataGridEnabled = () =>
  ACLStore.can("FeatureFlag:EnableAssessmentGradebookDataGrid");

export const getSidePanelGradingHeader = ({
  currentHeader,
  gradingPeriods,
  categoryOptions,
}) => {
  const { columnKey, termId, categoryId, assignment, labelText } =
    currentHeader || {};

  switch (columnKey) {
    case CRITERION_A:
    case CRITERION_B:
    case CRITERION_C:
    case CRITERION_D:
    case FINAL_SCORE:
    case FINAL_GRADE:
    case LOCAL_GRADE:
    case SUBJECT_DESCRIPTION:
    case FINAL_COMMENT:
    case CRITERION_A_COMMENT:
    case CRITERION_B_COMMENT:
    case CRITERION_C_COMMENT:
    case CRITERION_D_COMMENT:
    case CRITERION_A_GRADE_SCALE:
    case CRITERION_B_GRADE_SCALE:
    case CRITERION_C_GRADE_SCALE:
    case CRITERION_D_GRADE_SCALE:
    case GRADE_SCALE: {
      const term = _.find(gradingPeriods, { id: termId });
      return `${_.get(term, "label")} - ${labelText}`;
    }
    case CATEGORY_TOTAL:
    case CATEGORY_GRADE_SCALE:
    case CATEGORY_GRADE: {
      const category = _.find(categoryOptions, { value: categoryId });
      return `${_.get(category, "label")} - ${labelText}`;
    }
    default: {
      return _.get(assignment, "content.title.value");
    }
  }
};

export const getPointSetVariables = ({
  mode,
  organizationId,
  categoryId,
  selectedSubject,
  gradeId,
  termId,
  selectedGradingPeriod,
  curriculumProgramId,
  curriculumProgramType,
}) => {
  const associatedEntites = [];

  switch (mode) {
    case "term":
      {
        if (selectedSubject) {
          associatedEntites.push({ ids: selectedSubject, type: "SUBJECT" });
        }

        if (gradeId) {
          associatedEntites.push({ ids: gradeId, type: "GRADE" });
        }

        const academicTermId = termId || selectedGradingPeriod;

        if (academicTermId) {
          associatedEntites.push({
            ids: academicTermId,
            type: "ACADEMIC_TERM",
          });
        }
      }
      break;
    case "category": {
      if (categoryId) {
        associatedEntites.push({
          ids: categoryId,
          type: "ACADEMIC_CRITERIA_VALUE",
        });
      }
    }
  }

  const pointSetType =
    curriculumProgramType === CURRICULUM_TYPE_UBD
      ? LOCAL_GRADE
      : "GRADE_BOUNDARY";

  return {
    id: organizationId,
    filters: {
      types: pointSetType,
      curriculumProgramIds: curriculumProgramId,
      associatedEntites,
    },
  };
};

const getRatingOptionsByEntityType = ({
  gradeId,
  categoryId,
  categoryOptions,
  isCurriculumMYP,
  isCurriculumDP,
  isCurriculumUBD,
  localGradeCriteriaSet,
  finalGradeCriteriaSet,
  mypIbDefinedAchievementCriteriaSet,
  termGradeBoundarySet,
  entityType,
  localGradeCalculationMethod,
  gradeScaleEntityDetails,
  mypIbDefinedGradeScale,
  isUsingNewGradingSetupInCourses,
  categoryGradeScaleEntityDetails,
}) => {
  const isCurriculumWithIBGrades = isCurriculumDP || isCurriculumMYP;
  const finalGradeSetCriteriaValues = _.get(
    finalGradeCriteriaSet,
    "academicCriteriaValues"
  );
  const localGradeCriteriaValues = _.get(
    localGradeCriteriaSet,
    "academicCriteriaValues"
  );

  const isLocalGradeCalculatedFromFinalGrade = _.includes(
    [FINAL_GRADE, "MYP_FINAL_GRADE"],
    localGradeCalculationMethod
  );

  switch (entityType) {
    case CRITERION_A:
    case CRITERION_B:
    case CRITERION_C:
    case CRITERION_D: {
      if (isUsingNewGradingSetupInCourses) {
        const gradeScaleEntityDetails = _.get(
          mypIbDefinedGradeScale,
          "gradeScaleEntityDetails.0"
        );

        return _.map(
          _.get(gradeScaleEntityDetails, "gradeScaleValues", []),
          gradeScaleValue => {
            return {
              ...gradeScaleValue,
              label: _.get(gradeScaleValue, "abbreviation", ""),
            };
          }
        );
      }

      return _.get(
        mypIbDefinedAchievementCriteriaSet,
        "academicCriteriaValues"
      );
    }
    case CATEGORY_GRADE: {
      const category = _.find(categoryOptions, { value: categoryId });
      const categoryGradeBoundary = _.get(category, "gradeBoundary.categories");

      if (isCurriculumUBD) {
        const gradeScaleType = _.get(category, "gradeScaleType");

        if (gradeScaleType === "CUSTOM") {
          return categoryGradeBoundary;
        } else {
          if (_.isEmpty(termGradeBoundarySet)) {
            return localGradeCriteriaValues;
          } else {
            return termGradeBoundarySet;
          }
        }
      }

      if (_.isEmpty(categoryGradeBoundary)) {
        return finalGradeSetCriteriaValues;
      }

      return categoryGradeBoundary;
    }
    case FINAL_GRADE: {
      return finalGradeSetCriteriaValues;
    }
    case LOCAL_GRADE: {
      if (isCurriculumUBD) {
        if (_.isEmpty(termGradeBoundarySet)) {
          return localGradeCriteriaValues;
        }

        return termGradeBoundarySet;
      }

      if (isCurriculumMYP && isLocalGradeCalculatedFromFinalGrade) {
        const localGradeAcademicCriteriaValues = getLocalGradeAcademicCriteriaValuesFromFinalGradeSet(
          {
            finalGradeCriteriaSet,
            gradeId,
            filterByGrade: true,
          }
        );

        return localGradeAcademicCriteriaValues;
      }

      return localGradeCriteriaValues;
    }
    case CRITERION_A_GRADE_SCALE:
    case CRITERION_B_GRADE_SCALE:
    case CRITERION_C_GRADE_SCALE:
    case CRITERION_D_GRADE_SCALE:
    case CATEGORY_GRADE_SCALE:
    case GRADE_SCALE: {
      return _.map(
        _.get(gradeScaleEntityDetails, "gradeScaleValues", []),
        gradeScaleValue => {
          return {
            ...gradeScaleValue,
            label: _.get(gradeScaleValue, "abbreviation", ""),
          };
        }
      );
    }
  }
};

export const getOrderedRatingOptionsByEntityType = params => {
  const { hasDashOption = true, entityType } = params || {};

  const sortOrder = _.includes([FINAL_GRADE, CATEGORY_GRADE], entityType)
    ? "desc"
    : "asc";

  const ratingOptions = getRatingOptionsByEntityType(params);
  const orderedRatingOptions = _.orderBy(
    ratingOptions,
    ["displaySequence", "label"],
    sortOrder
  );

  if (hasDashOption) {
    orderedRatingOptions.unshift({
      id: "-",
      label: "-",
      displaySequence: _.isEqual(entityType, LOCAL_GRADE) ? Infinity : -1,
    });
  }

  return orderedRatingOptions;
};

export const getRatingValueDataByEntityType = ({
  categoryId,
  isTOK,
  courseStudentRatings,
  termGradeStudentWiseCriteria,
  studentId,
  criteriaNodes,
  termId,
  entityType,
  mypIbDefinedAchievementCriteriaSet,
  isCurriculumDP,
  isCurriculumMYP,
  isOverallScoreEnabled,
  finalGradeCalculationMethod,
  curriculumProgramType,
  isCumulativePeriod,
  selectedGradingPeriod,
  categoryTotalDisplayType,
  cumulativeAssociatedTermTotalDisplayType,
  achievementLevelCriteriaSetId: achievementLevelCriteriaSetIdParam,
  academicCriteriaSet,
  finalGradeCalculationMethodology,
  isUsingNewGradingSetupInCourses,
}) => {
  const studentWiseRatings = _.get(
    _.find(courseStudentRatings, node => node?.id === studentId),
    "ibPYPElementRatings",
    []
  );

  let value,
    currentValue,
    achievementLevelCriteriaSetId,
    isOverRidingValue = false;

  const defaultValue = isUsingNewGradingSetupInCourses ? undefined : "-";

  switch (entityType) {
    case CRITERION_A:
    case CRITERION_B:
    case CRITERION_C:
    case CRITERION_D:
    case CRITERION_A_COMMENT:
    case CRITERION_B_COMMENT:
    case CRITERION_C_COMMENT:
    case CRITERION_D_COMMENT:
    case CRITERION_A_GRADE_SCALE:
    case CRITERION_B_GRADE_SCALE:
    case CRITERION_C_GRADE_SCALE:
    case CRITERION_D_GRADE_SCALE:
      {
        const mypIbDefinedAchievementCriteriaSetId = _.get(
          mypIbDefinedAchievementCriteriaSet,
          "id"
        );

        const { id: yearLevelCriteriaId } = getYearLevelCriteriaSetForStudent({
          termGradeStudentWiseCriteria,
          studentId,
          termId,
        });

        const achievementLevelCriteriaSet = getAchievementLevelCriteriaSet({
          criteriaNodes,
          yearLevelCriteriaId,
          criteriaLabel: _.split(entityType, "_")[1],
        });
        achievementLevelCriteriaSetId =
          achievementLevelCriteriaSetIdParam ||
          _.get(achievementLevelCriteriaSet, "id");

        if (
          _.includes(
            [
              ...TERM_LEVEL_CRITERION_COLUMNS,
              ...TERM_LEVEL_CRITERION_GRADE_SCALE_COLUMNS,
            ],
            entityType
          )
        ) {
          const criteriaValue = getMYPTermLevelCriteriaValue({
            studentWiseRatings,
            termId,
            achievementLevelCriteriaSetId,
            mypIbDefinedAchievementCriteriaSetId,
            academicCriteriaSet,
            isUsingNewGradingSetupInCourses,
          });

          currentValue = criteriaValue || defaultValue;
          value = defaultValue;
        }
      }
      break;
    case FINAL_SCORE:
    case CATEGORY_TOTAL: {
      const {
        isRemoved,
        overallScore,
        computedScore,
        editedScore,
      } = getOverallScoreValue({
        termId,
        categoryId,
        studentWiseRatings,
        isCumulativePeriod,
        selectedGradingPeriod,
        categoryTotalDisplayType,
        overallScoreType: entityType,
        cumulativeAssociatedTermTotalDisplayType,
      });

      const showIndicationForOverridenScore = getShowIndicationForOverriddenScore(
        {
          editedScore,
          isRemoved,
          computedScore,
        }
      );

      currentValue = overallScore;
      value = computedScore;
      isOverRidingValue = showIndicationForOverridenScore;

      break;
    }
    case FINAL_GRADE:
    case LOCAL_GRADE:
    case CATEGORY_GRADE:
    case GRADE_SCALE:
    case CATEGORY_GRADE_SCALE:
      {
        const entityId = entityType === CATEGORY_GRADE ? categoryId : termId;

        const {
          isRemoved,
          overallGrade,
          editedOverallGrade,
          computedOverallGrade,
        } = getOverallGradeValue({
          isTOK,
          studentWiseRatings,
          entityId,
          overallGradeType: entityType,
          isCurriculumDP,
          isCurriculumMYP,
          termId,
          categoryId,
          academicCriteriaSet,
        });

        const showIndicationForOverridenGrade = getShowIndicationForOverriddenGrade(
          {
            isRemoved,
            editedOverallGrade,
            computedOverallGrade,
            isOverallScoreEnabled,
            finalGradeCalculationMethod,
            overallGradeType: entityType,
            curriculumProgramType,
            finalGradeCalculationMethodology,
          }
        );

        currentValue = overallGrade || defaultValue;
        value = computedOverallGrade || defaultValue;
        isOverRidingValue = showIndicationForOverridenGrade;
      }
      break;
  }

  return {
    value,
    currentValue,
    isOverRidingValue,
    achievementLevelCriteriaSetId,
  };
};

export const getIbPYPElementRatings = (ibPYPElementRatingsData = []) => {
  const ibPYPElementRatings = {};
  _.forEach(ibPYPElementRatingsData, rating => {
    const toolType = _.get(rating, "toolType");

    const ratingData = {
      academicCriteriaValueId: _.get(rating, "academicCriteriaValue.id", null),
      academicCriteriaSetId: _.get(rating, "academicCriteriaSet.id", null),
      ibPYPElementId: _.get(rating, "ibPYPElementId", null),
      ibPYPElementType: _.get(rating, "ibPYPElementType"),
      academicTermId: _.get(rating, "academicTermId", null),
      learningCourseId: _.get(rating, "learningCourseId"),
      subjectId: _.get(rating, "subjectId"),
    };

    if (_.has(rating, "value")) {
      const value = _.get(rating, "value");
      _.set(ratingData, "value", value);
    }

    if (_.has(ibPYPElementRatings, toolType)) {
      ibPYPElementRatings[toolType].push(ratingData);
    } else {
      _.set(ibPYPElementRatings, toolType, [ratingData]);
    }
  });

  return ibPYPElementRatings;
};

export const getElementRemark = (elementRemarksResponses = []) => {
  const elementRemark = _.first(elementRemarksResponses);

  return {
    message: _.get(elementRemark, "message", ""),
  };
};

export const getAssessmentToolResponse = (toolResponses = []) => {
  return _.map(toolResponses, toolReponse => ({
    rowId: _.get(toolReponse, "rowId"),
    columnId: _.get(toolReponse, "columnId"),
    subjectiveResponse: _.get(toolReponse, "subjectiveResponse", ""),
    studentAssessmentEvaluationId: 123,
  }));
};

export const getToolGradeResponses = ({
  assessmentTool,
  ibPYPElementRatings,
  isOverride,
}) => {
  let assessmentToolResponse, ibPYPElementRatingsResponses, elementRemark;

  const toolType = _.get(assessmentTool, "toolType");

  const toolReponses = _.get(assessmentTool, "toolResponses", []);
  if (!_.isEmpty(toolReponses)) {
    assessmentToolResponse = getAssessmentToolResponse(toolReponses);
  } else if (isOverride) {
    assessmentToolResponse = [];
  }

  const isScoreBasedOrStandardBasedRubric = [
    SCORE_BASED_RUBRIC,
    STANDARD_BASED_RUBRIC,
  ].includes(toolType);

  const ibPYPElementRatingsData = isScoreBasedOrStandardBasedRubric
    ? [
        ..._.get(ibPYPElementRatings, SCORE, []), // if score is overridden then data will be here for score
        ..._.get(ibPYPElementRatings, toolType, []),
      ]
    : _.get(ibPYPElementRatings, toolType, []);

  if (!_.isEmpty(ibPYPElementRatingsData)) {
    if (toolType === GRADE_SCALE_TOOL && isOverride) {
      ibPYPElementRatingsResponses = _.map(ibPYPElementRatingsData, rating => ({
        ...rating,
        metadata: { is_overridden: true },
      }));
    } else {
      ibPYPElementRatingsResponses = ibPYPElementRatingsData;
    }
  } else if (isOverride) {
    ibPYPElementRatingsResponses = [];
  }

  const elementRemarksResponses = _.get(assessmentTool, "elementRemarks", []);
  if (!_.isEmpty(elementRemarksResponses)) {
    elementRemark = getElementRemark(elementRemarksResponses);
  } else if (isOverride) {
    elementRemark = {};
  }

  return {
    assessmentToolResponse,
    ibPYPElementRatingsResponses,
    elementRemark,
  };
};

export const getBulkGradingMutationVariables = ({
  assessmentToolsData,
  ibPYPElementRatingsData,
  assignmentId,
  isOverride,
  createdBy,
  creatorType,
  academicTermId,
}) => {
  const commonVariables = {
    assignmentId,
    isOverride,
    source: "GRADEBOOK",
    createdBy,
    creatorType,
    isPublished: false,
    academicTermId,
  };

  const ibPYPElementRatings = getIbPYPElementRatings(ibPYPElementRatingsData);

  const bulkAssignmentGradeRatingsVariables = _.map(
    assessmentToolsData,
    assessmentTool => {
      const toolType = _.get(assessmentTool, "toolType");

      const variable = {
        ...commonVariables,
        assessmentToolType: toolType,
        assessmentToolId: _.get(assessmentTool, "toolId"),
      };

      const {
        assessmentToolResponse,
        ibPYPElementRatingsResponses,
        elementRemark,
      } = getToolGradeResponses({
        assessmentTool,
        ibPYPElementRatings,
        isOverride,
      });

      if (!_.isEmpty(assessmentToolResponse)) {
        _.set(variable, "assessmentToolResponse", assessmentToolResponse);
      }

      if (!_.isEmpty(ibPYPElementRatingsResponses)) {
        _.set(variable, "ibPYPElementRatings", ibPYPElementRatingsResponses);
      }

      if (!_.isEmpty(elementRemark)) {
        _.set(variable, "elementRemarks", elementRemark);
      }

      return variable;
    }
  );

  const filteredBulkAssignmentGradeRatingsVariables = _.filter(
    bulkAssignmentGradeRatingsVariables,
    variable => {
      return !(
        _.isEmpty(_.get(variable, "elementRemarks"), {}) &&
        _.isEmpty(_.get(variable, "ibPYPElementRatings"), []) &&
        _.isEmpty(_.get(variable, "assessmentToolResponse"), [])
      );
    }
  );

  return {
    ratings: filteredBulkAssignmentGradeRatingsVariables,
  };
};

export const getGradingOptions = ({
  t,
  disabled = false,
  isDisabledWithLockedTermCheck = false,
  keyPrefix = "",
  hideSort = false,
  hideBulkGrading = false,
  hideBulkEvaluation = false,
  sortOrder = "",
}) => {
  const options = [
    {
      key: _.join(_.compact([keyPrefix, "SORT"]), "-"),
      label: t("common:sort"),
      isSubMenu: true,
      icon: <UpDownArrowOutlined size="xxx-small" />,
      options: [
        {
          key: ASC,
          label: t("classRoom:ascending"),
          disabled: _.isEqual(sortOrder, ASC),
        },
        {
          key: DESC,
          label: t("classRoom:descending"),
          disabled: _.isEqual(sortOrder, DESC),
        },
      ],
      isVisible: !hideSort,
    },
    {
      key: _.join(_.compact([keyPrefix, BULK_GRADE]), "-"),
      label: t("classRoom:assign_same_grade_to_all_students"),
      icon: <MultipleUsersOutlined variant="subtle" />,
      disabled: isDisabledWithLockedTermCheck,
      isVisible: !hideBulkGrading,
    },
    {
      key: _.join(_.compact([keyPrefix, BULK_EVALUATION]), "-"),
      label: t("classRoom:open_quick_evaluation_modal"),
      icon: <ChecklistOutlined variant="subtle" />,
      disabled,
      isVisible: !hideBulkEvaluation,
    },
  ];

  return _.filter(options, { isVisible: true });
};

export const getSortedAssignedStudents = ({
  selectedStudents,
  filterConfig,
  getSortedStudents,
  assignedStudents,
}) => {
  const sortedStudents = getStudentsByFilterConfig({
    selectedStudents,
    filterConfig,
    getSortedStudents,
    allStudents: assignedStudents,
  });

  const sortedAssignedStudents = [];

  _.forEach(sortedStudents, student => {
    const assignedStudent = _.find(
      assignedStudents,
      currAssignedStudent =>
        _.get(currAssignedStudent, "student.id") === _.get(student, "id")
    );

    if (assignedStudent) {
      sortedAssignedStudents.push(assignedStudent);
    }
  });

  return sortedAssignedStudents;
};

export const getBulkActionParams = ({
  assessmentToolType = "",
  assignment = {},
  entityType = "",
}) => {
  let assessmentToolId = "";

  if (_.isEqual(assessmentToolType, "LEARNING_GOALS")) {
    assessmentToolId = "learningGoals";
  } else {
    const assessmentTools = _.get(assignment, "content.assessmentTools.edges");
    const selectedAssessmentTool = _.find(
      assessmentTools,
      tool => _.get(tool, "node.assessmentToolType") === assessmentToolType
    );
    assessmentToolId = _.get(
      selectedAssessmentTool,
      "node.assessmentToolItem.id"
    );
  }

  const isAssessmentTool =
    _.has(ASSESSMENT_TOOLS_OBJ, entityType) ||
    _.isEqual(entityType, "LEARNING_GOALS");

  const canTriggerAction =
    (!_.isEmpty(assignment) &&
      !_.isEmpty(assessmentToolId) &&
      !_.isEmpty(assessmentToolType)) ||
    !_.isEmpty(entityType);

  return {
    canTriggerAction,
    assignment,
    assignmentId: _.get(assignment, "id"),
    assessmentToolId,
    assessmentToolType,
    isAssessmentTool,
  };
};

export const getAcademicCriteriaSetDataForSidePanelGrading = ({
  columnKey,
  mypIbDefinedAchievementCriteriaSet,
  mypIbDefinedGradeScale,
  isUsingNewGradingSetupInCourses,
  finalGradeCriteriaSet,
  localGradeCriteriaSet,
  curriculumProgramType,
  academicCriteriaSet,
}) => {
  switch (columnKey) {
    case CRITERION_A:
    case CRITERION_B:
    case CRITERION_C:
    case CRITERION_D: {
      return isUsingNewGradingSetupInCourses
        ? mypIbDefinedGradeScale
        : mypIbDefinedAchievementCriteriaSet;
    }
    case LOCAL_GRADE: {
      return localGradeCriteriaSet;
    }
    case CATEGORY_GRADE_SCALE:
    case GRADE_SCALE:
    case CRITERION_A_GRADE_SCALE:
    case CRITERION_B_GRADE_SCALE:
    case CRITERION_C_GRADE_SCALE:
    case CRITERION_D_GRADE_SCALE: {
      return academicCriteriaSet;
    }
    default: {
      if (curriculumProgramType === CURRICULUM_TYPE_UBD) {
        return localGradeCriteriaSet;
      }

      return finalGradeCriteriaSet;
    }
  }
};

export const getAcademicCriteriaSets = ({
  academicCriteriaSets,
  curriculumProgramId,
  curriculumType,
  isTOK,
}) => {
  const finalScoreCriteriaSet = _.find(academicCriteriaSets, {
    criteriaType: FINAL_SCORE,
  });

  const localGradeCriteriaSet = _.find(
    academicCriteriaSets,
    ({ criteriaType, subType }) => {
      let condition = criteriaType === LOCAL_GRADE;

      if (curriculumType === CURRICULUM_TYPE_UBD) {
        condition = condition && subType === "DEFAULT";
      }

      return condition;
    }
  );

  const finalGradeCriteriaSet = _.find(
    academicCriteriaSets,
    ({ criteriaType, subType }) => {
      const subTypeCheck =
        curriculumType === CURRICULUM_TYPE_PYP ||
        (isTOK && subType === "TOK") ||
        (!isTOK && subType === SUBJECT);

      return criteriaType === FINAL_GRADE && subTypeCheck;
    }
  );

  const mypIbDefinedAchievementCriteriaSet = getMypIbDefinedCriteriaSet({
    academicCriteriaSets,
    curriculumProgramId,
  });

  return {
    finalScoreCriteriaSet,
    finalGradeCriteriaSet,
    localGradeCriteriaSet,
    mypIbDefinedAchievementCriteriaSet,
  };
};

export const getCategoriesForTerm = ({ categoryOptions, termId }) => {
  const groupedByTermId = _.groupBy(categoryOptions, item =>
    _.get(item, "termId", "null")
  );

  return _.get(groupedByTermId, termId, []);
};

export const getCategoryHeadersObjectKey = ({
  category,
  isUsingNewGradingSetupInCourses,
}) => {
  if (isUsingNewGradingSetupInCourses) {
    return `${category?.gradingCategoryValueId}-${category?.termId}`;
  }

  return category?.value;
};

export const calculateCategoriesLeafCount = ({
  categoryOptions,
  categoryHeadersObject,
  termId,
  isCumulativePeriod,
  showCategories,
  isAssessmentGradebookHeaderEnhancements,
  isUsingNewGradingSetupInCourses,
  cumulativeTotalCalculationMethod,
}) => {
  const categories = getCategoriesForTerm({
    termId,
    categoryOptions,
  });

  const isCategoriesShownUnderTermHeader =
    isCumulativePeriod &&
    (!isUsingNewGradingSetupInCourses ||
      cumulativeTotalCalculationMethod !== "FROM_ASSIGNMENTS");

  if (
    !isCategoriesShownUnderTermHeader ||
    (!isAssessmentGradebookHeaderEnhancements && !showCategories) ||
    _.isEmpty(categories)
  ) {
    return 0;
  }

  return categories.reduce((total, category) => {
    const categoryHeadersObjKey = getCategoryHeadersObjectKey({
      category,
      isUsingNewGradingSetupInCourses,
    });

    const leafCount = _.get(
      categoryHeadersObject,
      `${categoryHeadersObjKey}.categoryLeafColumnsCount`,
      0
    );
    return total + leafCount;
  }, 0);
};

export const getTermsBySelectedGradingPeriod = ({
  gradingPeriods = [],
  selectedGradingPeriod,
  isUsingNewGradingSetupInCourses,
  cumulativeTermWeights,
}) => {
  const selectedGradingPeriodObj = _.find(
    gradingPeriods,
    period => period?.id === selectedGradingPeriod
  );

  return isUsingNewGradingSetupInCourses
    ? getTermsV2({
        selectedGradingPeriodObj,
        gradingPeriods,
        cumulativeTermWeights,
      })
    : getTerms({
        selectedGradingPeriodObj,
        gradingPeriods,
      });
};

export const getRatingInputByEntityType = params => {
  const {
    courseId,
    studentId,
    categoryId,
    selectedSubject,
    academicTermId,
    academicCriteriaSetId,
    academicCriteriaValueId,
    studentAssignment,
    entityType,
    ibPYPElementType: _ibPYPElementType,
    achievementLevelCriteriaSetId,
    criteriaId,
    value,
    createdBy,
    emptyValue,
    shouldDeleteRating,
    isCourseBasedGrading,
    learningCourseId,
    isUsingNewGradingSetupInCourses,
    students = [],
    curriculumType,
    ibPYPElementId,
    assignment,
    toolResponsesData,
  } = params;

  switch (entityType) {
    case SCORE:
      return {
        studentId,
        parentId: _.get(studentAssignment, "id"),
        parentType: "STUDENT_ASSIGNMENT",
        ibPYPElementType: SCORE,
        isPublished: _.get(studentAssignment, "isEvaluated"),
        academicCriteriaSetId: "-9999",
        academicCriteriaValueId: "-9999",
        academicTermId,
        value,
        createdBy,
        courseId,
        source: "GRADEBOOK",
      };
    case COMMENT:
      return {
        studentId,
        parentId: _.get(studentAssignment, "id"),
        parentType: "STUDENT_ASSIGNMENT",
        ibPYPElementType: "STUDENT_ASSIGNMENT",
        value,
        courseId,
        createdBy,
        source: "GRADEBOOK",
      };
    case ASSIGNMENT_CRITERION_A:
    case ASSIGNMENT_CRITERION_B:
    case ASSIGNMENT_CRITERION_C:
    case ASSIGNMENT_CRITERION_D:
      if (shouldDeleteRating) {
        return {
          studentId,
          parentId: _.get(studentAssignment, "id"),
          parentType: "STUDENT_ASSIGNMENT",
          academicCriteriaSetId,
          ibPYPElementId: criteriaId,
          ibPYPElementType: "MYP_OBJECTIVES",
          academicTermId,
        };
      }

      return {
        courseId,
        studentId,
        parentId: _.get(studentAssignment, "id"),
        parentType: "STUDENT_ASSIGNMENT",
        academicCriteriaValueId,
        academicCriteriaSetId,
        ibPYPElementId: criteriaId,
        ibPYPElementType: "MYP_OBJECTIVES",
        academicTermId,
        createdBy,
        source: "GRADEBOOK",
      };
    case ASSESSMENT_GRADE_BOUNDARY:
      if (shouldDeleteRating) {
        return {
          studentId,
          parentId: _.get(studentAssignment, "id"),
          parentType: "STUDENT_ASSIGNMENT",
          academicCriteriaSetId,
          ibPYPElementId: academicCriteriaSetId,
          ibPYPElementType: ASSESSMENT_GRADE_BOUNDARY,
          academicTermId,
        };
      }

      return {
        courseId,
        studentId,
        parentId: _.get(studentAssignment, "id"),
        parentType: "STUDENT_ASSIGNMENT",
        academicCriteriaValueId,
        academicCriteriaSetId,
        ibPYPElementId: academicCriteriaSetId,
        ibPYPElementType: ASSESSMENT_GRADE_BOUNDARY,
        academicTermId,
        value,
        createdBy,
        source: "GRADEBOOK",
      };

    case GRADE_SCALE_TOOL: {
      if (shouldDeleteRating) {
        return {
          studentId,
          parentId: _.get(studentAssignment, "id"),
          parentType: "STUDENT_ASSIGNMENT",
          academicCriteriaSetId,
          ibPYPElementId,
          ibPYPElementType: GRADE_SCALE_TOOL,
          academicTermId,
        };
      }

      let metadata = null;
      if (assignment && toolResponsesData) {
        const assessmentTools = _.get(
          assignment,
          "content.assessmentTools.edges",
          []
        );

        const _ibPYPElementRatings = _.get(
          studentAssignment,
          "ibPYPElementRatings",
          []
        );

        // Calculate the expected rating
        const calculatedRating = calculateGradeScaleRatingFromOtherTool({
          toolResponses: toolResponsesData,
          assessmentTools,
          score: _.find(_ibPYPElementRatings, {
            ibPYPElementType: SCORE,
            creatorType: "STAFF",
          })?.value,
        });

        if (
          calculatedRating &&
          calculatedRating.id !== academicCriteriaValueId
        ) {
          metadata = { is_overridden: true };
        }
      }

      const ratingInput = {
        courseId,
        studentId,
        parentId: _.get(studentAssignment, "id"),
        parentType: "STUDENT_ASSIGNMENT",
        academicCriteriaValueId,
        academicCriteriaSetId,
        ibPYPElementId,
        ibPYPElementType: GRADE_SCALE_TOOL,
        academicTermId,
        value,
        createdBy,
        source: "GRADEBOOK",
        isPublished: _.get(studentAssignment, "isEvaluated"),
        metadata,
      };

      return ratingInput;
    }

    case SUBJECT_DESCRIPTION:
    case FINAL_COMMENT:
    case CRITERION_A_COMMENT:
    case CRITERION_B_COMMENT:
    case CRITERION_C_COMMENT:
    case CRITERION_D_COMMENT: {
      const isMYPCriteria = _.includes(
        TERM_LEVEL_CRITERION_COMMENTS_COLUMNS,
        entityType
      );

      const ibPYPElementId = isMYPCriteria
        ? achievementLevelCriteriaSetId
        : selectedSubject;

      const ibPYPElementType = isMYPCriteria
        ? ELEMENT_TYPE_MYP_OBJECTIVES
        : _ibPYPElementType;

      const parentId = studentId;
      const parentType = "STUDENT";

      const remarkType =
        entityType === SUBJECT_DESCRIPTION ? "DESCRIPTION" : "COMMENT";

      let genericTagId;
      if (curriculumType === CURRICULUM_TYPE_DP) {
        const student = _.find(students, { id: studentId });
        genericTagId = _.get(student, "tag.id");
      }

      return {
        courseId,
        studentId,
        parentId,
        parentType,
        ibPYPElementId,
        ibPYPElementType,
        academicTermId,
        createdBy,
        source: "GRADEBOOK",
        message: value,
        remarkType,
        genericTagId,
      };
    }

    default: {
      const isRemoved =
        !isUsingNewGradingSetupInCourses && value === emptyValue;
      let criteriaValueId = academicCriteriaValueId;

      if (isRemoved) {
        criteriaValueId = null;
      }

      const isMYPCriteria = _.includes(
        TERM_LEVEL_CRITERION_COLUMNS,
        entityType
      );
      const isMypCriteriaGradeScale = _.includes(
        TERM_LEVEL_CRITERION_GRADE_SCALE_COLUMNS,
        entityType
      );

      const elementId = isCourseBasedGrading
        ? learningCourseId
        : selectedSubject;

      const ibPYPElementId =
        isMYPCriteria || isMypCriteriaGradeScale
          ? achievementLevelCriteriaSetId
          : elementId;

      const ibPYPElementType =
        isMYPCriteria || isMypCriteriaGradeScale
          ? ELEMENT_TYPE_MYP_OBJECTIVES
          : _ibPYPElementType;

      const parentId = studentId;
      const parentType = "STUDENT";

      if (shouldDeleteRating) {
        const categoryKey = isUsingNewGradingSetupInCourses
          ? "globalCategoryId"
          : "categoryId";

        return {
          studentId,
          parentId,
          parentType,
          ibPYPElementId,
          ibPYPElementType,
          academicTermId,
          academicCriteriaSetId,
          [categoryKey]: categoryId || null,
        };
      }

      const ratingInput = {
        courseId,
        studentId,
        parentId,
        parentType,
        ibPYPElementId,
        ibPYPElementType,
        academicTermId,
        learningCourseId: isCourseBasedGrading ? learningCourseId : undefined,
        academicCriteriaSetId: academicCriteriaSetId,
        academicCriteriaValueId: criteriaValueId,
        isRemoved: isRemoved,
        subjectId: selectedSubject,
        isPublished: true,
        value,
        createdBy,
        source: "GRADEBOOK",
      };

      if (categoryId) {
        const categoryKey = isUsingNewGradingSetupInCourses
          ? "globalCategoryId"
          : "categoryId";
        ratingInput[categoryKey] = categoryId;
      }

      return ratingInput;
    }
  }
};

// this function parses the overall score to number, if the overall score is empty or if it's not a string/number then it returns an empty string
// this unifies the empty value to an empty string in all cases, and returns a number in other cases
export const parseOverallScore = overallScore => {
  if (typeof overallScore !== "string" && typeof overallScore !== "number") {
    return "";
  }

  return overallScore === "" ? overallScore : Number(overallScore);
};

export const getIsAssessmentGradebookHeaderEnhancements = () => {
  return (
    getIsAssessmentGradebookDataGridEnabled() &&
    ACLStore.can("FeatureFlag:AssessmentGradebookHeaderEnhancements")
  );
};

// * creating category options from graphql response
export const getCategoryOptions = ({
  organizationPointSetData = {},
  isCurriculumDP,
  conditionalPercentage,
  isTOK,
  subjectLevelGenericTags,
}) => {
  const organizationPointSet = _.get(
    organizationPointSetData,
    "pointAssessmentSets.edges",
    []
  );

  if (_.isEmpty(organizationPointSet)) {
    return [];
  }

  const categoryOptions = [];

  _.forEach(organizationPointSet, ({ node }) => {
    const categories = _.get(node, "categories", []);
    const associatedEntities = _.get(node, "associatedEntities", []);
    const isShowGradeBoundaries = _.get(node, "isShowGradeBoundaries", false);

    const associatedSubjectId = _.get(
      _.find(associatedEntities, associatedEntity => {
        return _.get(associatedEntity, "__typename") === "Subject";
      }),
      "id"
    );
    const associatedTermId = _.get(
      _.find(associatedEntities, associatedEntity => {
        return _.get(associatedEntity, "__typename") === "AcademicTerm";
      }),
      "id"
    );

    _.forEach(categories, category => {
      const {
        id,
        label,
        values,
        gradeBoundary,
        gradeScaleType,
        displaySequence,
      } = category || {};

      const orderedScoreValues = _.orderBy(values, "genericTag.label", "desc");

      const subText = getCategorySubtext({
        orderedScoreValues,
        isCurriculumDP,
        conditionalPercentage,
        isTOK,
      });

      const hasEvaluatedButNotSharedAssignemnts =
        _.get(category, "assignments.totalCount", 0) > 0;

      categoryOptions.push({
        value: id,
        label,
        subText,
        scoreValues: orderedScoreValues,
        isShowGradeBoundaries,
        gradeBoundary,
        hasGradeBoundary:
          !_.isEmpty(gradeScaleType) || !_.isEmpty(gradeBoundary),
        associatedEntities,
        displaySequence,
        gradeScaleType,
        subjectId: associatedSubjectId,
        termId: associatedTermId,
        hasEvaluatedButNotSharedAssignemnts,
      });
    });
  });

  const categoryOptionsWithPercentageValues = getCategoriesWithPercentageValues(
    {
      categories: categoryOptions,
      subjectLevelGenericTags,
      categoryValuePath: "scoreValues",
    }
  );

  const sortedCategoryOptions = _.orderBy(
    categoryOptionsWithPercentageValues,
    "displaySequence",
    "asc"
  );

  return sortedCategoryOptions;
};

const getTotalImpactAlertTooltip = ({
  t,
  isOverallTotalsEnableForStudents = false,
  isOverallTotalsEnableForFamily = false,
  isOverallScoreEnabled = false,
}) => {
  if (
    !isOverallScoreEnabled ||
    (!isOverallTotalsEnableForStudents && !isOverallTotalsEnableForFamily)
  )
    return null;

  let persona;
  switch (true) {
    case isOverallTotalsEnableForStudents && isOverallTotalsEnableForFamily:
      persona = t("classRoom:students_and_family_members");
      break;

    case !isOverallTotalsEnableForStudents && isOverallTotalsEnableForFamily:
      persona = t("common:family_member_plural");
      break;

    case isOverallTotalsEnableForStudents && !isOverallTotalsEnableForFamily:
      persona = t("common:students");
      break;
  }

  return (
    <Tooltip
      tooltip={t("classRoom:alert_hidden_grades_impacting_totals_tooltip", {
        persona,
      })}
      overlayInnerStyle={styles.infoTooltipStyle}
      dsVersion="2.0"
      showArrow={true}
    >
      <InformationOutlined size="xxx-small" style={styles.infoIconStyle} />
    </Tooltip>
  );
};

export const getAssessmentGradebookHeaderParams = params => {
  const {
    t,
    props,
    filterConfig,
    updatedAssignments,
    studentDropdownOptions,
    handleBulkGradingParams,
    selectedGradingPeriodTerms,
    handleStudentDropdownClick,
    handleBulkToolEvaluationParams,
    getDataGridDropdownPopupContainer,
    dvnScrollerRef,
    sortColumn,
    isCategoriesEnabled,
    shouldGroupByCategories,
    courseLevels = [],
  } = params;

  const {
    isCategoriesView,
    assessmentGradebookSettings,
    categoryOptions,
    isCumulativePeriod,
    showCategories,
    selectedGradingPeriod,
    curriculumType,
    getCriteriaSetOfPypType,
    selectedAssessmentTools,
    selectedAcademicYears,
    defaultStudentNameOrderPreference,
    mypIbDefinedAchievementCriteriaSet,
    criteriaNodes,
    interdisciplinaryCriteriaNodes,
    isCustomWeights,
    isCurrentAcademicYearSelected,
    isArchivedClass,
    studentWiseRatingsVariables,
    isAdminGradebook,
    userId,
    students,
    onClickAssessment,
    doesTeacherHaveEditAccessToOtherClassTasks,
    selectedSubject,
    isCurriculumDP,
    isCurriculumMYP,
    isTOK,
    isOverallScoreEnabled,
    categoryTotalDisplayType,
    finalGradeCalculationMethod,
    curriculumProgramId,
    hasNextPage,
    isCurriculumUBD,
    isOverallGradeEnabled,
    isWeightageForCategoryEnabled,
    gradeCalculationMethod,
    canOverrideFinalGrade,
    organizationId,
    subjectLevelGenericTags,
    isAllSubjectsSelected,
    finalScoreCriteriaSet,
    finalGradeCriteriaSet,
    termGradeBoundarySet,
    localGradeCriteriaSet,
    filters,
    localGradeCalculationMethod,
    cumulativeAssociatedTermTotalDisplayType,
    showTermTotalColumns,
    isLocalGradeCalculationEnabled,
    selectedGradingPeriodLabel,
    isFinalGradeManuallyAssigned,
    isAssociatedTermsWeightageDefined,
    gradeId,
    subjects,
    displaySettings,
    hasCategories,
    isRnPEnabled,
    canManageGradebook,
    courseId,
    isAssessmentGradebookHeaderEnhancements,
    course,
    evaluatedButNotSharedAssignmentsCountByTermMap,
    isOverallTotalsEnableForStudents,
    isOverallTotalsEnableForFamily,
    skipCountOfEvaluatedButNotSharedAssignments,
    gradingPeriodsStatusMap,
    differentWeightsForLevels,
    isCourseBasedGrading,
    gradeScales,
    isUsingNewGradingSetupInCourses,
    isSubjectCommentsSupportOnGradebookEnabled,
    learningCourseId,
    cumulativeTotalCalculationMethod,
    mypIbDefinedGradeScale,
    mypCriteriaTotalGradeScale,
    gradingMethodologyTypes,
    gradingPeriods,
    hiddenGradebookEntities,
    enableCategoryGrade,
    isCategoryGradeScalesEnabled,
    differentCategoriesForGradingPeriods,
    mypCriteriaRegularGradeScales,
    savedTermColumnsOrder,
    isCommonDisplaySettingsEnabled,
  } = props;

  const studentIds = _.map(students, "id");

  const totalImpactAlertTooltip = getTotalImpactAlertTooltip({
    t,
    isOverallTotalsEnableForFamily,
    isOverallTotalsEnableForStudents,
    isOverallScoreEnabled,
  });

  const highestAlertTypeCount = _.chain(students)
    .map(student => _.size(student.alertTypes))
    .max()
    .value();

  return {
    t,
    isCategoriesView,
    assessmentGradebookSettings,
    categoryOptions,
    isCumulativePeriod,
    showCategories,
    selectedGradingPeriodTerms,
    selectedGradingPeriod,
    assignments: updatedAssignments,
    handleStudentDropdownClick,
    studentDropdownOptions,
    filterConfig,
    handleBulkGradingParams,
    handleBulkToolEvaluationParams,
    getDataGridDropdownPopupContainer,
    curriculumType,
    getCriteriaSetOfPypType,
    selectedAssessmentTools,
    selectedAcademicYears,
    defaultStudentNameOrderPreference,
    mypIbDefinedAchievementCriteriaSet,
    criteriaNodes,
    interdisciplinaryCriteriaNodes,
    differentWeightsForLevels,
    isCustomWeights,
    isCurrentAcademicYearSelected,
    isArchivedClass,
    studentWiseRatingsVariables,
    isAdminGradebook,
    userId,
    students,
    onClickAssessment,
    doesTeacherHaveEditAccessToOtherClassTasks,
    selectedSubject,
    isCurriculumDP,
    isCurriculumMYP,
    isTOK,
    isOverallScoreEnabled,
    categoryTotalDisplayType,
    finalGradeCalculationMethod,
    curriculumProgramId,
    hasNextPage,
    isCurriculumUBD,
    isOverallGradeEnabled,
    isWeightageForCategoryEnabled,
    gradeCalculationMethod,
    canOverrideFinalGrade,
    organizationId,
    subjectLevelGenericTags,
    isAllSubjectsSelected,
    finalScoreCriteriaSet,
    finalGradeCriteriaSet,
    termGradeBoundarySet,
    localGradeCriteriaSet,
    filters,
    localGradeCalculationMethod,
    cumulativeAssociatedTermTotalDisplayType,
    showTermTotalColumns,
    isLocalGradeCalculationEnabled,
    selectedGradingPeriodLabel,
    isFinalGradeManuallyAssigned,
    isAssociatedTermsWeightageDefined,
    gradeId,
    subjects,
    dvnScrollerRef,
    sortColumn,
    displaySettings,
    hasCategories,
    isRnPEnabled,
    canManageGradebook,
    courseId,
    isAssessmentGradebookHeaderEnhancements,
    course,
    evaluatedButNotSharedAssignmentsCountByTermMap,
    studentIds,
    isOverallTotalsEnableForStudents,
    isOverallTotalsEnableForFamily,
    totalImpactAlertTooltip,
    skipCountOfEvaluatedButNotSharedAssignments,
    gradingPeriodsStatusMap,
    isCourseBasedGrading,
    gradeScales,
    isUsingNewGradingSetupInCourses,
    isSubjectCommentsSupportOnGradebookEnabled,
    learningCourseId,
    cumulativeTotalCalculationMethod,
    mypIbDefinedGradeScale,
    mypCriteriaTotalGradeScale,
    gradingMethodologyTypes,
    gradingPeriods,
    isCategoriesEnabled,
    shouldGroupByCategories,
    hiddenGradebookEntities,
    courseLevels,
    enableCategoryGrade,
    isCategoryGradeScalesEnabled,
    mypCriteriaRegularGradeScales,
    shouldIncreaseStudentHeaderWidth: highestAlertTypeCount > 4,
    differentCategoriesForGradingPeriods,
    savedTermColumnsOrder,
    isCommonDisplaySettingsEnabled,
  };
};

export const getShouldShowAlertForImpactTotals = () =>
  ACLStore.can("FeatureFlag:AssessmentsGradebookImpactTotalsAlert");

export const getBulkAssignmentLevelCriterionVariables = ({
  courseId,
  assignmentId,
  shouldOverrideGrades,
  createdBy,
  creatorType,
  academicTermId,
  categoryId,
  ibPYPElementType,
  subjectId,
  academicCriteriaSetId,
  academicCriteriaValueId,
  entityType,
  achievementLevelCriteriaSetId,
  ratingValue,
  emptyValue,
  criteriaId,
  assessmentToolId,
  assessmentToolType,
}) => {
  const commonVariables = {
    assignmentId,
    isOverride: shouldOverrideGrades,
    source: "GRADEBOOK",
    createdBy,
    creatorType,
    isPublished: false,
    academicTermId,
  };

  const ratingInput = getRatingInputByEntityType({
    courseId,
    // * not required studentId for this mutation's variables
    studentId: "-1",
    categoryId,
    ibPYPElementType,
    selectedSubject: subjectId,
    academicTermId,
    academicCriteriaSetId,
    academicCriteriaValueId,
    entityType,
    achievementLevelCriteriaSetId,
    value: ratingValue,
    createdBy,
    emptyValue,
    criteriaId,
  });

  const ibPYPElementRating = {
    academicCriteriaSetId: _.get(ratingInput, "academicCriteriaSetId"),
    academicCriteriaValueId: _.get(ratingInput, "academicCriteriaValueId"),
    ibPYPElementId: _.get(ratingInput, "ibPYPElementId"),
    ibPYPElementType: _.get(ratingInput, "ibPYPElementType"),
    academicTermId,
    value: null,
  };

  const rating = {
    ...commonVariables,
    assessmentToolId,
    assessmentToolType,
    ibPYPElementRatings: [ibPYPElementRating],
    isSingleCriterionRating: true,
  };

  return {
    ratings: [rating],
  };
};

export const getRatingValue = ({ value, emptyValue }) => {
  let ratingValue = _.isObject(value) ? _.get(value, "label") : value;

  // * if ratingValue is empty, set it to emptyValue defined in header
  // * else convert it to string, as mutation only supports string input
  if (_.isNil(ratingValue)) {
    ratingValue = emptyValue;
  } else {
    ratingValue = `${ratingValue}`;
  }

  return ratingValue;
};

export const getAcademicCriteriaValueId = ({
  ratingValue,
  emptyValue,
  value,
  ratingOptions,
}) => {
  let academicCriteriaValueId = null;
  if (!_.isEqual(ratingValue, emptyValue)) {
    const label = _.isObject(value) ? _.get(value, "label") : value;
    academicCriteriaValueId = _.find(ratingOptions, {
      label,
    })?.id;
  }

  return academicCriteriaValueId;
};

export const getTeacherAccessToAssignment = ({
  t = _.noop,
  courseId,
  assignment,
  isRnPEnabled,
  doesTeacherHaveEditAccessToOtherClassTasks,
}) => {
  const assignmentCourse = _.get(assignment, "course");
  const assignmentCourseId = _.get(assignmentCourse, "id");

  const isSameCourseAssignment = assignmentCourseId === courseId;
  const isMappedToCourse = _.get(assignmentCourse, "isMappedToMe");
  let doesTeacherHaveAccessToAssignmentCourse = isMappedToCourse;

  if (isRnPEnabled) {
    const assignmentCourseControlParameters = _.get(
      assignmentCourse,
      "aclControlParameters"
    );
    const canManageGradebookForAssignmentCourse = _.find(
      assignmentCourseControlParameters,
      { permission: MANAGE_GRADEBOOK_PERMISSION }
    )?.value;

    doesTeacherHaveAccessToAssignmentCourse =
      (isMappedToCourse ||
        ACLStorage.getPermissionValue(
          LIST_AND_ACCESS_ALL_CLASSES_PERMISSION
        )) &&
      canManageGradebookForAssignmentCourse;
  }

  const doesTeacherHaveAccessToAssignment =
    doesTeacherHaveEditAccessToOtherClassTasks ||
    isSameCourseAssignment ||
    doesTeacherHaveAccessToAssignmentCourse;

  const conditionalNoAccessTooltip = doesTeacherHaveAccessToAssignment
    ? undefined
    : t(
        "progressSummary:you_cannot_modify_this_task_since_you_do_not_have_access_to_it"
      );

  return {
    doesTeacherHaveAccessToAssignment,
    conditionalNoAccessTooltip,
  };
};

// * checking whether learning goals exist on assignment or not for rating in bulk grade
export const shouldShowRateLearningGoals = ({
  assessmentTools,
  assessmentFields,
  curriculumProgramType,
  getCriteriaSetOfPypType,
  isLearningGoalsEvaluationEnabled,
}) => {
  const leHasPypEvaluation = checkAssessmentHasPypEvaluation({
    assessmentDetails: {
      assessmentTools,
      allFields: assessmentFields,
    },
    curriculumProgramType,
  });

  const isEmptyAchievementCriteria = checkAchievementCriteriaExists({
    assessmentFields,
    curriculumProgramType,
    getCriteriaSetOfPypType,
  });

  const showRateLearningGoals =
    isLearningGoalsEvaluationEnabled &&
    leHasPypEvaluation &&
    !isEmptyAchievementCriteria;

  return showRateLearningGoals;
};

export const updateEvaluatedButNotSharedCountInCategory = ({
  id,
  count,
  courseId,
  curriculumType,
  primaryGradeId,
  selectedSubject,
  studentIds,
  showOtherClassesTasks,
  curriculumProgramId,
}) => {
  try {
    const isHideTasksFromOtherClasses = getSettingValue({
      name: "HideTasksFromOtherClassesInGradebook",
      currentCurriculumProgramId: curriculumProgramId,
    });

    const evaluatedButNotSharedAssignmentsFilter = getEvaluatedButNotSharedAssignmentsFilter(
      {
        courseId,
        curriculumType,
        primaryGradeId,
        selectedSubject,
        studentIds,
        showOtherClassesTasks,
        isHideTasksFromOtherClasses,
      }
    );

    const variables = {
      assignmentFeedFilters: evaluatedButNotSharedAssignmentsFilter,
      skipAssignments: false,
    };

    const categoryData = readCategoryItemFragment({ id, variables });

    if (!_.isEmpty(categoryData)) {
      // * handled for negative value
      const evaluatedButNotSharedCount = Math.max(
        _.get(categoryData, "assignments.totalCount", 0) + count,
        0
      );

      const updatedCategoryData = {
        ...categoryData,
        assignments: {
          totalCount: evaluatedButNotSharedCount,
        },
      };

      writeCategoryItemFragment({ id, variables, data: updatedCategoryData });
    }
  } catch (e) {}
};

export const updateEvaluatedButNotSharedCountInTerm = ({
  courseId,
  curriculumType,
  primaryGradeId,
  selectedSubject,
  studentIds,
  academicYearId,
  curriculumProgramId,
  count,
  gradebookSelectedTermId,
  showOtherClassesTasks,
}) => {
  try {
    const isHideTasksFromOtherClasses = getSettingValue({
      name: "HideTasksFromOtherClassesInGradebook",
      currentCurriculumProgramId: curriculumProgramId,
    });

    const evaluatedButNotSharedAssignmentsFilter = getEvaluatedButNotSharedAssignmentsFilter(
      {
        courseId,
        curriculumType,
        primaryGradeId,
        selectedSubject,
        studentIds,
        showOtherClassesTasks,
        isHideTasksFromOtherClasses,
      }
    );

    const variables = {
      id: academicYearId,
      termFilters: {
        curriculumProgramIds: [curriculumProgramId],
        academicTermIds: [gradebookSelectedTermId],
      },
      assignmentFeedFilters: evaluatedButNotSharedAssignmentsFilter,
    };

    const data = readEvaluatedButNotSharedAssignmentsCountByTermFromCache(
      variables
    );

    if (!_.isEmpty(data)) {
      const terms = _.get(data, "terms", []);

      const updatedTerms = _.map(terms, term => {
        const termId = _.get(term, "id");

        // * handled for negative value
        const evaluatedButNotSharedCount = Math.max(
          _.get(term, "assignments.totalCount", 0) + count,
          0
        );

        if (termId === gradebookSelectedTermId) {
          return {
            ...term,
            assignments: {
              ..._.get(term, "assignments", {}),
              totalCount: evaluatedButNotSharedCount,
            },
          };
        }

        return term;
      });

      const updatedData = {
        node: { ...data, terms: updatedTerms },
      };

      writeEvaluatedButNotSharedAssignmentsCountByTermInCache({
        data: updatedData,
        variables,
      });
    }
  } catch (e) {}
};

export const getRouteValueFromGradebookRoute = ({ route }) => {
  return Object.keys(GRADEBOOK_ROUTES).find(
    key => GRADEBOOK_ROUTES[key] === route
  );
};

export const getObjectiveLetterFromLabel = label => {
  // label format
  // "lorem ipsum <letter> : dolor sit amet";
  // e.g. "Objective D: Continuous learning"
  // trim is used to remove any leading or trailing spaces
  return _.last(_.split(_.trim(_.first(_.split(label, ":"))), " "));
};

export const shouldSkipCountOfEvaluatedButNotSharedAssignments = currentCurriculumProgramId => {
  if (_.isEmpty(currentCurriculumProgramId)) return false;

  const showAlertForImpactTotals = getShouldShowAlertForImpactTotals();

  const isOverallTotalsEnableForStudents = getSettingValue({
    name: "Common:EnableCategorywiseAndTermOverviewStudent",
    currentCurriculumProgramId,
  });

  const isOverallTotalsEnableForFamily = getSettingValue({
    name: "Common:EnableCategorywiseAndTermOverviewFamily",
    currentCurriculumProgramId,
  });

  const isOverallScoreEnabled = getSettingValue({
    name: OVERALL_SCORE_FOR_GRADING_SETUP_SETTING,
    currentCurriculumProgramId,
  });

  return (
    !showAlertForImpactTotals ||
    !isOverallScoreEnabled ||
    (!isOverallTotalsEnableForStudents && !isOverallTotalsEnableForFamily)
  );
};

export const getIsGradingHistoryEnhancementsEnabled = () =>
  ACLStore.can("FeatureFlag:GradingHistoryEnhancements");

const getPrefixElement = ({
  id,
  name,
  profileImage,
  prefixElementExtraProps = {},
}) => {
  return (
    <Avatar
      key={id}
      id={id}
      name={name}
      src={profileImage}
      size={"xx-small"}
      shade={"light"}
      dsVersion="2.0"
      {...prefixElementExtraProps}
    />
  );
};

export const getStudentsOptions = ({
  isGroupBySubjectLevel,
  students,
  isGroupByMypPhase,
  termGradeStudentWiseCriteria,
  t,
  prefixElementExtraProps = {},
  showSubLabelAsSourceId = false,
}) => {
  if (isGroupBySubjectLevel) {
    let allStudents = {};

    _.forEach(students, (student = {}) => {
      const {
        id,
        profileImage,
        courseMap,
        tag,
        isArchived,
        alertTypes = [],
        sourceId,
      } = student;

      const name = getFormattedUserName({
        user: student,
        type: STUDENT,
      });

      const tags = _.get(courseMap, "0.tags", []);

      const subjectLevelTags =
        _.find(tags, tag => tag?.type === "SUBJECT_LEVEL") || tag;

      const { id: tagId, label: tagLabel } = subjectLevelTags || {};

      // add subject level tag
      if (!allStudents[tagId]) {
        allStudents[tagId] = {
          value: tagId,
          label: t("common:all_students_with_label", {
            label: t(`common:${tagLabel}`),
          }),
          options: [],
          tag: tagLabel,
        };
      }

      // add student according to their tag
      allStudents[tagId]?.options?.push({
        id,
        label: name,
        value: id,
        isArchived,
        prefixElement: getPrefixElement({
          id,
          name,
          profileImage,
          prefixElementExtraProps,
        }),
        alertTypes,
        sourceId,
        subLabel: showSubLabelAsSourceId ? sourceId : undefined,
      });
    });

    allStudents = _.values(allStudents)?.sort((tag1, tag2) => {
      return (
        SUBJECT_LEVEL_SORT_ORDER?.indexOf(tag1?.tag) -
        SUBJECT_LEVEL_SORT_ORDER?.indexOf(tag2?.tag)
      );
    });

    return allStudents;
  } else if (isGroupByMypPhase) {
    let allStudents = {};

    const studentsWithNoPhaseSelected = {
      options: [],
    };
    _.forEach(students, (student = {}) => {
      const {
        id,
        profileImage,
        isArchived,
        alertTypes = [],
        sourceId,
      } = student;

      const name = getFormattedUserName({
        user: student,
        type: STUDENT,
      });

      const {
        id: yearLevelId,
        label: yearLevelLabel,
        displaySequence,
      } = getYearLevelCriteriaSetForStudent({
        studentId: id,
        termGradeStudentWiseCriteria,
      });

      if (_.isNil(yearLevelId)) {
        studentsWithNoPhaseSelected.options.push({
          id,
          label: name,
          value: id,
          isArchived,
          prefixElement: getPrefixElement({
            id,
            name,
            profileImage,
            prefixElementExtraProps,
          }),
          alertTypes,
          sourceId,
          subLabel: showSubLabelAsSourceId ? sourceId : undefined,
        });
      } else {
        if (!allStudents[yearLevelId]) {
          allStudents[yearLevelId] = {
            value: yearLevelId,
            label: t("common:all_students_with_label", {
              label: yearLevelLabel,
            }),
            options: [],
            displaySequence,
          };
        }

        if (!_.isEmpty(_.get(allStudents, [yearLevelId]))) {
          allStudents[yearLevelId].options.push({
            id,
            label: name,
            value: id,
            isArchived,
            prefixElement: getPrefixElement({
              id,
              name,
              profileImage,
              prefixElementExtraProps,
            }),
            alertTypes,
            sourceId,
            subLabel: showSubLabelAsSourceId ? sourceId : undefined,
          });
        }
      }
    });

    allStudents = _.values(allStudents)?.sort((yearLevel1, yearLevel2) => {
      return yearLevel1?.displaySequence - yearLevel2?.displaySequence;
    });

    return _.concat(allStudents, studentsWithNoPhaseSelected);
  } else {
    return _.map(students, (student = {}) => {
      const {
        id,
        profileImage,
        isArchived,
        alertTypes = [],
        sourceId,
      } = student;
      const name = getFormattedUserName({
        user: student,
        type: STUDENT,
      });

      return {
        id,
        label: name,
        value: id,
        isArchived,
        prefixElement: getPrefixElement({
          id,
          name,
          profileImage,
          prefixElementExtraProps,
        }),
        alertTypes,
        sourceId,
        subLabel: showSubLabelAsSourceId ? sourceId : undefined,
      };
    });
  }
};

// * used for filtering options based on search text
export const getFilteredOptions = ({ searchText = "", options = [] }) => {
  const _searchText = _.lowerCase(_.trim(searchText));

  if (!_.isEmpty(_searchText)) {
    const newOptions = [];

    _.forEach(options, option => {
      const childOptions = _.get(option, "options", []);
      const label = _.lowerCase(_.get(option, "label", ""));
      const subLabel = _.lowerCase(_.get(option, "subLabel", ""));

      const isMatch =
        _.includes(label, _searchText) || _.includes(subLabel, _searchText);

      // * grouped options
      if (!_.isEmpty(childOptions)) {
        // * return the entire group if the group label matches with search text
        if (isMatch) {
          newOptions.push({ ...option });
        }
        // * return only those child options that match with search text
        else {
          const filteredChildOptions = _.filter(childOptions, childOption => {
            const childLabel = _.lowerCase(_.get(childOption, "label"));
            const childSubLabel = _.lowerCase(_.get(childOption, "subLabel"));

            return (
              _.includes(childLabel, _searchText) ||
              _.includes(childSubLabel, _searchText)
            );
          });

          if (!_.isEmpty(filteredChildOptions)) {
            newOptions.push({ ...option, options: filteredChildOptions });
          }
        }
      }
      // * single options
      else {
        isMatch && newOptions.push(option);
      }
    });

    return newOptions;
  } else {
    return options;
  }
};

// * merge students element ratings for cache update
export const mergeStudentsElementRatingsForCacheUpdate = ({
  ratings: newRatings,
  studentsElementRatings,
}) => {
  const newRatingsByStudentMap = _.chain(newRatings)
    .groupBy("studentId")
    .mapValues(ratings =>
      _.map(ratings, rating => ({
        id: generateRandomId(),
        ibPYPElementId: rating.ibPYPElementId,
        ibPYPElementType: rating.ibPYPElementType,
        academicCriteriaSet: {
          id: rating.academicCriteriaSetId,
          __typename: "AcademicCriteriaSet",
        },
        academicCriteriaValue: {
          id: rating.academicCriteriaValueId,
          __typename: "AcademicCriteriaValue",
        },
        isComputedRating: rating.isComputedRating,
        __typename: "IBPYPElementRating",
      }))
    )
    .value();

  const updatedStudentsElementRatings = _.map(
    studentsElementRatings,
    student => {
      const studentId = student.id;
      const newRatings = newRatingsByStudentMap[studentId] || [];

      if (_.isEmpty(newRatings)) {
        return student;
      }

      const createRatingKey = rating =>
        `${rating.ibPYPElementId}__${rating.ibPYPElementType}__${rating.academicCriteriaSet?.id}__${rating.isComputedRating}`;

      const existingRatingsByElement = _.keyBy(
        student.ibPYPElementRatings || [],
        createRatingKey
      );
      const newRatingsByElement = _.keyBy(newRatings, createRatingKey);

      const mergedRatings = _.values({
        ...existingRatingsByElement,
        ...newRatingsByElement,
      });

      return {
        ...student,
        ibPYPElementRatings: mergedRatings,
      };
    }
  );

  return updatedStudentsElementRatings;
};

export const getAcademicCriteriaSetList = _.memoize(
  ({
    curriculumType,
    aliasPlannerElementType,
    plannerElementType,
    getCriteriaSetOfPypType,
    criteriaType,
    subjectId,
    gradeIds = [],
  }) => {
    const plannerElementTypes = [ELEMENT_TYPE_UBD_LEARNING_STANDARD];

    const elementType =
      curriculumType == CURRICULUM_TYPE_PYP
        ? aliasPlannerElementType
        : plannerElementType;

    const criteriaSets = getCriteriaSetBasedOnPlannerElementType({
      type: elementType,
      getCriteriaSetOfPypType,
      parentId:
        _.includes(plannerElementTypes, plannerElementType) && subjectId,
      criteriaType,
    });

    if (_.isEmpty(gradeIds)) {
      return criteriaSets;
    }

    const selectedGradeIdsSet = new Set(gradeIds);

    return _.filter(criteriaSets, criteriaSet => {
      const gradeIds = _.map(criteriaSet?.grades, "id");
      return _.some(gradeIds, id => selectedGradeIdsSet.has(id));
    });
  },
  params => {
    const {
      curriculumType,
      aliasPlannerElementType,
      plannerElementType,
      criteriaType,
      subjectId,
      gradeIds,
    } = params;

    return JSON.stringify({
      curriculumType,
      aliasPlannerElementType,
      plannerElementType,
      criteriaType,
      subjectId,
      gradeIds,
    });
  }
);

// * determines the appropriate route for the admin gradebook view
export const getAdminGradebookRoute = ({ params = {}, routes = [] }) => {
  const learningGoal = _.get(params, "learningGoal");
  if (learningGoal) {
    return `learning-goals/${learningGoal}`;
  }

  // * check if current path is a valid gradebook route
  const currentPath = _.last(routes)?.path;
  const validRoutes = ["assessments", "standards"];

  if (validRoutes.includes(currentPath)) {
    return currentPath;
  }

  return "";
};

export const getGradebookTypeFromRoute = route => {
  switch (true) {
    case _.includes(route, "assessments"):
      return ASSESSMENTS_GRADEBOOK;

    case _.includes(route, "learning-goals"):
      return LEARNING_GOALS_GRADEBOOK;

    case _.includes(route, "standards"):
      return STANDARDS_GRADEBOOK;

    case _.includes(route, "final-grades"):
      return FINAL_GRADES_GRADEBOOK;

    default:
      return "";
  }
};

const getGradingCategoriesWithPercentageValues = categoryOptions => {
  const subjectLevelTotalsMap = _.reduce(
    categoryOptions,
    (acc, category) => {
      const { scoreValues } = category || {};

      _.forEach(scoreValues, scoreValue => {
        const { subjectLevel, value } = scoreValue || {};

        const subjectLevelId = subjectLevel?.id || "null";

        if (!acc[subjectLevelId]) {
          acc[subjectLevelId] = 0;
        }

        const numberValue = Number(value);
        const safeValue = _.isFinite(numberValue) ? numberValue : 0;
        acc[subjectLevelId] += safeValue;
      });

      return acc;
    },
    {}
  );

  return _.map(categoryOptions, category => {
    const { scoreValues } = category || {};

    const scoreValuesWithPercent = _.map(scoreValues, scoreValue => {
      const { subjectLevel, value = 0 } = scoreValue || {};
      const subjectLevelId = subjectLevel?.id || "null";

      const totalForSubjectLevel = subjectLevelTotalsMap[subjectLevelId] || 0;

      const percentageValue = totalForSubjectLevel
        ? (value / totalForSubjectLevel) * 100
        : 0;

      return {
        ...scoreValue,
        percentageValue: _.round(percentageValue, 2),
      };
    });

    return {
      ...category,
      scoreValues: scoreValuesWithPercent,
    };
  });
};

export const getGradingCategories = ({
  gradingCategorySets,
  isCurriculumDP,
  conditionalPercentage,
  isTOK,
}) => {
  return _.flatMap(gradingCategorySets, categorySet => {
    const { gradingCategoryValues = [], academicTerm } = categorySet || {};

    const categoryOptions = _.map(
      gradingCategoryValues,
      (category = {}, index) => {
        const scoreValues = _.map(category.valueDetails, valueDetail => ({
          id: valueDetail.id,
          value: valueDetail.weight,
          subjectLevel: _.get(valueDetail, "subjectLevel"),
        }));

        return {
          gradingCategoryValueId: category.id,
          value: category.globalCategory.id,
          label: category.globalCategory.name,
          subText: getCategorySubtext({
            orderedScoreValues: scoreValues,
            isCurriculumDP,
            conditionalPercentage,
            isTOK,
          }),
          gradeScales: category?.gradeScale,
          scoreValues,
          isShowGradeBoundaries: false,
          gradeBoundary: null,
          hasGradeBoundary: false,
          associatedEntities: [
            // TODO(@abhinay-toddleapp): To revisit this
            {
              id: _.get(academicTerm, "id"),
              __typename: "AcademicTerm",
            },
          ],
          displaySequence: index,
          gradeScaleType: null,
          subjectId: null, // TODO(@abhinay-toddleapp): To revisit this
          termId: _.get(academicTerm, "id"),
          hasEvaluatedButNotSharedAssignemnts: false,
        };
      }
    );

    const categoryOptionsWithPercentageValues = getGradingCategoriesWithPercentageValues(
      categoryOptions
    );

    const sortedCategoryOptions = _.orderBy(
      categoryOptionsWithPercentageValues,
      "displaySequence",
      "asc"
    );

    return sortedCategoryOptions;
  });
};

const getSelectedDate = ({ currentDate, startDate, endDate }) => {
  if (
    isDateInBetween({
      date: currentDate,
      date1: startDate,
      date2: endDate,
    })
  )
    return currentDate;
  else if (currentDate < startDate) return startDate;
  else return endDate;
};

export const getDatesForObservationDateSelector = ({
  selectedGradingPeriodData,
  selectedObservationDate,
}) => {
  const startDate = getDateMomentObject(
    _.get(selectedGradingPeriodData, "startDate", selectedObservationDate)
  );

  const endDate = getDateMomentObject(
    _.get(selectedGradingPeriodData, "endDate", selectedObservationDate)
  );

  const selectedDate = getSelectedDate({
    currentDate: selectedObservationDate,
    startDate,
    endDate,
  });

  const isCurrentDate = selectedDate.isSame(moment(), "day");

  if (!isCurrentDate) {
    selectedDate.set({ H: 23, m: 59, s: 59 });
  } else {
    const timeObject = {
      H: selectedDate.get("hour"),
      m: selectedDate.get("minute"),
      s: selectedDate.get("second"),
    };
    selectedDate.set(timeObject);
  }

  return { selectedDate, startDate, endDate };
};

export const getSubjectCommentsLabelByType = ({ t, type }) => {
  const labelMap = {
    [SUBJECT_DESCRIPTION]: t("classRoom:subject_description"),
    [FINAL_COMMENT]: t("classRoom:final_comment"),
    [CRITERION_A_COMMENT]: `${t("common:criterion")} A: ${t("common:comment")}`,
    [CRITERION_B_COMMENT]: `${t("common:criterion")} B: ${t("common:comment")}`,
    [CRITERION_C_COMMENT]: `${t("common:criterion")} C: ${t("common:comment")}`,
    [CRITERION_D_COMMENT]: `${t("common:criterion")} D: ${t("common:comment")}`,
  };

  return labelMap[type] || "";
};

// * checking whether mapped grade scale has calculation method or not
export const checkIsStandardCalculationConfigured = ({
  gradingConfigurations = [],
  gradeScaleId,
}) => {
  if (_.isEmpty(gradingConfigurations)) {
    return false;
  }

  const gradingConfiguration = _.find(
    gradingConfigurations,
    config => _.get(config, "gradeScale.id") === gradeScaleId
  );

  const gradingConfigurationSettings = _.get(
    gradingConfiguration,
    "gradingConfiguration.settings",
    []
  );

  const finalGradeCalculationSettings = _.filter(gradingConfigurationSettings, {
    uid: "finalGradeCalculationMethod",
  });

  const groupTypes = _.compact(
    _.map(finalGradeCalculationSettings, "groupType")
  );

  return _.every(["HORIZONTAL", "VERTICAL"], type =>
    _.includes(groupTypes, type)
  );
};

// * get vertical calculation method of standardSet + gradeScale config
export const getVerticalCalculationMethodForStandardSet = ({
  gradingConfigurations = [],
  gradeScaleId,
}) => {
  const gradingConfiguration = _.find(
    gradingConfigurations,
    config => config?.gradeScale?.id === gradeScaleId
  );

  const gradingConfigurationSettings = _.get(
    gradingConfiguration,
    "gradingConfiguration.settings",
    []
  );

  const verticalCalculationMethod = _.find(
    gradingConfigurationSettings,
    ({ uid, groupType }) =>
      uid === "finalGradeCalculationMethod" && groupType === "VERTICAL"
  );

  return _.get(verticalCalculationMethod, "value", null);
};

// * Used to display label of selected grade scale based on its grading type
export const getdisplayRatingFn = ({
  gradingType,
  calculateStandardGradesUsing,
  viewBy, // * ABBREVIATION or LABEL
}) => {
  return ({ abbreviation, label, description }) => {
    if (
      gradingType !== "NUMERIC" ||
      calculateStandardGradesUsing === "NUMERIC_VALUE"
    ) {
      return { ratingLabel: abbreviation, ratingSubLabel: description };
    }

    if (viewBy === "LABEL") {
      return { ratingLabel: label, ratingSubLabel: abbreviation };
    } else {
      return { ratingLabel: abbreviation, ratingSubLabel: label };
    }
  };
};

// * Generate grade scale value options for dropdown
export const generateGradeScaleValueOptions = ({
  gradeScale,
  displayRatingFn,
}) => {
  const gradeScaleValues = _.get(
    gradeScale,
    "gradeScaleEntityDetails.0.gradeScaleValues",
    []
  );

  return _.map(
    gradeScaleValues,
    ({ id, abbreviation, label, description, color }) => {
      const { ratingLabel, ratingSubLabel } = displayRatingFn({
        abbreviation,
        label,
        description,
      });

      return {
        id,
        label: ratingLabel,
        subtitle: ratingSubLabel,
        prefix: {
          type: "icon",
          attrs: {
            color,
          },
        },
      };
    }
  );
};

const buildToken = ({ color, weight }) => `var(--${color}-${weight})`;

export const getCellColorStyle = _.memoize(color => {
  if (!color) return {};

  // * Match for var(--color-weight)
  const match = color.match(/var\(--([a-zA-Z]+)-(\d+)\)/);

  // * CASE 1: Matching var(--color-xxx)
  if (!_.isEmpty(match)) {
    const colorName = _.get(match, "[1]");

    const TEXT_WEIGHT_DARK = 300;
    const TEXT_WEIGHT_MEDIUM = 500;
    const BACKGROUND_WEIGHT_LIGHT = 950;

    return {
      textColor: getHexCodeFromColorToken(
        buildToken({ color: colorName, weight: TEXT_WEIGHT_DARK })
      ),
      textColor2: getHexCodeFromColorToken(
        buildToken({ color: colorName, weight: TEXT_WEIGHT_MEDIUM })
      ),
      backgroundColor: getHexCodeFromColorToken(
        buildToken({ color: colorName, weight: BACKGROUND_WEIGHT_LIGHT })
      ),
      defaultColor: color,
    };
  }

  // * CASE 2: No match → fallback from map
  const hexCode = getHexCodeFromColorToken(color);

  const fallback = _.get(GRADE_SCALE_VALUES_COLORS_MAP, _.toLower(hexCode), {});

  return {
    textColor: _.get(fallback, "textColor"),
    textColor2: _.get(fallback, "textColor2"),
    backgroundColor: _.get(fallback, "backgroundColor"),
    defaultColor: _.get(fallback, "defaultColor", color),
  };
});

export const getSubjectGradingMethodologyTypes = ({
  gradingMethodologies,
  selectedSubjectId,
  coursePrimaryGradeId,
}) => {
  const filteredGradingMethodology = _.find(
    gradingMethodologies,
    ({ entityDetail }) => {
      return (
        entityDetail?.subjectId === selectedSubjectId &&
        entityDetail?.gradeId === coursePrimaryGradeId
      );
    }
  );

  return _.get(filteredGradingMethodology, "gradingMethodologyTypes");
};

// * Enable - Compact Data Grid Table of Gradebook
export const getIsGradebookCompactUIEnabled = () => {
  return ACLStore.can("FeatureFlag:GradebookCompactUI");
};

export const getIsGradebookAssignmentDetailedPopoverEnabled = () => {
  return ACLStore.can("FeatureFlag:GradebookAssignmentDetailedPopover");
};

// * based on selected grade scale and calculation method returing boolean value
export const checkIsNumericValueCalculationMethod = gradeScale => {
  const { calculateStandardGradesUsing } = gradeScale || {};

  if (!_.isEmpty(calculateStandardGradesUsing)) {
    return calculateStandardGradesUsing === NUMERIC_VALUE;
  }

  return false;
};

// * get color for number value calculation grade scale
export const getColorForNumericGradeFn = (gradeScale = {}) => {
  if (
    _.isEmpty(gradeScale) ||
    !checkIsNumericValueCalculationMethod(gradeScale)
  )
    return _.noop;

  const gradeScaleValues = _.get(
    gradeScale,
    "gradeScaleEntityDetails.0.gradeScaleValues",
    []
  );

  if (_.isEmpty(gradeScaleValues)) return _.noop;

  const ranges = _.map(gradeScaleValues, gradeScaleValue => ({
    numericGrade: parseFloat(_.get(gradeScaleValue, "abbreviation")),
    color: _.get(gradeScaleValue, "color"),
  }));

  // * Pre-compute sorted ranges for binary search optimization
  const sortedRanges = [...ranges].sort(
    (a, b) => a.numericGrade - b.numericGrade
  );

  // * Extract boundaries and colors into separate arrays for faster access
  const boundaries = _.map(sortedRanges, "numericGrade");
  const colors = _.map(sortedRanges, "color");

  return grade => {
    // * Convert string numbers to numeric values (handles both string and number inputs)
    const numericGrade = parseFloat(grade);

    // * Early return for invalid inputs
    if (isNaN(numericGrade)) {
      return "";
    }

    // If input is below first boundary, return first color
    if (numericGrade < _.first(boundaries)) {
      return _.first(colors);
    }

    // If input is above last boundary, return last color
    if (numericGrade > _.last(boundaries)) {
      return _.last(colors);
    }

    // * Binary search implementation to find the appropriate range index
    let left = 0;
    let right = boundaries.length - 1;
    let result = -1; // * Stores the index of the highest valid range

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);

      if (boundaries[mid] <= numericGrade) {
        result = mid;
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }

    return result >= 0 ? colors[result] : "";
  };
};

export const getAcademicYearStartDate = _.memoize(
  ({ organizationId, academicYearId }) => {
    const selectedGradingPeriodAcademicYear = getAcademicYearById({
      organizationId,
      academicYearId,
    });

    return _.get(selectedGradingPeriodAcademicYear, "startDate", "");
  },
  ({ organizationId, academicYearId }) => `${organizationId}-${academicYearId}`
);

export const getGradeScalesByGradebookType = ({
  termId,
  gradeId,
  subjectId,
  gradeScales,
  standardSetIds,
  learningCourseId,
  gradebookType,
  gradingMethodologyTypes,
  mypCriteriaTotalGradeScale,
  standardSetGradeScaleId,
  skipStandardSetGradeScaleFilter,
}) => {
  const dependentScales = _.filter(gradeScales, { type: DEPENDENT });
  const otherScales = _.filter(gradeScales, ({ type }) => {
    return type !== DEPENDENT;
  });

  const filteredGradeScales = [];
  const getIsValid = ({
    gradebookType,
    finalGradeCalculationGradeScale,
    finalGradeCalculationMethodology,
    finalGradeCalculationStandardSets,
    gradeScaleType,
  }) => {
    switch (gradebookType) {
      case "ASSESSMENTS_GRADEBOOK": {
        return finalGradeCalculationMethodology !== STANDARD_BASED;
      }
      case "STANDARDS_GRADEBOOK": {
        if (gradeScaleType !== REGULAR) {
          return false;
        }
        if (finalGradeCalculationMethodology === MANUAL) {
          return true;
        }

        if (finalGradeCalculationMethodology === STANDARD_BASED) {
          if (skipStandardSetGradeScaleFilter) {
            return true;
          }

          return (
            _.some(finalGradeCalculationStandardSets, id =>
              _.includes(standardSetIds, id)
            ) &&
            _.isEqual(finalGradeCalculationGradeScale, standardSetGradeScaleId)
          );
        }

        return false;
      }
    }
  };

  _.forEach(otherScales, gradeScale => {
    const finalGradeCalculationSettings = getFinalGradeCalculationSettings({
      gradeScale,
      termId,
      subjectId,
      gradeId,
      learningCourseId,
    });
    const { type: gradeScaleType } = gradeScale || {};
    const finalGradeCalculationMethodology = _.get(
      finalGradeCalculationSettings,
      FINAL_GRADE_CALCULATION_METHODOLOGY_UID
    );
    const finalGradeCalculationStandardSets = getSelectedStandardSetsForGradeScale(
      finalGradeCalculationSettings
    );
    const finalGradeCalculationGradeScale = _.get(
      finalGradeCalculationSettings,
      FINAL_GRADE_CALCULATION_GRADE_SCALE_UID
    );

    const isValid = getIsValid({
      gradebookType,
      finalGradeCalculationGradeScale,
      finalGradeCalculationMethodology,
      finalGradeCalculationStandardSets,
      gradeScaleType,
    });

    if (!isValid) {
      return;
    }

    filteredGradeScales.push({
      ...gradeScale,
      finalGradeCalculationGradeScale,
      finalGradeCalculationMethodology,
      finalGradeCalculationStandardSets,
    });
  });

  _.forEach(dependentScales, gradeScale => {
    const { dependentGradeScales } = gradeScale || {};
    const allBaseScales = _.map(dependentGradeScales, "id");

    let validBaseScales = filteredGradeScales;

    // this is to allow adding grade scales that depend on 0-32 grade scale when MYP total score is enabled
    if (
      gradebookType === "ASSESSMENTS_GRADEBOOK" &&
      _.includes(gradingMethodologyTypes, MYP_CRITERIA_BASED) &&
      !_.isEmpty(mypCriteriaTotalGradeScale)
    ) {
      validBaseScales = [...filteredGradeScales, mypCriteriaTotalGradeScale];
    }

    const baseScaleIndex = _.findIndex(validBaseScales, ({ id }) => {
      return _.includes(allBaseScales, id);
    });

    if (baseScaleIndex === -1) {
      return;
    }

    filteredGradeScales.splice(baseScaleIndex + 1, 0, gradeScale);
  });

  return filteredGradeScales;
};

export const getStandardsAndLearningGoalsFiltersToSaveForCourse = filters => {
  return _.omit(filters, ["studentIds", "students"]);
};

export const getObservationAndOverrideAccess = ({
  canUserEdit,
  canOverrideGrades,
  isGradingPeriodUnlocked,
}) => {
  const canAddObservationRating = isGradingPeriodUnlocked && canUserEdit;
  const canOverrideRating =
    isGradingPeriodUnlocked && canUserEdit && canOverrideGrades;

  return {
    canOverrideRating,
    canAddObservationRating,
  };
};

export const getColumnOptions = ({ optionsList }) => {
  const categoryColumns = _.find(
    optionsList,
    item => _.get(item, "0.id") === "CATEGORY_COLUMNS"
  );
  const categoryColumnChildren = _.get(categoryColumns, "0.children");
  const categoryColumnOptions = !_.isEmpty(categoryColumnChildren)
    ? _.map(categoryColumnChildren, "id")
    : _.compact([_.get(categoryColumns, "0.id")]);

  const termColumns = _.find(
    optionsList,
    item => _.get(item, "0.id") === "TERM_TOTAL_COLUMNS"
  );
  const termColumnChildren = _.get(termColumns, "0.children");
  const termColumnOptions = [];

  if (!_.isEmpty(termColumnChildren)) {
    _.forEach(termColumnChildren, item => {
      const { id } = item;

      if (id === "CRITERIA_COLUMNS") {
        const children = _.get(item, "children");
        termColumnOptions.push(..._.map(children, "id"));
      } else {
        termColumnOptions.push(id);
      }
    });
  } else {
    const termParentId = _.get(termColumns, "0.id");
    if (termParentId) {
      termColumnOptions.push(termParentId);
    }
  }

  return {
    termColumnOptions,
    categoryColumnOptions,
    categoryColumns,
    termColumns,
  };
};
